import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../src/supabaseClient';
import {
    Task, Level, ResourceItem, BudgetFolder, BudgetItem, ApuItem, PhysicalUnit,
    Responsible, ProjectInfo, ProtocolConfig, WeeklyAllocation, WeeklyPlan, Constraint, LookaheadPlan,
    ProtocolState, ResourceProject, CNCCause, CNCGroup, ConstraintDefinition, ConstraintGroup, GanttBaseline, Profile, Activity,
    SubcontractType, ProjectProgressSnapshot
} from '../src/types';
import {
    calculateSCurve, calculateDelayDays, truncateProgress,
    calculateProjectProgress, calculateTaskProgress, isProtocolProgressed,
    calculateCriticalPath, findTaskById, findTaskName, flattenTasks, findParentTask
} from '../src/utils/planningUtils';
import * as XLSX from 'xlsx';
import { GraphicProgressViewer } from '../src/components/graphic-progress/GraphicProgressViewer';

interface ContextMenuState {
    visible: boolean;
    x: number;
    y: number;
    taskId: string | null;
}

// --- Custom Components ---

const SearchableCNCSelect = ({ value, onChange, groups, disabled }: { value: string, onChange: (val: string) => void, groups: CNCGroup[], disabled?: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Find selected cause name
    let selectedName = "Seleccionar Causa...";
    if (value) {
        for (const g of groups) {
            const found = g.causes.find(c => c.id === value);
            if (found) {
                selectedName = found.name;
                break;
            }
        }
    }

    // Filter groups and causes based on search
    const filteredGroups = groups.map(g => {
        const filteredCauses = g.causes.filter(c =>
            c.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return { ...g, causes: filteredCauses };
    }).filter(g => g.causes.length > 0);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full text-xs border border-slate-300 dark:border-slate-600 rounded p-1.5 bg-white dark:bg-slate-800 outline-none focus:ring-1 focus:ring-amber-500 text-left flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
                <span className="truncate">{selectedName}</span>
                <span className="material-symbols-outlined text-[14px]">expand_more</span>
            </button>

            {isOpen && (
                <div className="absolute z-[9999] top-full left-0 w-[280px] mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-xl max-h-60 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-slate-100 dark:border-slate-700">
                        <input
                            type="text"
                            className="w-full text-xs p-1.5 border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-900 focus:outline-none focus:border-amber-500"
                            placeholder="Buscar causa..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            autoFocus
                        />
                    </div>
                    <div className="overflow-y-auto flex-1 p-1">
                        <div
                            className="px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 italic"
                            onClick={() => { onChange(''); setIsOpen(false); }}
                        >
                            Seleccionar Causa...
                        </div>
                        {filteredGroups.length === 0 ? (
                            <div className="px-2 py-3 text-xs text-center text-slate-400">No hay coincidencias</div>
                        ) : (
                            filteredGroups.map(group => (
                                <div key={group.id} className="mb-1">
                                    <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-900/50">
                                        {group.name}
                                    </div>
                                    {group.causes.map(c => (
                                        <div
                                            key={c.id}
                                            className={`px-3 py-1.5 text-xs cursor-pointer truncate ${value === c.id ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 font-medium' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'}`}
                                            onClick={() => { onChange(c.id); setIsOpen(false); }}
                                            title={c.name}
                                        >
                                            {c.name}
                                        </div>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Helper Functions ---
const calculateDuration = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    const diffTime = e.getTime() - s.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays + 1 : 0;
};

const generateTimelineWeeks = (startDate: Date, numWeeks: number) => {
    let current = new Date(startDate);
    const day = current.getDay();
    const diff = current.getDate() - day + (day === 0 ? -6 : 1);
    current.setDate(diff);

    const weeks = [];
    for (let i = 0; i < numWeeks; i++) {
        weeks.push(new Date(current));
        current.setDate(current.getDate() + 7);
    }
    return weeks;
};

const groupWeeksByMonth = (weeks: Date[]) => {
    const months: { label: string; colSpan: number }[] = [];
    let currentMonthLabel = "";
    let spanCount = 0;
    const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

    weeks.forEach((date, index) => {
        const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
        if (label !== currentMonthLabel) {
            if (currentMonthLabel !== "") {
                months.push({ label: currentMonthLabel, colSpan: spanCount });
            }
            currentMonthLabel = label;
            spanCount = 1;
        } else {
            spanCount++;
        }
        if (index === weeks.length - 1) {
            months.push({ label: currentMonthLabel, colSpan: spanCount });
        }
    });
    return months;
};

const getDatesForWeekday = (startDate: string, endDate: string, weekday: number) => {
    if (!startDate || !endDate) return [];
    const start = new Date(startDate + "T12:00:00");
    const end = new Date(endDate + "T12:00:00");
    const dates: string[] = [];

    let current = new Date(start);
    // Find first occurrence of the weekday
    while (current.getDay() !== (weekday % 7)) {
        current.setDate(current.getDate() + 1);
    }

    while (current <= end) {
        dates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 7);
    }
    return dates;
};


interface PlanningScreenProps {
    tasks: Task[];
    setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
    responsibles: Responsible[];
    setResponsibles: React.Dispatch<React.SetStateAction<Responsible[]>>;
    matrixData: Record<string, string>;
    units: PhysicalUnit[];
    levels: Level[];
    savedProtocols: Record<string, ProtocolState>;
    protocolConfigs: Record<string, ProtocolConfig>;
    weeklyPlans: WeeklyPlan[];
    setWeeklyPlans: React.Dispatch<React.SetStateAction<WeeklyPlan[]>>;
    lookaheadPlans: LookaheadPlan[];
    setLookaheadPlans: React.Dispatch<React.SetStateAction<LookaheadPlan[]>>;
    projectInfo: ProjectInfo;
    subcontractTypes: SubcontractType[];
    setSubcontractTypes: React.Dispatch<React.SetStateAction<SubcontractType[]>>;
    cncGroups: CNCGroup[];
    setCncGroups: React.Dispatch<React.SetStateAction<CNCGroup[]>>;
    constraintGroups: ConstraintGroup[];
    setConstraintGroups: React.Dispatch<React.SetStateAction<ConstraintGroup[]>>;
    globalCNCGroups: CNCGroup[];
    globalConstraintGroups: ConstraintGroup[];
    baselines: GanttBaseline[];
    setBaselines: React.Dispatch<React.SetStateAction<GanttBaseline[]>>;
    forceSyncTasks?: (overriddenState?: Task[]) => Promise<void>;
    profiles: Profile[];
    addActivity: (activity: Omit<Activity, 'id' | 'timestamp'>) => void;
    progressHistory: ProjectProgressSnapshot[];
    setProgressHistory: React.Dispatch<React.SetStateAction<ProjectProgressSnapshot[]>>;
    notifications: any[];
    setNotifications: React.Dispatch<React.SetStateAction<any[]>>;
    currentUser?: any;
    hasPermission: (module: string, action: 'view'|'edit') => boolean;
}


// --- New Component: Dependency Arrows ---

const DependencyArrows: React.FC<{
    viewStartDate: Date;
    visibleTasks: Task[];
    zoomLevel: string;
    workingDays: boolean[];
    holidays: { date: string; name: string }[];
    timelineColumns: Date[];
    selectedGanttTaskId: string | null;
    taskPathInfo: {
        selectedId: string;
        selectedOrigId: number | undefined;
        predecessors: Set<number>;
        successors: Set<number>;
    } | null;
}> = ({ 
    viewStartDate, 
    visibleTasks, 
    zoomLevel, 
    workingDays, 
    holidays, 
    timelineColumns,
    selectedGanttTaskId,
    taskPathInfo
}) => {
    const uniqueLinks = new Map<string, { fromOrigId: number, toOrigId: number, type: string }>();

    const getLocalDateStr = (date: Date): string => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const getDayAfter = (dateStr: string): string => {
        const date = new Date(dateStr + "T12:00:00");
        date.setDate(date.getDate() + 1);
        return getLocalDateStr(date);
    };

    const isWorkingDay = (date: Date): boolean => {
        const dayOfWeek = date.getDay();
        if (!workingDays[dayOfWeek]) return false;
        const dateStr = getLocalDateStr(date);
        return !holidays.some(h => h.date === dateStr);
    };

    const getDateX = (dateStrOrObj: string | Date): number => {
        const date = typeof dateStrOrObj === 'string' ? new Date(dateStrOrObj + 'T12:00:00') : dateStrOrObj;
        if (isNaN(date.getTime())) return 0;

        if (zoomLevel === 'days') {
            const diffTime = date.getTime() - viewStartDate.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            return diffDays * 30;
        } else if (zoomLevel === 'weeks') {
            const diffTime = date.getTime() - viewStartDate.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            return (diffDays / 7) * 100;
        } else if (zoomLevel === 'months') {
            const monthDiff = (date.getFullYear() - viewStartDate.getFullYear()) * 12 + (date.getMonth() - viewStartDate.getMonth());
            const day = date.getDate();
            const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
            return monthDiff * 120 + ((day - 1) / daysInMonth) * 120;
        } else { // 'years'
            const yearDiff = date.getFullYear() - viewStartDate.getFullYear();
            const startOfYear = new Date(date.getFullYear(), 0, 1);
            const endOfYear = new Date(date.getFullYear() + 1, 0, 1);
            const pct = (date.getTime() - startOfYear.getTime()) / (endOfYear.getTime() - startOfYear.getTime());
            return yearDiff * 250 + pct * 250;
        }
    };

    // Collect and deduplicate links from both predecessors and successors of all visible tasks
    visibleTasks.forEach(task => {
        const taskIdOriginal = task.id_original_project;
        if (taskIdOriginal === undefined) return;

        // 1. Process predecessors (incoming links: B is predecessor of A)
        if (task.predecessors) {
            const allPreds: string[] = [];
            task.predecessors.forEach(p => {
                if (typeof p === 'string' && p.includes(',')) {
                    p.split(',').forEach(s => {
                        const trimmed = s.trim();
                        if (trimmed) allPreds.push(trimmed);
                    });
                } else if (typeof p === 'string') {
                    const trimmed = p.trim();
                    if (trimmed) allPreds.push(trimmed);
                }
            });

            allPreds.forEach(predStr => {
                const match = predStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                if (!match) return;
                const predOriginalId = parseInt(match[1]);
                let type = (match[2] || "FS").toUpperCase();

                // Normalize Spanish codes to English standard codes
                if (type === "FC") type = "FS";
                if (type === "CC") type = "SS";
                if (type === "CF") type = "SF";

                const key = `${predOriginalId}-${taskIdOriginal}-${type}`;
                uniqueLinks.set(key, { fromOrigId: predOriginalId, toOrigId: taskIdOriginal, type });
            });
        }

        // 2. Process successors (outgoing links: B is successor of A)
        if (task.successors) {
            const allSuccs: string[] = [];
            task.successors.forEach(s => {
                if (typeof s === 'string' && s.includes(',')) {
                    s.split(',').forEach(sub => {
                        const trimmed = sub.trim();
                        if (trimmed) allSuccs.push(trimmed);
                    });
                } else if (typeof s === 'string') {
                    const trimmed = s.trim();
                    if (trimmed) allSuccs.push(trimmed);
                }
            });

            allSuccs.forEach(succStr => {
                const match = succStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                if (!match) return;
                const succOriginalId = parseInt(match[1]);
                let type = (match[2] || "FS").toUpperCase();

                // Normalize Spanish codes to English standard codes
                if (type === "FC") type = "FS";
                if (type === "CC") type = "SS";
                if (type === "CF") type = "SF";

                const key = `${taskIdOriginal}-${succOriginalId}-${type}`;
                uniqueLinks.set(key, { fromOrigId: taskIdOriginal, toOrigId: succOriginalId, type });
            });
        }
    });

    const arrowElements: { element: React.ReactNode, isHighlighted: boolean }[] = [];

    // Draw the deduplicated links using start/end bar anchors
    uniqueLinks.forEach((link, key) => {
        const predTask = visibleTasks.find(t => t.id_original_project === link.fromOrigId);
        const task = visibleTasks.find(t => t.id_original_project === link.toOrigId);

        if (!predTask || !predTask.start || !predTask.end || !task || !task.start || !task.end) return;

        // Anchors (Left = Start, Right = End)
        const taskStart = getDateX(task.start);
        const taskEnd = getDateX(getDayAfter(task.end));
        const predStart = getDateX(predTask.start);
        const predEnd = getDateX(getDayAfter(predTask.end));

        const fromIdx = visibleTasks.findIndex(t => t.id_original_project === link.fromOrigId);
        const toIdx = visibleTasks.findIndex(t => t.id_original_project === link.toOrigId);

        if (fromIdx === -1 || toIdx === -1) return;

        const y1 = fromIdx * 44 + 18;
        const y2 = toIdx * 44 + 18;

        let x1 = predEnd;
        let x2 = taskStart;
        let isEntryFromLeft = true; // enters start of task bar (FS, SS)
        let d = "";
        const yMid = y1 + (y2 - y1) / 2;

        if (link.type === "FS") {
            x1 = predEnd;
            x2 = taskStart;
            isEntryFromLeft = true;
            
            if (x2 >= x1 + 10) {
                const midX = x1 + 10;
                d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
            } else {
                d = `M ${x1} ${y1} L ${x1 + 10} ${y1} L ${x1 + 10} ${yMid} L ${x2 - 10} ${yMid} L ${x2 - 10} ${y2} L ${x2} ${y2}`;
            }
        } else if (link.type === "SS") {
            x1 = predStart;
            x2 = taskStart;
            isEntryFromLeft = true;
            
            if (x2 >= x1) {
                const midX = x1 - 10;
                d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
            } else {
                const midX = x1 - 10;
                d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${yMid} L ${x2 - 10} ${yMid} L ${x2 - 10} ${y2} L ${x2} ${y2}`;
            }
        } else if (link.type === "FF") {
            x1 = predEnd;
            x2 = taskEnd;
            isEntryFromLeft = false;
            
            const midX = Math.max(x1, x2) + 10;
            d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
        } else if (link.type === "SF") {
            x1 = predStart;
            x2 = taskEnd;
            isEntryFromLeft = false;
            
            if (x2 > x1 - 10) {
                d = `M ${x1} ${y1} L ${x1 - 10} ${y1} L ${x1 - 10} ${yMid} L ${x2 + 10} ${yMid} L ${x2 + 10} ${y2} L ${x2} ${y2}`;
            } else {
                const midX = x1 - 10;
                d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
            }
        }

        // Highlight paths (Direct relations only)
        const isPredecessorLink = !!(taskPathInfo && 
            link.toOrigId === taskPathInfo.selectedOrigId && 
            taskPathInfo.predecessors.has(link.fromOrigId)
        );

        const isSuccessorLink = !!(taskPathInfo && 
            link.fromOrigId === taskPathInfo.selectedOrigId && 
            taskPathInfo.successors.has(link.toOrigId)
        );

        const isCriticalLink = !!(predTask.isCritical && task.isCritical);

        let color = "#475569";
        let strokeWidth = "1.5";
        let opacityClass = "opacity-60";

        if (selectedGanttTaskId) {
            if (isPredecessorLink) {
                color = "#eab308"; // Amarillo brillante
                strokeWidth = "2.5";
                opacityClass = "opacity-100";
            } else if (isSuccessorLink) {
                color = "#d946ef"; // Rosado/Fucsia
                strokeWidth = "2.5";
                opacityClass = "opacity-100";
            } else {
                opacityClass = "opacity-25"; // Atenuado
            }
        } else {
            if (isCriticalLink) {
                color = "#ef4444"; // Rojo brillante
                strokeWidth = "2";
                opacityClass = "opacity-90";
            }
        }

        const arrowHeadPath = isEntryFromLeft
            ? `M ${x2 - 4} ${y2 - 3} L ${x2} ${y2} L ${x2 - 4} ${y2 + 3}`  // pointing right (into start)
            : `M ${x2 + 4} ${y2 - 3} L ${x2} ${y2} L ${x2 + 4} ${y2 + 3}`; // pointing left (into end)

        arrowElements.push({
            isHighlighted: !!((selectedGanttTaskId && (isPredecessorLink || isSuccessorLink)) || (!selectedGanttTaskId && isCriticalLink)),
            element: (
                <g key={key} className="group/arrow">
                    <path
                        d={d}
                        fill="none"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`${opacityClass} group-hover/arrow:opacity-100 transition-all`}
                    />
                    <path
                        d={arrowHeadPath}
                        fill="none"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        className={`${opacityClass} group-hover/arrow:opacity-100 transition-all`}
                    />
                </g>
            )
        });
    });

    // Separate normal and highlighted arrows so highlighted ones are drawn last (on top)
    const normalArrows = arrowElements.filter(a => !a.isHighlighted).map(a => a.element);
    const highlightedArrows = arrowElements.filter(a => a.isHighlighted).map(a => a.element);
    const lines = [...normalArrows, ...highlightedArrows];

    const totalHeight = visibleTasks.length * 44;
    const bgElements: React.ReactNode[] = [];

    // Draw background shading and grid lines
    if (zoomLevel === 'days') {
        timelineColumns.forEach((date) => {
            const x = getDateX(date);
            if (!isWorkingDay(date)) {
                bgElements.push(
                    <rect
                        key={`shade-${date.getTime()}`}
                        x={x}
                        y={0}
                        width={30}
                        height={totalHeight}
                        fill="currentColor"
                        className="text-slate-200/90 dark:text-slate-800/75"
                    />
                );
            }
            bgElements.push(
                <line
                    key={`line-${date.getTime()}`}
                    x1={x + 30}
                    y1={0}
                    x2={x + 30}
                    y2={totalHeight}
                    stroke="currentColor"
                    className="text-slate-200/60 dark:text-slate-850/30"
                    strokeWidth="1"
                />
            );
        });
    } else if (zoomLevel === 'weeks') {
        if (timelineColumns.length > 0) {
            const startLimit = new Date(timelineColumns[0]);
            const endLimit = new Date(timelineColumns[timelineColumns.length - 1]);
            endLimit.setDate(endLimit.getDate() + 7);

            let curr = new Date(startLimit);
            const dayWidth = 100 / 7;
            while (curr < endLimit) {
                if (!isWorkingDay(curr)) {
                    const x = getDateX(curr);
                    bgElements.push(
                        <rect
                            key={`shade-${curr.getTime()}`}
                            x={x}
                            y={0}
                            width={dayWidth}
                            height={totalHeight}
                            fill="currentColor"
                            className="text-slate-200/90 dark:text-slate-800/75"
                        />
                    );
                }
                curr.setDate(curr.getDate() + 1);
            }
        }
        timelineColumns.forEach((date) => {
            const x = getDateX(date);
            bgElements.push(
                <line
                    key={`line-${date.getTime()}`}
                    x1={x + 100}
                    y1={0}
                    x2={x + 100}
                    y2={totalHeight}
                    stroke="currentColor"
                    className="text-slate-200/60 dark:text-slate-850/30"
                    strokeWidth="1"
                />
            );
        });
    } else if (zoomLevel === 'months') {
        timelineColumns.forEach((date) => {
            const x = getDateX(date);
            bgElements.push(
                <line
                    key={`line-${date.getTime()}`}
                    x1={x + 120}
                    y1={0}
                    x2={x + 120}
                    y2={totalHeight}
                    stroke="currentColor"
                    className="text-slate-200/60 dark:text-slate-850/30"
                    strokeWidth="1"
                />
            );
        });
    } else { // 'years'
        timelineColumns.forEach((date) => {
            const x = getDateX(date);
            bgElements.push(
                <line
                    key={`line-${date.getTime()}`}
                    x1={x + 250}
                    y1={0}
                    x2={x + 250}
                    y2={totalHeight}
                    stroke="currentColor"
                    className="text-slate-200/60 dark:text-slate-850/30"
                    strokeWidth="1"
                />
            );
        });
    }

    return (
        <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-0 overflow-visible">
            {bgElements}
            {lines}
        </svg>
    );
};

interface PredecessorInputCellProps {
    task: Task;
    onUpdate: (taskId: string, newPreds: string[]) => void;
}

const PredecessorInputCell: React.FC<PredecessorInputCellProps> = ({ task, onUpdate }) => {
    const [val, setVal] = useState(task.predecessors?.join(", ") || "");

    useEffect(() => {
        setVal(task.predecessors?.join(", ") || "");
    }, [task.predecessors]);

    const handleConfirm = () => {
        const rawParts = val.split(",").map(s => s.trim()).filter(s => s !== "");
        
        const validated = rawParts.map(part => {
            if (/^\d+$/.test(part)) {
                return `${part}FS`;
            }
            const match = part.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)$/i);
            if (match) {
                const id = match[1];
                let type = match[2].toUpperCase();
                if (type === "FC") type = "FS";
                if (type === "CC") type = "SS";
                if (type === "CF") type = "SF";
                return `${id}${type}`;
            }
            return part;
        });

        const cleaned = validated.filter(part => /^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i.test(part));
        onUpdate(task.id, cleaned);
    };

    return (
        <input
            type="text"
            className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={handleConfirm}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    handleConfirm();
                }
            }}
            placeholder="e.g. 1FS, 2SS"
        />
    );
};

interface SuccessorInputCellProps {
    task: Task;
    onUpdate: (taskId: string, newSuccs: string[]) => void;
}

const SuccessorInputCell: React.FC<SuccessorInputCellProps> = ({ task, onUpdate }) => {
    const [val, setVal] = useState(task.successors?.join(", ") || "");

    useEffect(() => {
        setVal(task.successors?.join(", ") || "");
    }, [task.successors]);

    const handleConfirm = () => {
        const rawParts = val.split(",").map(s => s.trim()).filter(s => s !== "");
        
        const validated = rawParts.map(part => {
            if (/^\d+$/.test(part)) {
                return `${part}FS`;
            }
            const match = part.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)$/i);
            if (match) {
                const id = match[1];
                let type = match[2].toUpperCase();
                if (type === "FC") type = "FS";
                if (type === "CC") type = "SS";
                if (type === "CF") type = "SF";
                return `${id}${type}`;
            }
            return part;
        });

        const cleaned = validated.filter(part => /^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i.test(part));
        onUpdate(task.id, cleaned);
    };

    return (
        <input
            type="text"
            className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={handleConfirm}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    handleConfirm();
                }
            }}
            placeholder="e.g. 3FS, 4FF"
        />
    );
};

const PlanningScreen: React.FC<PlanningScreenProps> = ({
    tasks,
    setTasks,
    responsibles,
    setResponsibles,
    matrixData,
    units,
    levels,
    savedProtocols,
    protocolConfigs,
    weeklyPlans,
    setWeeklyPlans,
    lookaheadPlans,
    setLookaheadPlans,
    projectInfo,
    subcontractTypes,
    setSubcontractTypes,
    cncGroups,
    setCncGroups,
    constraintGroups,
    setConstraintGroups,
    globalCNCGroups,
    globalConstraintGroups,
    baselines,
    setBaselines,
    forceSyncTasks,
    profiles,
    addActivity,
    progressHistory,
    setProgressHistory,
    notifications,
    setNotifications,
    currentUser,
    hasPermission
}) => {
    // --- State and Logic ---
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);



    const projectStatus = useMemo(() => {
        if (!progressHistory || progressHistory.length === 0) return { label: 'Al día', color: 'bg-[#52be80]', textColor: 'text-green-300', icon: 'check_circle', delay: 0 };
        const lastRecord = [...progressHistory].sort((a, b) => b.date.localeCompare(a.date))[0];
        const delay = Math.max(lastRecord.delayRutaCritica || 0, lastRecord.delayCurvaS || 0);

        if (delay <= 0) {
            return { label: 'Al día', color: 'bg-[#52be80] border-[#229954] text-white', icon: 'check_circle', delay };
        } else if (delay <= 9) {
            return { label: 'En Riesgo', color: 'bg-[#f4d03f] border-[#d4ac0d] text-slate-900', icon: 'warning', delay };
        } else {
            return { label: 'Atrasado', color: 'animate-pulse bg-[#ff0000] border-[#cc0000] text-yellow-300', icon: 'error', delay };
        }
    }, [progressHistory]);

    // --- State ---
    const [activeTab, setActiveTab] = useState<'maestra' | 'intermedia' | 'semanal'>(() => {
        return (sessionStorage.getItem('planning_activeTab') as any) || 'maestra';
    });

    React.useEffect(() => {
        sessionStorage.setItem('planning_activeTab', activeTab);
    }, [activeTab]);

    React.useEffect(() => {
        if (activeTab === 'maestra' && !hasPermission('pm_gantt_edit', 'view') && !hasPermission('pm_reprog', 'view')) {
            if (hasPermission('pi_planificaciones', 'view')) setActiveTab('intermedia');
            else if (hasPermission('ps_planificaciones', 'view')) setActiveTab('semanal');
        }
        
        // Mobile redirect: if on mobile and activeTab is 'maestra', go to 'intermedia' automatically.
        if (typeof window !== 'undefined' && window.innerWidth < 768 && activeTab === 'maestra') {
            setActiveTab('intermedia');
        }
    }, [hasPermission, activeTab]);

    // Modes
    const [isMasterEditing, setIsMasterEditing] = useState(false);
    const [activeTooltipAllocIdx, setActiveTooltipAllocIdx] = useState<number | null>(null);
    const [isWeeklyEditing, setIsWeeklyEditing] = useState(false);

    // Zoom and Calendar states
    const [zoomLevel, setZoomLevel] = useState<'days' | 'weeks' | 'months' | 'years'>('weeks');

    // Calendar Settings
    const [workingDays, setWorkingDays] = useState<boolean[]>(() => {
        const saved = localStorage.getItem(`gantt_working_days_${projectInfo?.id || 'default'}`);
        return saved ? JSON.parse(saved) : [false, true, true, true, true, true, false]; // Default Mon-Fri
    });

    const [holidays, setHolidays] = useState<{ date: string; name: string }[]>(() => {
        const saved = localStorage.getItem(`gantt_holidays_${projectInfo?.id || 'default'}`);
        return saved ? JSON.parse(saved) : [];
    });

    const [calendarModalOpen, setCalendarModalOpen] = useState(false);
    const [tempWorkingDays, setTempWorkingDays] = useState<boolean[]>([]);
    const [tempHolidays, setTempHolidays] = useState<{ date: string; name: string }[]>([]);

    useEffect(() => {
        if (calendarModalOpen) {
            setTempWorkingDays([...workingDays]);
            setTempHolidays(JSON.parse(JSON.stringify(holidays)));
        }
    }, [calendarModalOpen, workingDays, holidays]);

    const zoomFocusRef = React.useRef<{ date: Date; mouseX: number } | null>(null);

    // Process tasks with Critical Path Method (CPM) dynamically
    const processedTasks = useMemo(() => {
        return calculateCriticalPath(tasks, workingDays, holidays);
    }, [tasks, workingDays, holidays]);

    const flattenedTasks = useMemo(() => flattenTasks(processedTasks), [processedTasks]);

    const visibleFlattenedTasks = useMemo(() => {
        const getVisible = (list: Task[]): Task[] => {
            let res: Task[] = [];
            list.forEach(t => {
                if (activeTab !== 'semanal' && t.isWeeklySubtask) return;
                res.push(t);
                if (t.expanded && t.children) {
                    res = res.concat(getVisible(t.children));
                }
            });
            return res;
        };
        return getVisible(processedTasks);
    }, [processedTasks, activeTab]);

    // Timeline Config (Dynamic Range by Zoom Level)
    const timelineConfig = useMemo(() => {
        const allTasks = flattenedTasks;
        const today = new Date();
        if (allTasks.length === 0) {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 2, 1);
            return { start, end };
        }

        const startDates = allTasks
            .map(t => t.start ? new Date(t.start + "T12:00:00") : null)
            .filter((d): d is Date => d !== null && !isNaN(d.getTime()));

        const endDates = allTasks
            .map(t => t.end ? new Date(t.end + "T12:00:00") : null)
            .filter((d): d is Date => d !== null && !isNaN(d.getTime()));

        if (startDates.length === 0) {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 2, 1);
            return { start, end };
        }

        const minDate = new Date(Math.min(...startDates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...(endDates.length > 0 ? endDates : startDates).map(d => d.getTime())));

        const viewStart = new Date(minDate);
        const viewEnd = new Date(maxDate);

        if (zoomLevel === 'days') {
            viewStart.setDate(viewStart.getDate() - 7);
            viewEnd.setDate(viewEnd.getDate() + 7);
            const diffDays = Math.ceil((viewEnd.getTime() - viewStart.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays < 15) {
                viewEnd.setDate(viewEnd.getDate() + (15 - diffDays));
            }
        } else if (zoomLevel === 'weeks') {
            viewStart.setMonth(viewStart.getMonth() - 1);
            viewEnd.setMonth(viewEnd.getMonth() + 1);
        } else if (zoomLevel === 'months') {
            viewStart.setMonth(viewStart.getMonth() - 3);
            viewEnd.setMonth(viewEnd.getMonth() + 3);
        } else { // 'years'
            viewStart.setFullYear(viewStart.getFullYear() - 1);
            viewEnd.setFullYear(viewEnd.getFullYear() + 1);
        }

        // Normalize starts
        if (zoomLevel === 'days' || zoomLevel === 'weeks') {
            const day = viewStart.getDay();
            const diff = viewStart.getDate() - day + (day === 0 ? -6 : 1);
            viewStart.setDate(diff);
        } else if (zoomLevel === 'months') {
            viewStart.setDate(1);
        } else { // 'years'
            viewStart.setMonth(0, 1);
        }

        return { start: viewStart, end: viewEnd };
    }, [flattenedTasks, zoomLevel]);

    const gridStartDate = timelineConfig.start;
    const viewStartDate = timelineConfig.start;
    const viewEndDate = timelineConfig.end;

    const timelineColumns = useMemo(() => {
        const cols = [];
        let curr = new Date(viewStartDate);
        if (zoomLevel === 'days') {
            while (curr <= viewEndDate) {
                cols.push(new Date(curr));
                curr.setDate(curr.getDate() + 1);
            }
        } else if (zoomLevel === 'weeks') {
            while (curr <= viewEndDate) {
                cols.push(new Date(curr));
                curr.setDate(curr.getDate() + 7);
            }
        } else if (zoomLevel === 'months') {
            while (curr <= viewEndDate) {
                cols.push(new Date(curr));
                curr.setMonth(curr.getMonth() + 1);
            }
        } else { // 'years'
            while (curr <= viewEndDate) {
                cols.push(new Date(curr));
                curr.setFullYear(curr.getFullYear() + 1);
            }
        }
        return cols;
    }, [viewStartDate, viewEndDate, zoomLevel]);

    const timelineHeaders = useMemo(() => {
        const headers: { label: string; width: number }[] = [];
        if (zoomLevel === 'days') {
            let currentMonthLabel = "";
            let dayCount = 0;
            const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

            timelineColumns.forEach((date, idx) => {
                const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
                if (label !== currentMonthLabel) {
                    if (currentMonthLabel !== "") {
                        headers.push({ label: currentMonthLabel, width: dayCount * 30 });
                    }
                    currentMonthLabel = label;
                    dayCount = 1;
                } else {
                    dayCount++;
                }
                if (idx === timelineColumns.length - 1) {
                    headers.push({ label: currentMonthLabel, width: dayCount * 30 });
                }
            });
        } else if (zoomLevel === 'weeks') {
            let currentMonthLabel = "";
            let weekCount = 0;
            const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

            timelineColumns.forEach((date, idx) => {
                const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
                if (label !== currentMonthLabel) {
                    if (currentMonthLabel !== "") {
                        headers.push({ label: currentMonthLabel, width: weekCount * 100 });
                    }
                    currentMonthLabel = label;
                    weekCount = 1;
                } else {
                    weekCount++;
                }
                if (idx === timelineColumns.length - 1) {
                    headers.push({ label: currentMonthLabel, width: weekCount * 100 });
                }
            });
        } else if (zoomLevel === 'months') {
            let currentYearLabel = "";
            let monthCount = 0;

            timelineColumns.forEach((date, idx) => {
                const label = `${date.getFullYear()}`;
                if (label !== currentYearLabel) {
                    if (currentYearLabel !== "") {
                        headers.push({ label: currentYearLabel, width: monthCount * 120 });
                    }
                    currentYearLabel = label;
                    monthCount = 1;
                } else {
                    monthCount++;
                }
                if (idx === timelineColumns.length - 1) {
                    headers.push({ label: currentYearLabel, width: monthCount * 120 });
                }
            });
        } else { // 'years'
            headers.push({ label: "Línea de Tiempo Macro", width: timelineColumns.length * 250 });
        }
        return headers;
    }, [timelineColumns, zoomLevel]);

    // --- Calendar & Date Math Helpers ---
    const getLocalDateStr = (date: Date): string => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const getDayAfter = (dateStr: string): string => {
        const date = new Date(dateStr + "T12:00:00");
        date.setDate(date.getDate() + 1);
        return getLocalDateStr(date);
    };

    const isWorkingDay = (date: Date, currentWorkingDays = workingDays, currentHolidays = holidays): boolean => {
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        if (!currentWorkingDays[dayOfWeek]) return false;

        const dateStr = getLocalDateStr(date);
        return !currentHolidays.some(h => h.date === dateStr);
    };

    const calculateEndDate = (start: string, duration: number, currentWorkingDays = workingDays, currentHolidays = holidays): string => {
        let currDate = new Date(start + "T12:00:00");
        while (!isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
            currDate.setDate(currDate.getDate() + 1);
        }
        
        let remaining = duration - 1;
        while (remaining > 0) {
            currDate.setDate(currDate.getDate() + 1);
            if (isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
                remaining--;
            }
        }
        return getLocalDateStr(currDate);
    };

    const calculateStartDate = (end: string, duration: number, currentWorkingDays = workingDays, currentHolidays = holidays): string => {
        let currDate = new Date(end + "T12:00:00");
        while (!isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
            currDate.setDate(currDate.getDate() - 1);
        }

        let remaining = duration - 1;
        while (remaining > 0) {
            currDate.setDate(currDate.getDate() - 1);
            if (isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
                remaining--;
            }
        }
        return getLocalDateStr(currDate);
    };

    const calculateWorkingDaysDuration = (start: string, end: string, currentWorkingDays = workingDays, currentHolidays = holidays): number => {
        const startD = new Date(start + "T12:00:00");
        const endD = new Date(end + "T12:00:00");
        if (startD > endD) return 0;

        let count = 0;
        let curr = new Date(startD);
        while (curr <= endD) {
            if (isWorkingDay(curr, currentWorkingDays, currentHolidays)) {
                count++;
            }
            curr.setDate(curr.getDate() + 1);
        }
        return count;
    };

    const getNextWorkingDay = (dateStr: string, currentWorkingDays = workingDays, currentHolidays = holidays): string => {
        const date = new Date(dateStr + "T12:00:00");
        date.setDate(date.getDate() + 1);
        while (!isWorkingDay(date, currentWorkingDays, currentHolidays)) {
            date.setDate(date.getDate() + 1);
        }
        return getLocalDateStr(date);
    };

    const adjustTaskToCalendar = (task: Task, currentWorkingDays = workingDays, currentHolidays = holidays): Task => {
        if (task.type === 'folder') {
            if (task.children) {
                task.children = task.children.map(child => adjustTaskToCalendar(child, currentWorkingDays, currentHolidays));
            }
            return task;
        }

        if (!task.start || !task.end) return task;

        let startD = new Date(task.start + "T12:00:00");
        let shifted = false;
        while (!isWorkingDay(startD, currentWorkingDays, currentHolidays)) {
            startD.setDate(startD.getDate() + 1);
            shifted = true;
        }
        if (shifted) {
            task.start = getLocalDateStr(startD);
        }

        let duration = task.duration || calculateWorkingDaysDuration(task.start, task.end, currentWorkingDays, currentHolidays);
        if (duration < 1) duration = 1;
        task.duration = duration;

        task.end = calculateEndDate(task.start, duration, currentWorkingDays, currentHolidays);
        return task;
    };

    const realignTasksToCalendar = (list: Task[], currentWorkingDays: boolean[], currentHolidays: { date: string; name: string }[]): Task[] => {
        const adjustRecursive = (tasksList: Task[]): Task[] => {
            return tasksList.map(t => {
                if (t.type === 'folder') {
                    return {
                        ...t,
                        children: t.children ? adjustRecursive(t.children) : undefined
                    };
                }
                if (!t.start || !t.end) return t;

                let sDate = new Date(t.start + "T12:00:00");
                while (!isWorkingDay(sDate, currentWorkingDays, currentHolidays)) {
                    sDate.setDate(sDate.getDate() + 1);
                }
                const newStart = getLocalDateStr(sDate);
                const duration = t.duration || 1;
                const newEnd = calculateEndDate(newStart, duration, currentWorkingDays, currentHolidays);

                return {
                    ...t,
                    start: newStart,
                    end: newEnd
                };
            });
        };

        const adjusted = adjustRecursive(list);
        return syncDependencies(adjusted, undefined, currentWorkingDays, currentHolidays);
    };

    // --- Coordinate Math Helpers ---
    const getDateX = (dateStrOrObj: string | Date, currentZoomLevel = zoomLevel, currentGridStartDate?: Date): number => {
        const refStartDate = currentGridStartDate || gridStartDate;
        const date = typeof dateStrOrObj === 'string' ? new Date(dateStrOrObj + 'T12:00:00') : dateStrOrObj;
        if (isNaN(date.getTime())) return 0;

        if (currentZoomLevel === 'days') {
            const diffTime = date.getTime() - refStartDate.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            return diffDays * 30;
        } else if (currentZoomLevel === 'weeks') {
            const diffTime = date.getTime() - refStartDate.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            return (diffDays / 7) * 100;
        } else if (currentZoomLevel === 'months') {
            const monthDiff = (date.getFullYear() - refStartDate.getFullYear()) * 12 + (date.getMonth() - refStartDate.getMonth());
            const day = date.getDate();
            const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
            return monthDiff * 120 + ((day - 1) / daysInMonth) * 120;
        } else { // 'years'
            const yearDiff = date.getFullYear() - refStartDate.getFullYear();
            const startOfYear = new Date(date.getFullYear(), 0, 1);
            const endOfYear = new Date(date.getFullYear() + 1, 0, 1);
            const pct = (date.getTime() - startOfYear.getTime()) / (endOfYear.getTime() - startOfYear.getTime());
            return yearDiff * 250 + pct * 250;
        }
    };

    const getXDate = (x: number, currentZoomLevel = zoomLevel, currentGridStartDate?: Date): Date => {
        const refStartDate = currentGridStartDate || gridStartDate;
        if (currentZoomLevel === 'days') {
            const diffDays = x / 30;
            return new Date(refStartDate.getTime() + diffDays * 24 * 60 * 60 * 1000);
        } else if (currentZoomLevel === 'weeks') {
            const diffDays = (x / 100) * 7;
            return new Date(refStartDate.getTime() + diffDays * 24 * 60 * 60 * 1000);
        } else if (currentZoomLevel === 'months') {
            const monthDiff = Math.floor(x / 120);
            const pct = (x % 120) / 120;
            const targetMonth = new Date(refStartDate.getFullYear(), refStartDate.getMonth() + monthDiff, 1);
            const daysInMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
            const day = Math.max(1, Math.min(daysInMonth, 1 + Math.round(pct * daysInMonth)));
            return new Date(targetMonth.getFullYear(), targetMonth.getMonth(), day, 12, 0, 0);
        } else { // 'years'
            const yearDiff = Math.floor(x / 250);
            const pct = (x % 250) / 250;
            const targetYear = refStartDate.getFullYear() + yearDiff;
            const startOfYear = new Date(targetYear, 0, 1).getTime();
            const endOfYear = new Date(targetYear + 1, 0, 1).getTime();
            const dateTime = startOfYear + pct * (endOfYear - startOfYear);
            return new Date(dateTime);
        }
    };

    React.useEffect(() => {
        const container = rightScrollRef.current;
        if (!container) return;

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                e.preventDefault(); // Bloquea por completo el zoom nativo del navegador, manteniendo fijo el tamaño de la interfaz general al 100%

                const rect = container.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const contentX = mouseX + container.scrollLeft;

                const focusDate = getXDate(contentX, zoomLevel, gridStartDate);

                const isZoomIn = e.deltaY < 0;
                const zoomOrder: ('days' | 'weeks' | 'months' | 'years')[] = ['days', 'weeks', 'months', 'years'];
                const currentIndex = zoomOrder.indexOf(zoomLevel);

                let nextIndex = currentIndex;
                if (isZoomIn && currentIndex > 0) {
                    nextIndex = currentIndex - 1;
                } else if (!isZoomIn && currentIndex < zoomOrder.length - 1) {
                    nextIndex = currentIndex + 1;
                }

                const nextZoomLevel = zoomOrder[nextIndex];
                if (nextZoomLevel !== zoomLevel) {
                    zoomFocusRef.current = { date: focusDate, mouseX };
                    setZoomLevel(nextZoomLevel);
                }
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [zoomLevel, gridStartDate]);

    React.useLayoutEffect(() => {
        if (zoomFocusRef.current && rightScrollRef.current) {
            const container = rightScrollRef.current;
            const { date, mouseX } = zoomFocusRef.current;
            const newContentX = getDateX(date, zoomLevel, gridStartDate);
            const newScrollLeft = newContentX - mouseX;
            container.scrollLeft = Math.max(0, newScrollLeft);
            zoomFocusRef.current = null;
        }
    }, [zoomLevel, gridStartDate]);

    const handleSaveCalendar = () => {
        setWorkingDays(tempWorkingDays);
        setHolidays(tempHolidays);

        localStorage.setItem(`gantt_working_days_${projectInfo?.id || 'default'}`, JSON.stringify(tempWorkingDays));
        localStorage.setItem(`gantt_holidays_${projectInfo?.id || 'default'}`, JSON.stringify(tempHolidays));

        setTasks(prev => {
            return realignTasksToCalendar(prev, tempWorkingDays, tempHolidays);
        });

        addActivity({
            type: 'planning',
            title: 'Calendario laboral actualizado',
            description: 'Se ha modificado la jornada estándar y la lista de días festivos del proyecto.',
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setCalendarModalOpen(false);
    };

    const handleExportToMSProject = () => {
        interface ExportTaskItem {
            uid: number;
            id: number;
            name: string;
            start: string;
            end: string;
            duration: number;
            progress: number;
            isSummary: boolean;
            outlineLevel: number;
            outlineNumber: string;
            predecessors: { fromUid: number; type: number }[];
        }

        const list: ExportTaskItem[] = [];
        let nextUid = 1;
        const origIdToUidMap = new Map<number, number>();

        const buildExportList = (tasksList: Task[], level = 1, parentPrefix = "") => {
            tasksList.forEach((t, index) => {
                const currentIndexStr = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;
                const currentUid = t.id_original_project || nextUid++;
                origIdToUidMap.set(t.id_original_project || currentUid, currentUid);

                const hasChildren = t.children && t.children.length > 0;
                const startStr = t.start || getLocalDateStr(new Date());
                const endStr = t.end || startStr;

                const item: ExportTaskItem = {
                    uid: currentUid,
                    id: currentUid,
                    name: t.name,
                    start: startStr,
                    end: endStr,
                    duration: t.duration || 1,
                    progress: t.progress || 0,
                    isSummary: t.type === 'folder' || hasChildren,
                    outlineLevel: level,
                    outlineNumber: currentIndexStr,
                    predecessors: []
                };

                (t.predecessors || []).forEach(pStr => {
                    const match = pStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                    if (match) {
                        const predOrigId = parseInt(match[1]);
                        let typeStr = (match[2] || "FS").toUpperCase();
                        if (typeStr === "FC") typeStr = "FS";
                        if (typeStr === "CC") typeStr = "SS";
                        if (typeStr === "CF") typeStr = "SF";

                        let typeCode = 1; // FS
                        if (typeStr === "FF") typeCode = 0;
                        if (typeStr === "SF") typeCode = 2;
                        if (typeStr === "SS") typeCode = 3;

                        item.predecessors.push({ fromUid: predOrigId, type: typeCode });
                    }
                });

                list.push(item);

                if (t.children && t.children.length > 0) {
                    buildExportList(t.children, level + 1, currentIndexStr);
                }
            });
        };

        buildExportList(tasks);

        list.forEach(item => {
            item.predecessors = item.predecessors.map(p => {
                const resolvedUid = origIdToUidMap.get(p.fromUid);
                return {
                    fromUid: resolvedUid || p.fromUid,
                    type: p.type
                };
            }).filter(p => origIdToUidMap.has(p.fromUid));
        });

        const weekDaysXml = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((dayName, idx) => {
            const isWorking = workingDays[idx];
            const dayNum = idx + 1;
            if (isWorking) {
                return `
        <WeekDay>
            <DayType>${dayNum}</DayType>
            <DayWorking>1</DayWorking>
            <WorkingTimes>
                <WorkingTime>
                    <FromTime>08:00:00</FromTime>
                    <ToTime>12:00:00</ToTime>
                </WorkingTime>
                <WorkingTime>
                    <FromTime>13:00:00</FromTime>
                    <ToTime>17:00:00</ToTime>
                </WorkingTime>
            </WorkingTimes>
        </WeekDay>`;
            } else {
                return `
        <WeekDay>
            <DayType>${dayNum}</DayType>
            <DayWorking>0</DayWorking>
        </WeekDay>`;
            }
        }).join('');

        const exceptionsXml = holidays.map((h) => `
        <Exception>
            <EnteredByOccurrences>0</EnteredByOccurrences>
            <TimePeriod>
                <From>${h.date}T08:00:00</From>
                <To>${h.date}T17:00:00</To>
            </TimePeriod>
            <Occurrences>1</Occurrences>
            <Name>${escapeXml(h.name)}</Name>
            <Type>1</Type>
            <DayWorking>0</DayWorking>
        </Exception>`).join('');

        const tasksXml = list.map(item => {
            const durationHours = item.duration * 8;
            const durationFormat = `PT${durationHours}H0M0S`;

            const predecessorLinksXml = item.predecessors.map(p => `
            <PredecessorLink>
                <PredecessorUID>${p.fromUid}</PredecessorUID>
                <Type>${p.type}</Type>
                <LinkLag>0</LinkLag>
                <LagFormat>7</LagFormat>
            </PredecessorLink>`).join('');

            return `
        <Task>
            <UID>${item.uid}</UID>
            <ID>${item.id}</ID>
            <Name>${escapeXml(item.name)}</Name>
            <Type>0</Type>
            <Manual>0</Manual>
            <PercentComplete>${item.progress}</PercentComplete>
            <Start>${item.start}T08:00:00</Start>
            <Finish>${item.end}T17:00:00</Finish>
            <Duration>${durationFormat}</Duration>
            <DurationFormat>7</DurationFormat>
            <OutlineLevel>${item.outlineLevel}</OutlineLevel>
            <OutlineNumber>${item.outlineNumber}</OutlineNumber>
            <Summary>${item.isSummary ? 1 : 0}</Summary>
            ${predecessorLinksXml}
        </Task>`;
        }).join('');

        const projectXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Project xmlns="http://schemas.microsoft.com/project">
    <Name>${escapeXml(projectInfo.name)}</Name>
    <Title>${escapeXml(projectInfo.name)}</Title>
    <StartDate>${gridStartDate ? getLocalDateStr(gridStartDate) : getLocalDateStr(new Date())}T08:00:00</StartDate>
    <CalendarUID>1</CalendarUID>
    <Calendars>
        <Calendar>
            <UID>1</UID>
            <Name>Calendario Laboral</Name>
            <IsBaseCalendar>1</IsBaseCalendar>
            <WeekDays>
                ${weekDaysXml}
            </WeekDays>
            <Exceptions>
                ${exceptionsXml}
            </Exceptions>
        </Calendar>
    </Calendars>
    <Tasks>
        ${tasksXml}
    </Tasks>
</Project>`;

        function escapeXml(unsafe: string): string {
            return unsafe.replace(/[<>&'"]/g, (c) => {
                switch (c) {
                    case '<': return '&lt;';
                    case '>': return '&gt;';
                    case '&': return '&amp;';
                    case '\'': return '&apos;';
                    case '"': return '&quot;';
                    default: return c;
                }
            });
        }

        const blob = new Blob([projectXml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Carta_Gantt_${projectInfo.name.replace(/\s+/g, '_')}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        addActivity({
            type: 'planning',
            title: 'Carta Gantt Exportada',
            description: 'Se exportó la planificación maestra en formato XML compatible con Microsoft Project (MSPDI).',
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setExportModalOpen(false);
    };

    // Tarea column resizing and left pane splitter states
    const [tareaColumnWidth, setTareaColumnWidth] = useState<number | null>(null);
    const [leftPaneWidth, setLeftPaneWidth] = useState<number | null>(null);

    const handleResizeMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const headerElement = document.getElementById('planning-tarea-header');
        const startWidth = headerElement ? headerElement.getBoundingClientRect().width : 250;
        const startX = e.clientX;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(150, Math.min(800, startWidth + (moveEvent.clientX - startX)));
            setTareaColumnWidth(newWidth);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleSplitterMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const paneElement = document.getElementById('planning-left-pane');
        const startWidth = paneElement ? paneElement.getBoundingClientRect().width : 480;
        const startX = e.clientX;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const maxAllowedWidth = Math.max(800, window.innerWidth * 0.75);
            const newWidth = Math.max(250, Math.min(maxAllowedWidth, startWidth + (moveEvent.clientX - startX)));
            setLeftPaneWidth(newWidth);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // States for Warning on Exit without Saving
    const [showExitWarningModal, setShowExitWarningModal] = useState(false);
    const [exitWarningTarget, setExitWarningTarget] = useState<'semanal' | 'intermedia' | null>(null);
    const [weeklyPlansBackup, setWeeklyPlansBackup] = useState<WeeklyPlan[] | null>(null);
    const [lookaheadPlansBackup, setLookaheadPlansBackup] = useState<LookaheadPlan[] | null>(null);
    const [isBreakdownMode, setIsBreakdownMode] = useState(false);
    const [isITEView, setIsITEView] = useState(false); // NEW: ITE View State
    const [showGanttSuggestions, setShowGanttSuggestions] = useState(false);

    // Data Store for Weekly Plans is now in props (weeklyPlans)
    const [graphicMode, setGraphicMode] = useState(false);
    const [graphicSelectedTaskId, setGraphicSelectedTaskId] = useState<string | null>(null);
    const [selectedGanttTaskId, setSelectedGanttTaskId] = useState<string | null>(null);

    // Calculate Task Path dependencies (Predecessors and Successors) - Direct relationships only (No recursion/cascading)
    const taskPathInfo = useMemo(() => {
        if (!selectedGanttTaskId) return null;
        const selectedTask = flattenedTasks.find(t => t.id === selectedGanttTaskId);
        if (!selectedTask) return null;

        const selectedOrigId = selectedTask.id_original_project;
        const predecessors = new Set<number>();
        const successors = new Set<number>();

        const parseIds = (arr: any[] | undefined): number[] => {
            if (!arr) return [];
            const ids: number[] = [];
            arr.forEach(val => {
                if (typeof val === 'string') {
                    val.split(',').forEach(part => {
                        const match = part.trim().match(/^(\d+)/);
                        if (match) {
                            ids.push(parseInt(match[1]));
                        }
                    });
                }
            });
            return ids;
        };

        // Populate direct predecessors from the selected task
        parseIds(selectedTask.predecessors).forEach(id => predecessors.add(id));

        // Populate direct successors from the selected task
        parseIds(selectedTask.successors).forEach(id => successors.add(id));

        return {
            selectedId: selectedTask.id,
            selectedOrigId,
            predecessors,
            successors
        };
    }, [selectedGanttTaskId, flattenedTasks]);
    const [activePlanId, setActivePlanId] = useState<number | null>(() => {
        const stored = sessionStorage.getItem('planning_activePlanId');
        return stored ? parseInt(stored) : null;
    });

    React.useEffect(() => {
        if (activePlanId !== null) {
            sessionStorage.setItem('planning_activePlanId', activePlanId.toString());
        } else {
            sessionStorage.removeItem('planning_activePlanId');
        }
    }, [activePlanId]);

    // States for Additional Tasks Modal
    const [additionalModalOpen, setAdditionalModalOpen] = useState(false);
    const [editingAdditionalIndex, setEditingAdditionalIndex] = useState<number | null>(null);
    const [additionalModalData, setAdditionalModalData] = useState({
        index: null as number | null,
        levelId: '',
        unitId: '',
        additionalTaskName: '',
        responsibleId: '',
        days: { L: false, M: false, X: false, J: false, V: false, S: false } as Record<string, boolean>
    });

    // States for Move Modal
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [movingItemId, setMovingItemId] = useState<string | null>(null);

    const handleSaveAdditionalTask = () => {
        if (!additionalModalData.levelId) {
            alert("Debe seleccionar un nivel.");
            return;
        }
        if (!additionalModalData.unitId) {
            alert("Debe seleccionar una unidad física.");
            return;
        }
        if (!additionalModalData.additionalTaskName.trim()) {
            alert("Debe ingresar un nombre para la tarea.");
            return;
        }
        if (!additionalModalData.responsibleId) {
            alert("Debe seleccionar un responsable.");
            return;
        }
        if (!Object.values(additionalModalData.days).some(d => d)) {
            alert("Debe seleccionar al menos un día de ejecución.");
            return;
        }

        if (activePlanId === null) return;

        setWeeklyPlans(prevPlans => prevPlans.map(plan => {
            if (plan.id === activePlanId) {
                const newAllocations = [...plan.allocations];
                
                if (additionalModalData.index !== null) {
                    // Editing existing allocation
                    newAllocations[additionalModalData.index] = {
                        ...newAllocations[additionalModalData.index],
                        unitId: additionalModalData.unitId,
                        additionalTaskName: additionalModalData.additionalTaskName,
                        responsibleId: additionalModalData.responsibleId,
                        days: additionalModalData.days
                    };
                } else {
                    // Creating new additional allocation
                    const newAlloc: WeeklyAllocation = {
                        taskId: 'additional_' + Date.now(),
                        unitId: additionalModalData.unitId,
                        responsibleId: additionalModalData.responsibleId,
                        days: additionalModalData.days,
                        executed: null,
                        isAdditional: true,
                        additionalTaskName: additionalModalData.additionalTaskName
                    };
                    newAllocations.push(newAlloc);
                }
                
                return { ...plan, allocations: newAllocations };
            }
            return plan;
        }));

        setAdditionalModalOpen(false);
        setAdditionalModalData({
            index: null,
            levelId: '',
            unitId: '',
            additionalTaskName: '',
            responsibleId: '',
            days: { L: false, M: false, X: false, J: false, V: false, S: false }
        });
    };
    const [draftWeeklyPlan, setDraftWeeklyPlan] = useState<WeeklyPlan | null>(null);
    const [editingWeeklyPlanId, setEditingWeeklyPlanId] = useState<number | null>(null);

    // Data Store for Lookahead Plans
    const [activeLookaheadId, setActiveLookaheadId] = useState<string | null>(null);
    const [lookaheadModalOpen, setLookaheadModalOpen] = useState(false);
    const [lookaheadRange, setLookaheadRange] = useState({ start: '', end: '' });
    const [editingLookaheadId, setEditingLookaheadId] = useState<string | null>(null);

    // Add Constraint Modal State
    const [constraintModalOpen, setConstraintModalOpen] = useState(false);
    const [activeConstraintTask, setActiveConstraintTask] = useState<string | null>(null); // Task ID being constrained
    const [newConstraintData, setNewConstraintData] = useState({
        typeId: '',
        responsibleId: '',
        description: '',
        requiredDate: '',
        unitsType: 'na' as 'na' | 'all' | 'custom',
        unitIds: [] as string[]
    });

    // Unit selection modal state
    const [unitSelectionModalOpen, setUnitSelectionModalOpen] = useState(false);
    const [unitsViewModalOpen, setUnitsViewModalOpen] = useState(false);
    const [activeConstraintForUnits, setActiveConstraintForUnits] = useState<string | null>(null);

    // Modals State
    const [newPlanModalOpen, setNewPlanModalOpen] = useState(false);
    const [weeklyPlanDates, setWeeklyPlanDates] = useState({ start: '', end: '' });

    // Import/Export Modals
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    // Document Manager Integration Mock
    const [docManagerModalOpen, setDocManagerModalOpen] = useState(false);
    const [docManagerModalType, setDocManagerModalType] = useState<'import' | 'reprogram'>('import');

    // NEW: Sync Progress Modal State
    const [syncProgressModalOpen, setSyncProgressModalOpen] = useState(false);
    const [syncDay, setSyncDay] = useState<number>(5); // 1-7, default Friday (5)
    const [manualCriticalPathDelays, setManualCriticalPathDelays] = useState<Record<string, number>>({});

    const [modalOpen, setModalOpen] = useState(false); // Generic Input Modal
    const [inputName, setInputName] = useState("");
    const [targetParentId, setTargetParentId] = useState<string | null>(null);
    const [editingConstraint, setEditingConstraint] = useState<string | null>(null);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);

    // Baseline & Reprogramming State
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [reprogrammingModalOpen, setReprogrammingModalOpen] = useState(false);
    const [reprogrammingComment, setReprogrammingComment] = useState('');
    const [comparisonBaselineId, setComparisonBaselineId] = useState<string | null>(null);
    const [showComparison, setShowComparison] = useState(false);

    const comparisonBaseline = useMemo(() => {
        if (!comparisonBaselineId) return null;
        return baselines.find(b => b.id === comparisonBaselineId) || null;
    }, [comparisonBaselineId, baselines]);
    const [modalMode, setModalMode] = useState<'folder' | 'subtask'>('folder');

    // Bi-directional Scroll Sync
    const leftScrollRef = React.useRef<HTMLDivElement>(null);
    const rightScrollRef = React.useRef<HTMLDivElement>(null);
    const isScrollingLeft = React.useRef(false);
    const isScrollingRight = React.useRef(false);

    const handleLeftScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!leftScrollRef.current || !rightScrollRef.current) return;
        if (isScrollingRight.current) {
            isScrollingRight.current = false;
            return;
        }
        isScrollingLeft.current = true;
        rightScrollRef.current.scrollTop = leftScrollRef.current.scrollTop;
    };

    const handleRightScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!leftScrollRef.current || !rightScrollRef.current) return;
        if (isScrollingLeft.current) {
            isScrollingLeft.current = false;
            return;
        }
        isScrollingRight.current = true;
        leftScrollRef.current.scrollTop = rightScrollRef.current.scrollTop;
    };

    // Responsibles Modals
    const [responsiblesMenuOpen, setResponsiblesMenuOpen] = useState(false);

    // CNC (Causas de No Cumplimiento) State
    const [cncMenuOpen, setCncMenuOpen] = useState(false);
    const [cncModalOpen, setCncModalOpen] = useState(false);
    // REMOVED LOCAL STATE cncGroups
    const [newCNCGroupName, setNewCNCGroupName] = useState('');
    const [activeGroupForNewCNC, setActiveGroupForNewCNC] = useState<string | null>(null);
    const [newCNCCauseName, setNewCNCCauseName] = useState('');

    // NEW CRUD State for CNC
    const [editingCNCGroupId, setEditingCNCGroupId] = useState<string | null>(null);
    const [editCNCGroupName, setEditCNCGroupName] = useState('');
    const [editingCNCCauseId, setEditingCNCCauseId] = useState<string | null>(null);
    const [editCNCCauseName, setEditCNCCauseName] = useState('');

    // Constraints Master Management State
    const [constraintsMenuOpen, setConstraintsMenuOpen] = useState(false);
    const [constraintsModalOpen, setConstraintsModalOpen] = useState(false);
    // REMOVED LOCAL STATE constraintGroups
    const [newConstraintGroupName, setNewConstraintGroupName] = useState('');
    const [activeGroupForNewConstraint, setActiveGroupForNewConstraint] = useState<string | null>(null);
    const [newConstraintName, setNewConstraintName] = useState('');

    // NEW CRUD State for Constraints
    const [editingConstraintGroupId, setEditingConstraintGroupId] = useState<string | null>(null);
    const [editConstraintGroupName, setEditConstraintGroupName] = useState('');
    const [editingConstraintDefId, setEditingConstraintDefId] = useState<string | null>(null);
    const [editConstraintDefName, setEditConstraintDefName] = useState('');

    // Plan Task Modal State
    const [planTaskModalOpen, setPlanTaskModalOpen] = useState(false);
    const [planningTaskId, setPlanningTaskId] = useState<string | null>(null);
    const [currentAllocations, setCurrentAllocations] = useState<(WeeklyAllocation & { selected: boolean; isRestricted: boolean; isPreviouslyExecuted?: boolean; isReprogrammed?: boolean })[]>([]);

    // NEW: Mobile Filters and Obs Modals
    const [mobileFilterITE, setMobileFilterITE] = useState<'all' | 'mine'>('mine');
    const [mobileFilterWeekly, setMobileFilterWeekly] = useState<'all' | 'mine'>(() => {
        return (sessionStorage.getItem('planning_mobileFilterWeekly') as any) || 'mine';
    });

    React.useEffect(() => {
        sessionStorage.setItem('planning_mobileFilterWeekly', mobileFilterWeekly);
    }, [mobileFilterWeekly]);
    const [constraintObsModalOpen, setConstraintObsModalOpen] = useState(false);
    const [constraintObsData, setConstraintObsData] = useState<{planId: string, constraintId: string, currentStatus: string, observations: string, description: string} | null>(null);

    // Edit Single Allocation State
    const [isEditingAllocation, setIsEditingAllocation] = useState(false);
    const [editingAllocationIndex, setEditingAllocationIndex] = useState<number>(-1);
    const [editingAllocationData, setEditingAllocationData] = useState<WeeklyAllocation | null>(null);

    // Gantt Autoprogram Mass Assignment State
    const [ganttAutoPlanModalOpen, setGanttAutoPlanModalOpen] = useState(false);
    const [ganttAutoPlanSelectedRespId, setGanttAutoPlanSelectedRespId] = useState('');

    // Context Menu
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({ visible: false, x: 0, y: 0, taskId: null });

    // Dragging state for Gantt bars
    const [draggingTask, setDraggingTask] = useState<{ taskId: string; startX: number; originalStart: string } | null>(null);



    // Dragging Logic Effect
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!draggingTask) return;

            const deltaX = e.clientX - draggingTask.startX;
            const daysToShift = Math.round(deltaX / (100 / 7)); // 100px = 7 days

            if (daysToShift === 0) return;

            const updateTaskDates = (list: Task[], tid: string, shift: number, origStart: string): Task[] => {
                return list.map(t => {
                    if (t.id === tid) {
                        const sDate = new Date(origStart + "T12:00:00");
                        sDate.setDate(sDate.getDate() + shift);
                        
                        // Ensure start lands on a working day
                        while (!isWorkingDay(sDate, workingDays, holidays)) {
                            sDate.setDate(sDate.getDate() + 1);
                        }
                        const newStart = getLocalDateStr(sDate);
                        const newEnd = calculateEndDate(newStart, t.duration || 1, workingDays, holidays);

                        return { ...t, start: newStart, end: newEnd };
                    }
                    if (t.children) return { ...t, children: updateTaskDates(t.children, tid, shift, origStart) };
                    return t;
                });
            };

            setTasks(prev => {
                const updated = updateTaskDates(prev, draggingTask.taskId, daysToShift, draggingTask.originalStart);
                return syncDependencies(updated, draggingTask.taskId);
            });
        };

        const handleMouseUp = () => {
            setDraggingTask(null);
        };

        if (draggingTask) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [draggingTask]);

    // Generic Task Logic Helper: Sync Dependencies using a Fixed-Point Iteration Solver
    const syncDependencies = (
        currentTasks: Task[],
        changedTaskId?: string,
        currentWorkingDays = workingDays,
        currentHolidays = holidays
    ): Task[] => {
        let updated = JSON.parse(JSON.stringify(currentTasks));
        
        let changed = true;
        let iterations = 0;
        const maxIterations = 50;
        
        while (changed && iterations < maxIterations) {
            changed = false;
            iterations++;
            
            const flat = flattenTasks(updated);
            
            flat.forEach(task => {
                if (task.type === 'folder' || !task.predecessors || task.predecessors.length === 0) return;
                
                let latestStartStr = "";
                let latestEndStr = "";
                
                task.predecessors.forEach(predStr => {
                    const match = predStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                    if (!match) return;
                    
                    const predOriginalId = parseInt(match[1]);
                    let type = (match[2] || "FS").toUpperCase();
                    
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    
                    const predTask = flat.find(pt => pt.id_original_project === predOriginalId);
                    if (!predTask || !predTask.start || !predTask.end) return;
                    
                    if (type === "FS") {
                        const candidateStart = getNextWorkingDay(predTask.end, currentWorkingDays, currentHolidays);
                        if (!latestStartStr || candidateStart > latestStartStr) {
                            latestStartStr = candidateStart;
                        }
                    } else if (type === "SS") {
                        const candidateStart = predTask.start;
                        if (!latestStartStr || candidateStart > latestStartStr) {
                            latestStartStr = candidateStart;
                        }
                    } else if (type === "FF") {
                        const candidateEnd = predTask.end;
                        if (!latestEndStr || candidateEnd > latestEndStr) {
                            latestEndStr = candidateEnd;
                        }
                    } else if (type === "SF") {
                        const candidateEnd = predTask.start;
                        if (!latestEndStr || candidateEnd > latestEndStr) {
                            latestEndStr = candidateEnd;
                        }
                    }
                });
                
                const duration = task.duration && task.duration > 0 ? task.duration : 1;
                
                let targetStart = "";
                if (latestStartStr && latestEndStr) {
                    const startFromEnd = calculateStartDate(latestEndStr, duration, currentWorkingDays, currentHolidays);
                    targetStart = latestStartStr > startFromEnd ? latestStartStr : startFromEnd;
                } else if (latestStartStr) {
                    targetStart = latestStartStr;
                } else if (latestEndStr) {
                    targetStart = calculateStartDate(latestEndStr, duration, currentWorkingDays, currentHolidays);
                }
                
                if (targetStart) {
                    let tStartD = new Date(targetStart + "T12:00:00");
                    while (!isWorkingDay(tStartD, currentWorkingDays, currentHolidays)) {
                        tStartD.setDate(tStartD.getDate() + 1);
                    }
                    const normalizedStart = getLocalDateStr(tStartD);
                    const targetEnd = calculateEndDate(normalizedStart, duration, currentWorkingDays, currentHolidays);
                    
                    if (task.start !== normalizedStart || task.end !== targetEnd) {
                        task.start = normalizedStart;
                        task.end = targetEnd;
                        changed = true;
                    }
                }
            });
        }
        
        return calculateCriticalPath(updated, currentWorkingDays, currentHolidays);
    };

    // --- Actions ---

    // Lookahead Calculation Logic
    const calculatedWeeksAhead = useMemo(() => {
        if (!lookaheadRange.start || !lookaheadRange.end) return 0;
        const s = new Date(lookaheadRange.start);
        const e = new Date(lookaheadRange.end);
        const diffTime = e.getTime() - s.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 0) return 0;
        return Math.max(1, Math.round(diffDays / 7));
    }, [lookaheadRange]);

    const handleOpenLookaheadModal = () => {
        // Default: Next Monday + 4 weeks (standard lookahead)
        const today = new Date();
        const nextMonday = new Date(today);
        nextMonday.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7 || 7)); // Calculate next Monday

        const fourWeeksLater = new Date(nextMonday);
        fourWeeksLater.setDate(nextMonday.getDate() + 27); // 4 weeks window (approx 28 days - 1 day to end on Sunday)

        setLookaheadRange({
            start: nextMonday.toISOString().split('T')[0],
            end: fourWeeksLater.toISOString().split('T')[0]
        });
        setLookaheadModalOpen(true);
    };

    const handleCreateLookaheadPlan = () => {
        if (!lookaheadRange.start || !lookaheadRange.end) {
            alert("Por favor seleccione ambas fechas.");
            return;
        }

        if (editingLookaheadId) {
            setLookaheadPlans(prev => prev.map(p => {
                if (p.id === editingLookaheadId) {
                    return {
                        ...p,
                        weeksAhead: calculatedWeeksAhead,
                        startDate: lookaheadRange.start,
                        endDate: lookaheadRange.end,
                    };
                }
                return p;
            }));
            addActivity({
                type: 'planning',
                title: 'Planificación Intermedia Editada',
                description: `Se han modificado las fechas del plan ${lookaheadRange.start} al ${lookaheadRange.end}`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
            setLookaheadModalOpen(false);
            setEditingLookaheadId(null);
            return;
        }

        const newPlan: LookaheadPlan = {
            id: Math.random().toString(36).substr(2, 9),
            weeksAhead: calculatedWeeksAhead, // Calculated from range
            startDate: lookaheadRange.start,
            endDate: lookaheadRange.end,
            status: 'En Planificación',
            constraints: [],
            clearedTaskIds: []
        };
        setLookaheadPlansBackup(JSON.parse(JSON.stringify(lookaheadPlans)));
        setLookaheadPlans([...lookaheadPlans, newPlan]);
        setActiveLookaheadId(newPlan.id);

        addActivity({
            type: 'planning',
            title: 'Nueva Planificación Intermedia',
            description: `Se ha creado una nueva planificación intermedia del ${newPlan.startDate} al ${newPlan.endDate}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setLookaheadModalOpen(false);
    };

    const getTasksForLookahead = (startDate: string, endDate: string) => {
        const allTasks = flattenTasks(tasks);
        return allTasks.filter(t => {
            if (t.type === 'folder' || !t.start || !t.end) return false;
            // Check if task range overlaps with lookahead week
            // Overlap logic: (StartA <= EndB) and (EndA >= StartB)
            return (t.start <= endDate && t.end >= startDate);
        });
    };

    // Toggle Task Cleared Status (No Aplica Restricción)
    const handleToggleTaskCleared = (planId: string, taskId: string) => {
        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === planId) {
                const currentCleared = p.clearedTaskIds || [];
                const isCleared = currentCleared.includes(taskId);
                let newCleared: string[];

                if (isCleared) {
                    newCleared = currentCleared.filter(id => id !== taskId);
                } else {
                    newCleared = [...currentCleared, taskId];
                }

                return { ...p, clearedTaskIds: newCleared };
            }
            return p;
        }));
    };

    // New Constraint Logic
    const handleOpenAddConstraintModal = (taskId: string) => {
        setActiveConstraintTask(taskId);
        setNewConstraintData({
            typeId: '',
            responsibleId: '',
            description: '',
            requiredDate: activeLookahead?.startDate || '',
            unitsType: 'na',
            unitIds: []
        });
        setConstraintModalOpen(true);
    };

    const handleSaveConstraint = () => {
        if (!activeLookaheadId || !activeConstraintTask) return;
        if (!newConstraintData.typeId || !newConstraintData.responsibleId || !newConstraintData.description) {
            alert("Por favor complete todos los campos obligatorios.");
            return;
        }

        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === activeLookaheadId) {
                return {
                    ...p,
                    constraints: [...p.constraints, {
                        id: Math.random().toString(36).substr(2, 9),
                        taskId: activeConstraintTask,
                        typeId: newConstraintData.typeId,
                        description: newConstraintData.description,
                        responsibleId: newConstraintData.responsibleId,
                        requiredDate: newConstraintData.requiredDate,
                        status: 'Pendiente',
                        unitsType: newConstraintData.unitsType,
                        unitIds: newConstraintData.unitIds
                    }]
                };
            }
            return p;
        }));
        setConstraintModalOpen(false);
    };

    const dispatchPlanningNotifications = async (planName: string, planType: 'Semanal' | 'Intermedia', assignments: { taskId: string, responsibleId: string }[]) => {
        try {
            const flat = flattenTasks(tasks);
            const assignmentsGrouped: Record<string, { userName: string, tasks: any[] }> = {};
            
            assignments.forEach(a => {
                if (!a.responsibleId || !a.taskId) return;
                const responsible = responsibles.find(r => r.id === a.responsibleId);
                if (!responsible || !responsible.email) return;

                const task = flat.find(t => t.id === a.taskId);
                if (!task) return;

                if (!assignmentsGrouped[responsible.email]) {
                    assignmentsGrouped[responsible.email] = {
                        userName: responsible.name,
                        tasks: []
                    };
                }
                assignmentsGrouped[responsible.email].tasks.push({
                    name: task.name,
                    startDate: task.start || 'N/D',
                    endDate: task.end || 'N/D'
                });
            });

            const recipients = responsibles
                .filter(r => r.email)
                .map(r => r.email);

            // Create in-app notifications
            if (setNotifications) {
                const newNotifications = Object.keys(assignmentsGrouped).map(email => {
                    const responsible = responsibles.find(r => r.email === email);
                    if (!responsible) return null;
                    const tasksQty = assignmentsGrouped[email].tasks.length;
                    return {
                        id: Math.random().toString(36).substr(2, 9),
                        userId: responsible.id,
                        title: `Nuevas tareas asignadas: Planificación ${planType}`,
                        description: `Se te han asignado ${tasksQty} nueva(s) tarea(s) en la Planificación ${planType} "${planName}".`,
                        date: new Date().toISOString(),
                        isRead: false,
                        type: 'planning'
                    };
                }).filter(Boolean);
                
                if (newNotifications.length > 0) {
                    setNotifications((prev: any) => [...newNotifications, ...prev]);
                }
            }

            if (recipients.length === 0) return;

            const { error } = await supabase.functions.invoke('notify-planning', {
                body: {
                    projectId: projectInfo.name, 
                    planName,
                    planType,
                    assignmentsGrouped,
                    recipients,
                    authorName: 'Coordinador / Administrador'
                }
            });
            if (error) console.error("Notification Error:", error);
        } catch(e) {
            console.error("Error dispatching planning notifications:", e);
        }
    };

    const handleSaveLookaheadPlan = () => {
        const plan = lookaheadPlans.find(p => p.id === activeLookaheadId);
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia Guardada',
            description: `Se han guardado cambios en la planificación ${plan?.startDate} - ${plan?.endDate}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        if (plan && plan.constraints) {
            dispatchPlanningNotifications(
                `${plan.startDate} al ${plan.endDate}`,
                'Intermedia',
                plan.constraints // Assuming they contain taskId and responsibleId
            );
        }

        // Just closes the editing view, effectively "saving" the state
        setActiveLookaheadId(null);
        setIsITEView(false); // Reset ITE view
    };

    const handleUpdateConstraint = (planId: string, constraintId: string, field: keyof Constraint, value: any) => {
        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === planId) {
                return {
                    ...p,
                    constraints: p.constraints.map(c => c.id === constraintId ? { ...c, [field]: value } : c)
                };
            }
            return p;
        }));
    };

    const handleReleaseConstraintWithObservation = (planId: string, constraintId: string, observations: string, status: 'Pendiente' | 'Liberado' | 'NO LIBERADO' = 'Liberado') => {
        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === planId) {
                return {
                    ...p,
                    constraints: p.constraints.map(c => c.id === constraintId ? { ...c, status, observations } : c)
                };
            }
            return p;
        }));
        setConstraintObsModalOpen(false);
    };

    const handleDeleteConstraint = (planId: string, constraintId: string) => {
        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === planId) {
                return {
                    ...p,
                    constraints: p.constraints.filter(c => c.id !== constraintId)
                };
            }
            return p;
        }));
    };

    // Constraint Master Logic
    const handleAddConstraintGroup = () => {
        if (!newConstraintGroupName.trim()) return;
        const newGroup: ConstraintGroup = {
            id: Math.random().toString(36).substr(2, 9),
            name: newConstraintGroupName,
            definitions: []
        };
        setConstraintGroups([...constraintGroups, newGroup]);
        setNewConstraintGroupName('');
    };

    const handleAddConstraintDefinition = (groupId: string) => {
        if (!newConstraintName.trim()) return;
        const newDef: ConstraintDefinition = {
            id: Math.random().toString(36).substr(2, 9),
            name: newConstraintName
        };
        setConstraintGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return { ...g, definitions: [...g.definitions, newDef] };
            }
            return g;
        }));
        setNewConstraintName('');
        setActiveGroupForNewConstraint(null);
    };

    const handleEditConstraintGroup = (groupId: string, newName: string) => {
        if (!newName.trim()) return;
        setConstraintGroups(prev => prev.map(g => g.id === groupId ? { ...g, name: newName } : g));
        setEditingConstraintGroupId(null);
    };

    const handleDeleteConstraintGroup = (groupId: string) => {
        if (window.confirm("¿Seguro que desea eliminar este grupo y todas sus definiciones?")) {
            setConstraintGroups(prev => prev.filter(g => g.id !== groupId));
        }
    };

    const handleEditConstraintDefinition = (groupId: string, defId: string, newName: string) => {
        if (!newName.trim()) return;
        setConstraintGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return {
                    ...g,
                    definitions: g.definitions.map(d => d.id === defId ? { ...d, name: newName } : d)
                };
            }
            return g;
        }));
        setEditingConstraintDefId(null);
    };

    const handleDeleteConstraintDefinition = (groupId: string, defId: string) => {
        if (window.confirm("¿Seguro que desea eliminar esta definición de restricción?")) {
            setConstraintGroups(prev => prev.map(g => {
                if (g.id === groupId) {
                    return { ...g, definitions: g.definitions.filter(d => d.id !== defId) };
                }
                return g;
            }));
        }
    };

    const isProtocolProgressedLocal = (taskId: string, unitId: string) => {
        return isProtocolProgressed(taskId, unitId, savedProtocols, protocolConfigs);
    };

    const calculateTaskProgressLocal = (task: Task): number => {
        if (task.children && task.children.length > 0) {
            const hasWeeklySubtasks = task.children.some(c => c.isWeeklySubtask);
            if (hasWeeklySubtasks) {
                const subtaskProgresses = task.children.map(child => calculateTaskProgressLocal(child));
                return Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
            } else {
                // Normal children (Gantt subtasks) -> Duration-weighted average progress
                let totalWeightedProgress = 0;
                let totalDuration = 0;
                task.children.forEach(child => {
                    const childProgress = calculateTaskProgressLocal(child);
                    const childDuration = child.duration || 0;
                    totalWeightedProgress += childProgress * childDuration;
                    totalDuration += childDuration;
                });
                if (totalDuration > 0) {
                    return totalWeightedProgress / totalDuration;
                } else {
                    const sum = task.children.reduce((acc, child) => acc + calculateTaskProgressLocal(child), 0);
                    return sum / task.children.length;
                }
            }
        }
        const physicalProgress = calculateTaskProgress(task.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
        return Math.min(100, (task.baseProgress !== undefined ? task.baseProgress : (task.progress || 0)) + physicalProgress);
    };

    const calculateTotalProjectProgress = () => {
        return calculateProjectProgress(tasks, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans);
    };

    const handleSyncProgressToDate = (date: string, delayCS: number = 0, delayRC: number = 0) => {
        // 1. Calculate global progress (High precision)
        const globalProgress = calculateTotalProjectProgress();

        // 2. Add to progressHistory
        const newRecord: ProjectProgressSnapshot = {
            date,
            progress: globalProgress,
            delayCurvaS: delayCS,
            delayRutaCritica: delayRC
        };

        // Check if date already exists and replace it, or add new
        setProgressHistory(prev => {
            const filtered = prev.filter(r => r.date !== date);
            return [...filtered, newRecord].sort((a, b) => a.date.localeCompare(b.date));
        });

        // 3. Update tasks in Master Plan with current calculated progress
        const syncRecursive = (list: Task[]): Task[] => {
            return list.map(t => {
                const updatedTask = { ...t };
                if (t.children && t.children.length > 0) {
                    updatedTask.children = syncRecursive(t.children);
                    updatedTask.progress = calculateTaskProgressLocal(updatedTask);
                } else if (t.type === 'task') {
                    if (updatedTask.baseProgress === undefined) {
                        updatedTask.baseProgress = t.progress || 0;
                    }
                    updatedTask.progress = calculateTaskProgressLocal(updatedTask);
                }
                return updatedTask;
            });
        };
        const updatedTasks = syncRecursive(tasks);
        setTasks(updatedTasks);
        if (forceSyncTasks) forceSyncTasks(updatedTasks);

        addActivity({
            type: 'planning',
            title: 'Avance Semanal Sincronizado',
            description: `Se ha sincronizado el avance del proyecto (${globalProgress}%) para la fecha ${date}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        alert(`Sincronización completada para el ${date}: ${globalProgress}% de avance global.`);
    };

    const handleCreateFolderClick = (parentId: string | null = null) => {
        setTargetParentId(parentId);
        setEditingItemId(null);
        setInputName("");
        setModalMode('folder');
        setModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleCreateSubtaskClick = (parentId: string) => {
        setTargetParentId(parentId);
        setEditingItemId(null);
        setInputName("");
        setModalMode('subtask'); // Used for both breakdown subtasks and Master tasks inside folders
        setModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleEditNameClick = (task: Task) => {
        setTargetParentId(null);
        setEditingItemId(task.id);
        setInputName(task.name);
        setModalMode(task.type === 'folder' ? 'folder' : 'subtask');
        setModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleDeleteItem = (taskId: string) => {
        if (!confirm("¿Estás seguro de eliminar este elemento?")) return;
        const deleteRecursive = (list: Task[]): Task[] => {
            return list.filter(t => t.id !== taskId).map(t => ({
                ...t,
                children: t.children ? deleteRecursive(t.children) : undefined
            }));
        };
        setTasks(deleteRecursive(tasks));
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleMoveItemClick = (itemId: string) => {
        setMovingItemId(itemId);
        setMoveModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    // Recursive helper to check if a folder is a descendant of another item
    const isDescendant = (parentList: Task[], childId: string): boolean => {
        const findChild = (list: Task[]): boolean => {
            return list.some(t => {
                if (t.id === childId) return true;
                if (t.children) return findChild(t.children);
                return false;
            });
        };
        return findChild(parentList);
    };

    // Recursive helper to build a list of all folders (excluding moving item and its descendants)
    const getValidTargetFolders = (list: Task[], movingId: string): { id: string; name: string; level: number }[] => {
        let result: { id: string; name: string; level: number }[] = [];
        
        const traverse = (items: Task[], currentLevel: number) => {
            items.forEach(item => {
                if (item.type === 'folder' && item.id !== movingId) {
                    const movingItem = findTaskById(movingId, tasks);
                    const isDesc = movingItem?.children ? isDescendant(movingItem.children, item.id) : false;
                    
                    if (!isDesc) {
                        result.push({ id: item.id, name: item.name, level: currentLevel });
                        if (item.children) {
                            traverse(item.children, currentLevel + 1);
                        }
                    }
                }
            });
        };
        
        traverse(list, 0);
        return result;
    };

    const handleMoveItem = (targetParentId: string | null) => {
        if (!movingItemId) return;

        let itemToMove: Task | null = null;

        // 1. Remove the item from the tree and capture it
        const removeRecursive = (list: Task[]): Task[] => {
            return list.filter(t => {
                if (t.id === movingItemId) {
                    itemToMove = t;
                    return false;
                }
                return true;
            }).map(t => {
                if (t.children) {
                    return { ...t, children: removeRecursive(t.children) };
                }
                return t;
            });
        };

        const treeWithoutItem = removeRecursive(tasks);

        if (!itemToMove) {
            alert("No se pudo encontrar el elemento a mover.");
            setMoveModalOpen(false);
            return;
        }

        let updatedTree: Task[];

        // 2. Insert it at the target destination
        if (targetParentId === null) {
            // Move to root level
            updatedTree = [...treeWithoutItem, itemToMove];
        } else {
            // Move inside target folder
            const insertRecursive = (list: Task[]): Task[] => {
                return list.map(t => {
                    if (t.id === targetParentId) {
                        return {
                            ...t,
                            children: [...(t.children || []), itemToMove!]
                        };
                    }
                    if (t.children) {
                        return { ...t, children: insertRecursive(t.children) };
                    }
                    return t;
                });
            };
            updatedTree = insertRecursive(treeWithoutItem);
        }

        // 3. Update tasks state
        const processedTree = calculateCriticalPath(updatedTree, workingDays, holidays);
        setTasks(processedTree);
        if (forceSyncTasks) forceSyncTasks(processedTree);

        addActivity({
            type: 'planning',
            title: 'Elemento movido',
            description: `Se ha movido el elemento "${(itemToMove as Task).name}" a una nueva ubicación.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setMoveModalOpen(false);
        setMovingItemId(null);
    };

    const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fileName = file.name.toLowerCase();
        if (fileName.endsWith('.mpp')) {
            alert("Los archivos nativos de Microsoft Project (.mpp) son formatos binarios protegidos. Para una importación exitosa en la web, por favor exporte su proyecto desde MS Project como 'XML de Microsoft Project' (.xml) y suba ese archivo.");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            const xmlText = event.target?.result as string;
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, "text/xml");

            const projectTasks = xmlDoc.getElementsByTagName("Task");
            const tempTasks: any[] = [];
            const taskMap: Record<string, Task> = {};

            // First pass: Create task objects
            for (let i = 0; i < projectTasks.length; i++) {
                const xmlTask = projectTasks[i];

                // Helper to get element by tag name (prioritizing direct children)
                const getEl = (tag: string) => {
                    // 1. Try direct children first (avoids baseline confusion)
                    const directMatch = Array.from(xmlTask.children).find(c =>
                        c.localName?.toLowerCase() === tag.toLowerCase()
                    );
                    if (directMatch) return directMatch;

                    // 2. Fallback to any descendant
                    const found = xmlTask.getElementsByTagName(tag);
                    if (found.length > 0) return found[0];
                    return null;
                };

                const uid = getEl("UID")?.textContent || "";
                const idOriginalText = getEl("ID")?.textContent || "";
                const idOriginal = parseInt(idOriginalText) || (i + 1); // physical row index (1-based)
                const name = getEl("Name")?.textContent || "";
                const type = getEl("Summary")?.textContent === "1" ? "folder" : "task";
                let start = (getEl("Start")?.textContent || getEl("StartDate")?.textContent || "")?.split("T")[0] || "";
                let end = (getEl("Finish")?.textContent || getEl("FinishDate")?.textContent || "")?.split("T")[0] || "";
                const isCritical = getEl("Critical")?.textContent === "1";

                // Parse Duration (handles PT40H0M0S or raw seconds)
                const durationText = getEl("Duration")?.textContent || "0";
                let duration = 0;
                if (durationText.startsWith('PT')) {
                    const h = parseInt(durationText.match(/(\d+)H/)?.[1] || "0");
                    const m = parseInt(durationText.match(/(\d+)M/)?.[1] || "0");
                    const s = parseInt(durationText.match(/(\d+)S/)?.[1] || "0");
                    duration = Math.ceil((h * 3600 + m * 60 + s) / (8 * 3600));
                } else {
                    duration = Math.ceil(parseInt(durationText) / (8 * 3600));
                }

                const outlineLevel = parseInt(getEl("OutlineLevel")?.textContent || "1");

                // Fallback: If end is missing but start and duration exist, calculate it
                if (!end && start && duration > 0) {
                    try {
                        const sDate = new Date(start + "T12:00:00"); // Use noon to avoid TZ shifts
                        if (!isNaN(sDate.getTime())) {
                            const eDate = new Date(sDate);
                            eDate.setDate(sDate.getDate() + (duration - 1));
                            end = eDate.toISOString().split('T')[0];
                        }
                    } catch (e) {
                        console.error("Error calculating fallback end date", e);
                    }
                }

                // Predecessors (raw extraction from XML, using UID references first)
                const rawPredecessors: string[] = [];
                const links = xmlTask.getElementsByTagName("PredecessorLink");
                for (let j = 0; j < links.length; j++) {
                    const link = links[j];
                    const predUid = Array.from(link.children).find(c => c.localName === "PredecessorUID")?.textContent || link.getElementsByTagName("PredecessorUID")[0]?.textContent;
                    const linkType = Array.from(link.children).find(c => c.localName === "Type")?.textContent || link.getElementsByTagName("Type")[0]?.textContent || "1";

                    if (predUid) {
                        // Project XML Types: 0:FF, 1:FS, 2:SS, 3:SF
                        let suffix = "FS";
                        if (linkType === "0") suffix = "FF";
                        else if (linkType === "2") suffix = "SS";
                        else if (linkType === "3") suffix = "SF";

                        rawPredecessors.push(`${predUid}${suffix}`);
                    }
                }

                if (!name || uid === "0") continue; // Skip project root or empty tasks

                const percentCompleteText = getEl("PercentComplete")?.textContent || "0";
                const parsedProgress = parseFloat(percentCompleteText) || 0;

                const task: Task = {
                    id: uid,
                    name,
                    type,
                    start: start || undefined,
                    end: end || undefined,
                    duration: duration > 0 ? duration : undefined,
                    children: [],
                    expanded: true,
                    progress: parsedProgress,
                    predecessors: rawPredecessors.length > 0 ? rawPredecessors : undefined,
                    id_original_project: idOriginal,
                    isCritical: isCritical
                };

                taskMap[uid] = task;
                tempTasks.push({ task, outlineLevel, uid });
            }

            // Map XML UID predecessor references to id_original_project values and build successors
            const uidToOriginalIdMap: Record<string, number> = {};
            tempTasks.forEach(({ task, uid }) => {
                if (uid && task.id_original_project !== undefined) {
                    uidToOriginalIdMap[uid] = task.id_original_project;
                }
            });

            const rawPredecessorsMap: Record<string, string[]> = {};
            tempTasks.forEach(({ task }) => {
                if (task.predecessors) {
                    rawPredecessorsMap[task.id] = [...task.predecessors];
                    task.predecessors = []; // Clear to rebuild with original IDs
                }
                task.successors = []; // Initialize successors array
            });

            tempTasks.forEach(({ task }) => {
                const rawPreds = rawPredecessorsMap[task.id];
                if (rawPreds) {
                    const translatedPreds: string[] = [];
                    rawPreds.forEach(predStr => {
                        const match = predStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                        if (match) {
                            const predUid = match[1];
                            let type = (match[2] || "FS").toUpperCase();
                            if (type === "FC") type = "FS";
                            if (type === "CC") type = "SS";
                            if (type === "CF") type = "SF";
                            const predOriginalId = uidToOriginalIdMap[predUid];
                            if (predOriginalId !== undefined) {
                                translatedPreds.push(`${predOriginalId}${type}`);
                                
                                // Find predecessor task to add the inverse successor relation
                                const predTaskObj = tempTasks.find(t => t.uid === predUid);
                                if (predTaskObj) {
                                    predTaskObj.task.successors = predTaskObj.task.successors || [];
                                    const successorLink = `${task.id_original_project}${type}`;
                                    if (!predTaskObj.task.successors.includes(successorLink)) {
                                        predTaskObj.task.successors.push(successorLink);
                                    }
                                }
                            }
                        }
                    });
                    if (translatedPreds.length > 0) {
                        task.predecessors = translatedPreds;
                    } else {
                        delete task.predecessors;
                    }
                }
            });

            // Second pass: Reconstruct hierarchy
            const rootTasks: Task[] = [];
            const stack: { level: number; task: Task }[] = [];

            tempTasks.forEach(({ task, outlineLevel }) => {
                while (stack.length > 0 && stack[stack.length - 1].level >= outlineLevel) {
                    stack.pop();
                }

                if (stack.length === 0) {
                    rootTasks.push(task);
                } else {
                    const parent = stack[stack.length - 1].task;
                    parent.children = parent.children || [];
                    parent.children.push(task);
                    parent.type = 'folder'; // Ensure parent is a folder if it has children
                }

                stack.push({ level: outlineLevel, task });
            });

            // Clean up empty fields to save Firestore space
            const optimizeTasks = (list: Task[]): Task[] => {
                return list.map(t => {
                    const cleaned: any = { ...t };
                    if (cleaned.children && cleaned.children.length === 0) delete cleaned.children;
                    if (cleaned.predecessors && cleaned.predecessors.length === 0) delete cleaned.predecessors;
                    if (cleaned.successors && cleaned.successors.length === 0) delete cleaned.successors;
                    if (cleaned.children) cleaned.children = optimizeTasks(cleaned.children);
                    return cleaned as Task;
                });
            };

            const optimizedRootTasks = optimizeTasks(rootTasks);

            let finalStoredTasks = optimizedRootTasks;

            // --- SMART MERGE LOGIC (For Reprogrammings) ---
            const isReprogramming = !!(window as any).__REPROGRAMMING_COMMENT;
            if (isReprogramming) {
                const comment = (window as any).__REPROGRAMMING_COMMENT;
                delete (window as any).__REPROGRAMMING_COMMENT;

                // 1. Create a Snapshot of the CURRENT state before merging
                const currentStart = tasks.length > 0 ? tasks[0].start : "";
                const currentEnd = tasks.length > 0 ? tasks[0].end : "";

                const newBaseline: GanttBaseline = {
                    id: Date.now().toString(),
                    versionNumber: baselines.length + 1,
                    uploadDate: new Date().toISOString(),
                    tasksSnapshot: JSON.parse(JSON.stringify(tasks)), // Corrected field name
                    comment: comment,
                    startDate: currentStart || "N/A",
                    endDate: currentEnd || "N/A"
                };

                // Add to history
                setBaselines([...baselines, newBaseline]);

                // 2. Map existing tasks by UID for quick lookup
                const existingTaskMap: Record<string, Task> = {};
                const flattenIntoMap = (list: Task[]) => {
                    list.forEach(t => {
                        existingTaskMap[t.id] = t;
                        if (t.children) flattenIntoMap(t.children);
                    });
                };
                flattenIntoMap(tasks);

                // 3. Smart Merge Recursive Function
                const mergeRecursive = (newList: Task[]): Task[] => {
                    return newList.map(newTask => {
                        const existing = existingTaskMap[newTask.id];
                        if (existing) {
                            // Task exists: Preserve data, update schedule
                            return {
                                ...existing,
                                start: newTask.start,
                                end: newTask.end,
                                duration: newTask.duration,
                                predecessors: newTask.predecessors,
                                // Preserve children structure from NEW file
                                children: newTask.children ? mergeRecursive(newTask.children) : undefined
                            };
                        }
                        // New task: Keep as is
                        return newTask;
                    });
                };

                finalStoredTasks = mergeRecursive(optimizedRootTasks);
                alert("Reprogramación procesada con éxito. Se ha guardado una copia en el historial.");
            } else {
                if (!confirm("Esta acción reemplazará la Carta Gantt actual por completo. ¿Desea continuar?")) return;
            }

            if (finalStoredTasks.length > 0) {
                console.log(`[Import] Setting ${finalStoredTasks.length} optimized root tasks to state (with Critical Path).`);
                
                // Adjust tasks to working calendar
                const adjustedTasks = finalStoredTasks.map(t => adjustTaskToCalendar(t, workingDays, holidays));
                // Resolve dependencies
                const syncedTasks = syncDependencies(adjustedTasks, undefined, workingDays, holidays);
                
                const processed = calculateCriticalPath(syncedTasks, workingDays, holidays);
                setTasks(processed);
                addActivity({
                    type: 'planning',
                    title: isReprogramming ? 'Reprogramación Cargada' : 'Carta Gantt Importada',
                    description: isReprogramming
                        ? `Se ha procesado una reprogramación: ${(window as any).__REPROGRAMMING_COMMENT}`
                        : 'Se ha importado una nueva Carta Gantt desde archivo XML',
                    user: currentUser?.name || currentUser?.email || 'Usuario'
                });
                if (isReprogramming) delete (window as any).__REPROGRAMMING_COMMENT;

                if (forceSyncTasks) {
                    forceSyncTasks(processed).catch(err => {
                        console.error("Error forced syncing imported tasks:", err);
                    });
                }
                setImportModalOpen(false);
            } else {
                console.warn("[Import] No valid tasks found in file.");
                alert("No se encontraron tareas válidas en el archivo.");
            }
        };
        reader.readAsText(file);
    };

    const invertRelationType = (type: string): string => {
        if (type === "FS") return "SF";
        if (type === "SF") return "FS";
        return type; // SS remains SS, FF remains FF
    };

    const handleUpdatePredecessors = (taskId: string, newPreds: string[]) => {
        setTasks(prev => {
            const flat = flattenTasks(prev);
            const target = flat.find(t => t.id === taskId);
            if (!target) return prev;

            const targetOrigId = target.id_original_project;
            if (targetOrigId === undefined) return prev;

            // Normalize relationship inputs (e.g., 7fc -> 7FS, 8cc -> 8SS)
            const normalizedPreds = newPreds.map(predStr => {
                const match = predStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                if (match) {
                    const id = match[1];
                    let type = (match[2] || "FS").toUpperCase();
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    return `${id}${type}`;
                }
                return predStr;
            });

            const oldPreds = target.predecessors || [];

            // 1. Remove old mirror relations
            oldPreds.forEach(oldPredStr => {
                if (normalizedPreds.includes(oldPredStr)) return; // Still present
                const match = oldPredStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)$/i);
                if (match) {
                    const predOrigId = parseInt(match[1]);
                    let type = match[2].toUpperCase();
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    const predTask = flat.find(t => t.id_original_project === predOrigId);
                    if (predTask && predTask.successors) {
                        const mirrorLink = `${targetOrigId}${invertRelationType(type)}`;
                        predTask.successors = predTask.successors.filter(s => s !== mirrorLink);
                    }
                }
            });

            // 2. Add new mirror relations
            normalizedPreds.forEach(newPredStr => {
                if (oldPreds.includes(newPredStr)) return; // Already present
                const match = newPredStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)$/i);
                if (match) {
                    const predOrigId = parseInt(match[1]);
                    let type = match[2].toUpperCase();
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    const predTask = flat.find(t => t.id_original_project === predOrigId);
                    if (predTask) {
                        predTask.successors = predTask.successors || [];
                        const mirrorLink = `${targetOrigId}${invertRelationType(type)}`;
                        if (!predTask.successors.includes(mirrorLink)) {
                            predTask.successors.push(mirrorLink);
                        }
                    }
                }
            });

            // Update target predecessors
            target.predecessors = normalizedPreds.length > 0 ? normalizedPreds : undefined;

            // Clean up empty arrays across all tasks
            flat.forEach(t => {
                if (t.successors && t.successors.length === 0) delete t.successors;
                if (t.predecessors && t.predecessors.length === 0) delete t.predecessors;
            });

            return syncDependencies(prev, taskId);
        });
    };

    const handleUpdateSuccessors = (taskId: string, newSuccs: string[]) => {
        setTasks(prev => {
            const flat = flattenTasks(prev);
            const target = flat.find(t => t.id === taskId);
            if (!target) return prev;

            const targetOrigId = target.id_original_project;
            if (targetOrigId === undefined) return prev;

            // Normalize relationship inputs (e.g., 7fc -> 7FS, 8cc -> 8SS)
            const normalizedSuccs = newSuccs.map(succStr => {
                const match = succStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)?$/i);
                if (match) {
                    const id = match[1];
                    let type = (match[2] || "FS").toUpperCase();
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    return `${id}${type}`;
                }
                return succStr;
            });

            const oldSuccs = target.successors || [];

            // 1. Remove old mirror relations
            oldSuccs.forEach(oldSuccStr => {
                if (normalizedSuccs.includes(oldSuccStr)) return; // Still present
                const match = oldSuccStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)$/i);
                if (match) {
                    const succOrigId = parseInt(match[1]);
                    let type = match[2].toUpperCase();
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    const succTask = flat.find(t => t.id_original_project === succOrigId);
                    if (succTask && succTask.predecessors) {
                        const mirrorLink = `${targetOrigId}${invertRelationType(type)}`;
                        succTask.predecessors = succTask.predecessors.filter(p => p !== mirrorLink);
                    }
                }
            });

            // 2. Add new mirror relations
            normalizedSuccs.forEach(newSuccStr => {
                if (oldSuccs.includes(newSuccStr)) return; // Already present
                const match = newSuccStr.match(/^(\d+)(FS|FC|SS|CC|FF|SF|CF)$/i);
                if (match) {
                    const succOrigId = parseInt(match[1]);
                    let type = match[2].toUpperCase();
                    if (type === "FC") type = "FS";
                    if (type === "CC") type = "SS";
                    if (type === "CF") type = "SF";
                    const succTask = flat.find(t => t.id_original_project === succOrigId);
                    if (succTask) {
                        succTask.predecessors = succTask.predecessors || [];
                        const mirrorLink = `${targetOrigId}${invertRelationType(type)}`;
                        if (!succTask.predecessors.includes(mirrorLink)) {
                            succTask.predecessors.push(mirrorLink);
                        }
                    }
                }
            });

            // Update target successors
            target.successors = normalizedSuccs.length > 0 ? normalizedSuccs : undefined;

            // Clean up empty arrays across all tasks
            flat.forEach(t => {
                if (t.successors && t.successors.length === 0) delete t.successors;
                if (t.predecessors && t.predecessors.length === 0) delete t.predecessors;
            });

            return syncDependencies(prev, taskId);
        });
    };

    const confirmInputModal = () => {
        if (!inputName.trim()) return;
        if (editingItemId) {
            const updateNameRecursive = (list: Task[]): Task[] => {
                return list.map(t => {
                    if (t.id === editingItemId) return { ...t, name: inputName };
                    if (t.children) return { ...t, children: updateNameRecursive(t.children) }
                    return t;
                });
            };
            setTasks(updateNameRecursive(tasks));
        } else {
            const nextOriginalId = flattenTasks(tasks).reduce((max, t) => Math.max(max, t.id_original_project || 0), 0) + 1;
            const newItem: Task = {
                id: Math.random().toString(36).substr(2, 9),
                name: inputName,
                // If creating a child item (modalMode subtask), it's always type 'task'. If modalMode folder, it's 'folder'.
                type: modalMode === 'folder' ? 'folder' : 'task',
                children: [],
                expanded: true,
                progress: 0,
                start: '2026-01-01', // Default dates for master plan
                end: '2026-01-15',
                duration: 14,
                id_original_project: nextOriginalId,
                // Only identify as weekly subtask if we are in breakdown mode
                isWeeklySubtask: isBreakdownMode
            };
            if (targetParentId === null) {
                setTasks([...tasks, newItem]);
            } else {
                const addRecursive = (list: Task[]): Task[] => {
                    return list.map(t => {
                        if (t.id === targetParentId) {
                            return { ...t, children: [...(t.children || []), newItem] };
                        }
                        if (t.children) return { ...t, children: addRecursive(t.children) };
                        return t;
                    });
                };
                setTasks(addRecursive(tasks));
            }
        }
        setModalOpen(false);
    };

    const handleContextMenu = (e: React.MouseEvent, task: Task) => {
        if (activeTab === 'maestra' && !isMasterEditing) return;
        if (activeTab === 'semanal' && !isWeeklyEditing && !isBreakdownMode) return;
        if (activeTab === 'intermedia') return;

        e.preventDefault();
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            taskId: task.id
        });
    };

    const handleToggleCritical = (taskId: string) => {
        const toggleRecursive = (list: Task[]): Task[] => {
            return list.map(t => {
                if (t.id === taskId) {
                    const newCritical = !t.isCritical;
                    // Also toggle all children
                    const toggleChildren = (children: Task[]): Task[] => {
                        return children.map(c => ({
                            ...c,
                            isCritical: newCritical,
                            children: c.children ? toggleChildren(c.children) : undefined
                        }));
                    };
                    return {
                        ...t,
                        isCritical: newCritical,
                        children: t.children ? toggleChildren(t.children) : undefined
                    };
                }
                if (t.children) return { ...t, children: toggleRecursive(t.children) };
                return t;
            });
        };
        setTasks(toggleRecursive(tasks));
        setContextMenu({ ...contextMenu, visible: false });
    };

    useEffect(() => {
        const handleClick = () => {
            setContextMenu({ ...contextMenu, visible: false });
            setResponsiblesMenuOpen(false);
            setCncMenuOpen(false);
            setConstraintsMenuOpen(false);
        };
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, [contextMenu]);

    // --- Exportación a Excel ---
    const exportLookaheadToExcel = (lookaheadId: string) => {
        const lookahead = lookaheadPlans.find(p => p.id === lookaheadId);
        if (!lookahead) return;

        const tasksInWindow = getTasksForLookahead(lookahead.startDate, lookahead.endDate);
        const dataToExport: any[] = [];

        tasksInWindow.forEach(task => {
            const constraints = lookahead.constraints.filter(c => c.taskId === task.id);
            if (constraints.length > 0) {
                constraints.forEach(c => {
                    let typeName = 'General';
                    constraintGroups.forEach(g => {
                        const def = g.definitions.find(d => d.id === c.typeId);
                        if (def) typeName = def.name;
                    });
                    const respName = responsibles.find(r => r.id === c.responsibleId)?.name || 'Sin asignar';
                    dataToExport.push({
                        "Nombre Tarea": task.name,
                        "Fecha Inicio": task.start,
                        "Fecha Termino": task.end,
                        "Restricción": c.description,
                        "Tipo de Restricción": typeName,
                        "Responsable": respName,
                        "Fecha Requerida": c.requiredDate,
                        "Estado": c.status,
                        "Observaciones": c.observations || "-"
                    });
                });
            }
        });

        if (dataToExport.length === 0) {
            alert("No hay restricciones registradas para exportar en este plan.");
            return;
        }

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Restricciones");
        XLSX.writeFile(wb, `Planificacion_Intermedia_${lookahead.startDate}.xlsx`);
    };

    const exportWeeklyPlanToExcel = (planId: string | number) => {
        const plan = weeklyPlans.find(p => String(p.id) === String(planId));
        if (!plan) return;

        const dataToExport: any[] = [];
        
        plan.allocations.forEach(alloc => {
            const unit = units.find(u => u.id === alloc.unitId);
            const unitName = unit?.name || 'N/A';
            const levelName = levels.find(l => l.id === unit?.levelId)?.name || '-';
            const taskName = findTaskName(alloc.taskId, tasks);
            const responsibleName = responsibles.find(r => r.id === alloc.responsibleId)?.name || '-';
            
            const config = protocolConfigs[alloc.taskId];
            const appliesProtocol = config?.applies ?? false;
            const isExecuted = appliesProtocol ? isProtocolProgressedLocal(alloc.taskId, alloc.unitId) : alloc.executed;

            const pctComprometido = alloc.percentComprometido ?? (alloc.executed ? 100 : 100);
            const pctCumplimiento = alloc.percentCumplimiento ?? (isExecuted ? 100 : 0);
            
            const cncName = alloc.cncId ? (cncGroups.flatMap(g => g.causes).find(c => c.id === alloc.cncId)?.name || alloc.cncId) : '-';

            dataToExport.push({
                "Nivel": levelName,
                "Unidades Físicas": unitName,
                "Tarea": taskName || "Desconocida",
                "Responsable": responsibleName,
                "L": alloc.days['L'] ? 'X' : '',
                "M": alloc.days['M'] ? 'X' : '',
                "X": alloc.days['X'] ? 'X' : '',
                "J": alloc.days['J'] ? 'X' : '',
                "V": alloc.days['V'] ? 'X' : '',
                "S": alloc.days['S'] ? 'X' : '',
                "% Comprometido": pctComprometido,
                "% Cumplimiento": pctCumplimiento,
                "C.N.C": (plan.status === 'En Revisión' || plan.status === 'Cerrada') ? cncName : undefined
            });
        });

        if (dataToExport.length === 0) {
            alert("No hay tareas programadas en este plan semanal.");
            return;
        }

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Tareas Semanales");
        XLSX.writeFile(wb, `Planificacion_Semanal_${plan.id}.xlsx`);
    };

    // --- Specific Mode Logic ---

    const handleCreateWeeklyPlan = () => {
        if (!weeklyPlanDates.start || !weeklyPlanDates.end) {
            alert("Ingrese fechas validas");
            return;
        }

        if (editingWeeklyPlanId) {
            setWeeklyPlans(prev => prev.map(p => {
                if (p.id === editingWeeklyPlanId) {
                    return {
                        ...p,
                        startDate: weeklyPlanDates.start,
                        endDate: weeklyPlanDates.end,
                    };
                }
                return p;
            }));
            addActivity({
                type: 'planning',
                title: 'Planificación Semanal Editada',
                description: `Se han modificado las fechas de la planificación semanal #${editingWeeklyPlanId} del ${weeklyPlanDates.start} al ${weeklyPlanDates.end}`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
            setNewPlanModalOpen(false);
            setEditingWeeklyPlanId(null);
            return;
        }

        const newId = weeklyPlans.length > 0 ? Math.max(...weeklyPlans.map(p => p.id)) + 1 : 1;
        const newPlan: WeeklyPlan = {
            id: newId,
            startDate: weeklyPlanDates.start,
            endDate: weeklyPlanDates.end,
            status: 'En Planificación',
            allocations: []
        };

        setWeeklyPlansBackup(JSON.parse(JSON.stringify(weeklyPlans)));
        setWeeklyPlans([...weeklyPlans, newPlan]);
        setActivePlanId(newId);

        addActivity({
            type: 'planning',
            title: 'Nueva Planificación Semanal',
            description: `Se ha creado la planificación semanal #${newId} del ${newPlan.startDate} al ${newPlan.endDate}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setNewPlanModalOpen(false);
        setIsWeeklyEditing(true);
        setIsBreakdownMode(false);
    };

    const handleSaveWeeklyPlanFromToolbar = () => {
        const plan = weeklyPlans.find(p => p.id === activePlanId);
        addActivity({
            type: 'planning',
            title: 'Planificación Semanal Guardada',
            description: `Se han guardado cambios en la planificación semanal #${activePlanId} del ${plan?.startDate} al ${plan?.endDate}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        if (plan && plan.allocations && plan.allocations.length > 0) {
            dispatchPlanningNotifications(
                `${plan.startDate} al ${plan.endDate}`,
                'Semanal',
                plan.allocations // They contain taskId and responsibleId
            );
        }

        setIsWeeklyEditing(false);
        setActivePlanId(null); // Return to History View
    };

    const handleExitWithoutSaving = () => {
        if (exitWarningTarget === 'semanal') {
            if (weeklyPlansBackup) {
                setWeeklyPlans(weeklyPlansBackup);
            }
            setIsWeeklyEditing(false);
            setActivePlanId(null);
        } else if (exitWarningTarget === 'intermedia') {
            if (lookaheadPlansBackup) {
                setLookaheadPlans(lookaheadPlansBackup);
            }
            setActiveLookaheadId(null);
            setIsITEView(false);
        }
        setShowExitWarningModal(false);
        setExitWarningTarget(null);
    };

    const handleExitWithSaving = () => {
        if (exitWarningTarget === 'semanal') {
            handleSaveWeeklyPlanFromToolbar();
        } else if (exitWarningTarget === 'intermedia') {
            handleSaveLookaheadPlan();
        }
        setShowExitWarningModal(false);
        setExitWarningTarget(null);
    };

    const handlePublishPlan = (planId: number) => {
        setWeeklyPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Ejecución' } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Semanal Publicada',
            description: `La planificación semanal #${planId} ha sido publicada y ahora está En Ejecución.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
        setIsWeeklyEditing(false);
    };

    const handleRevertToDraft = (planId: number) => {
        setWeeklyPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Planificación' } : p));
        setIsWeeklyEditing(true);
        addActivity({
            type: 'planning',
            title: 'Planificación Semanal en Edición',
            description: `La planificación semanal #${planId} ha sido devuelta al estado En Planificación.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    const handlePublishLookahead = (planId: string) => {
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Ejecución' } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia Publicada',
            description: `La planificación intermedia ha sido publicada y ahora está En Ejecución.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
        setActiveLookaheadId(null);
        setIsITEView(false);
    };

    const handleRevertLookaheadToDraft = (planId: string) => {
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Planificación' } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia en Edición',
            description: `La planificación intermedia ha sido devuelta al estado En Planificación.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    const handleStartLookaheadReview = (planId: string) => {
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Revisión' } : p));
        addActivity({
            type: 'planning',
            title: 'Revisión de Planificación Intermedia',
            description: `La planificación intermedia ha sido enviada a revisión.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    const handleCloseLookahead = (planId: string) => {
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'Cerrada' } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia Cerrada',
            description: `La planificación intermedia ha sido cerrada.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
        setActiveLookaheadId(null);
    };

    const handleBackToLookaheadExecution = (planId: string) => {
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Ejecución' } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia Re-abierta',
            description: `Se ha devuelto la planificación intermedia al estado En Ejecución.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    const handleReopenLookahead = (planId: string) => {
        if (window.confirm("¿Seguro que desea volver a abrir esta planificación intermedia?")) {
            setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Revisión' } : p));
            addActivity({
                type: 'planning',
                title: 'Planificación Intermedia Reabierta',
                description: `Se ha reabierto la planificación intermedia para revisión`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
        }
    };

    const isLookaheadFullyResolved = (plan: LookaheadPlan | null): boolean => {
        if (!plan) return false;
        const tasksInWindow = getTasksForLookahead(plan.startDate, plan.endDate);
        for (const task of tasksInWindow) {
            const isCleared = plan.clearedTaskIds?.includes(task.id);
            if (isCleared) continue; // Resolved: No Aplica Restricción

            const taskConstraints = plan.constraints.filter(c => c.taskId === task.id);
            for (const c of taskConstraints) {
                const isReleased = c.status === 'Liberado';
                const hasComment = c.observations && c.observations.trim().length > 0;
                if (!isReleased && !hasComment) {
                    return false; // Not resolved!
                }
            }
        }
        return true;
    };

    const handleDeleteLookahead = (planId: string) => {
        if (!hasPermission('pi_eliminar', 'edit')) return;
        const plan = lookaheadPlans.find(p => p.id === planId);
        if (!plan) return;
        
        if (window.confirm(`¿Estás completamente seguro de eliminar la Planificación Intermedia del ${plan.startDate} al ${plan.endDate}?\n\nEsta acción es irreversible y eliminará todas sus restricciones y datos asociados.`)) {
            setLookaheadPlans(prev => prev.filter(p => p.id !== planId));
            addActivity({
                type: 'planning',
                title: 'Planificación Intermedia Eliminada',
                description: `Se ha eliminado la planificación intermedia del ${plan.startDate} al ${plan.endDate}`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
        }
    };

    const handleDeleteWeeklyPlan = (planId: number) => {
        if (!hasPermission('ps_eliminar', 'edit')) return;
        const plan = weeklyPlans.find(p => p.id === planId);
        if (!plan) return;
        
        if (window.confirm(`¿Estás completamente seguro de eliminar la Planificación Semanal #${planId} (${plan.startDate} al ${plan.endDate})?\n\nEsta acción es irreversible y eliminará todo el desglose de tareas, asignaciones e historial de avance.`)) {
            setWeeklyPlans(prev => prev.filter(p => p.id !== planId));
            if (activePlanId === planId) {
                setActivePlanId(null);
            }
            addActivity({
                type: 'planning',
                title: 'Planificación Semanal Eliminada',
                description: `Se ha eliminado la planificación semanal #${planId} del ${plan.startDate} al ${plan.endDate}`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
        }
    };

    // Updated to handle "Review" state instead of closing immediately
    const handleStartReview = (planId: number) => {
        const plan = weeklyPlans.find(p => p.id === planId);
        if (plan) {
            const hasNoWithoutComment = plan.allocations.some(a => {
                const status = getStatusValue(a);
                return status === 'no' && !a.comentario?.trim();
            });
            if (hasNoWithoutComment) {
                alert("No se puede iniciar la revisión: Existen tareas declaradas como 'No' que no tienen una justificación de incumplimiento. Por favor complete todas las justificaciones.");
                return;
            }
            setDraftWeeklyPlan(JSON.parse(JSON.stringify({ ...plan, status: 'En Revisión' })));
        }
        setWeeklyPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Revisión' } : p));
        addActivity({
            type: 'planning',
            title: 'Revisión de Planificación Iniciada',
            description: `La planificación semanal #${planId} (${plan?.startDate} al ${plan?.endDate}) ha sido enviada a revisión`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
        setIsWeeklyEditing(false); // Stop standard editing
    };

    const handleSaveReview = (planId: number) => {
        if (draftWeeklyPlan && draftWeeklyPlan.id === planId) {
            setWeeklyPlans(prev => prev.map(p => p.id === planId ? draftWeeklyPlan : p));
        }
        setDraftWeeklyPlan(null);
        addActivity({
            type: 'planning',
            title: 'Revisión Guardada',
            description: `Se han guardado los cambios en la revisión del Plan #${planId}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
        // Stay in review mode, or if you want to exit: setActivePlanId(null);
        // The previous code had setActivePlanId(null); so we keep it if that was the behavior.
        // Wait, the previous code didn't have the addActivity here. 
        // Oh wait, in my previous read handleSaveReview had:
        //  const handleSaveReview = (planId: number) => {
        //     setActivePlanId(null);
        //  };
        setActivePlanId(null);
    };

    const handleBackToExecution = (planId: number) => {
        setDraftWeeklyPlan(null);
        setWeeklyPlans(currentPlans => currentPlans.map(p => {
            if (p.id === planId) {
                return { ...p, status: 'En Ejecución' };
            }
            return p;
        }));
        setIsWeeklyEditing(false);
        addActivity({
            type: 'planning',
            title: 'Planificación re-abierta para edición',
            description: `Se ha vuelto al modo de edición para la Planificación Semanal #${planId}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    const handleFinalClosePlan = (planId: number) => {
        const plan = (draftWeeklyPlan && draftWeeklyPlan.id === planId) ? draftWeeklyPlan : weeklyPlans.find(p => p.id === planId);
        if (plan) {
            const hasPendientes = plan.allocations.some(a => {
                const status = getStatusValue(a);
                return status === 'pendiente';
            });
            if (hasPendientes) {
                alert("No se puede finalizar la revisión: Existen tareas en estado 'Pendiente'. Debe declararlas como 'Sí' o 'No'.");
                return;
            }
            const hasNoWithoutComment = plan.allocations.some(a => {
                const status = getStatusValue(a);
                return status === 'no' && !a.comentario?.trim();
            });
            if (hasNoWithoutComment) {
                alert("No se puede finalizar la revisión: Existen tareas declaradas como 'No' sin justificación.");
                return;
            }
        }

        // Close Plan Immediately (No Confirmation)
        setWeeklyPlans(currentPlans => currentPlans.map(p => {
            if (p.id === planId) {
                return draftWeeklyPlan && draftWeeklyPlan.id === planId ? { ...draftWeeklyPlan, status: 'Cerrada' } : { ...p, status: 'Cerrada' };
            }
            return p;
        }));
        setDraftWeeklyPlan(null);

        const planObj = weeklyPlans.find(p => p.id === planId);
        addActivity({
            type: 'planning',
            title: 'Planificación Semanal Cerrada',
            description: `La planificación semanal #${planId} (${planObj?.startDate} al ${planObj?.endDate}) ha sido cerrada finalizando la semana`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setActivePlanId(null);
        setIsWeeklyEditing(false);
    };

    const handleReopenPlan = (planId: number) => {
        if (window.confirm("¿Seguro que desea volver a abrir esta planificación?")) {
            setWeeklyPlans(currentPlans => currentPlans.map(p => {
                if (p.id === planId) {
                    return { ...p, status: 'En Revisión' };
                }
                return p;
            }));
            const plan = weeklyPlans.find(p => p.id === planId);
            addActivity({
                type: 'planning',
                title: 'Planificación Semanal Reabierta',
                description: `Se ha reabierto la planificación semanal #${planId} del ${plan?.startDate} al ${plan?.endDate} para revisión`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
        }
    };

    const handleBreakdownClick = () => {
        setIsBreakdownMode(true);
        setIsWeeklyEditing(false); // Exclusive
        setNewPlanModalOpen(false);
    };

    // Update allocation field in the main view (for CNC during review)
    const updateMainAllocationField = (planId: number, allocationIndex: number, field: keyof WeeklyAllocation, value: any) => {
        if (draftWeeklyPlan && draftWeeklyPlan.id === planId) {
            setDraftWeeklyPlan(prev => {
                if (!prev) return prev;
                const newAllocations = [...prev.allocations];
                newAllocations[allocationIndex] = {
                    ...newAllocations[allocationIndex],
                    [field]: value
                };
                return { ...prev, allocations: newAllocations };
            });
        } else {
            setWeeklyPlans(prev => prev.map(p => {
                if (p.id !== planId) return p;

                const newAllocations = [...p.allocations];
                newAllocations[allocationIndex] = {
                    ...newAllocations[allocationIndex],
                    [field]: value
                };

                return { ...p, allocations: newAllocations };
            }));
        }
    };

    const handleDeleteAllocation = (planId: number, allocationIndex: number) => {
        if (!confirm("¿Estás seguro de eliminar esta actividad de la planificación semanal?")) return;
        setWeeklyPlans(prev => prev.map(p => {
            if (p.id !== planId) return p;
            const newAllocations = p.allocations.filter((_, i) => i !== allocationIndex);
            return { ...p, allocations: newAllocations };
        }));
        addActivity({
            type: 'planning',
            title: 'Actividad eliminada',
            description: `Se ha eliminado una actividad de la Planificación #${planId}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    // CNC Logic
    const handleAddCNCGroup = () => {
        if (!newCNCGroupName.trim()) return;
        const newGroup: CNCGroup = {
            id: Math.random().toString(36).substr(2, 9),
            name: newCNCGroupName,
            causes: []
        };
        setCncGroups([...cncGroups, newGroup]);
        setNewCNCGroupName('');
    };

    const handleAddCNCCause = (groupId: string) => {
        if (!newCNCCauseName.trim()) return;
        const newCause: CNCCause = {
            id: Math.random().toString(36).substr(2, 9),
            name: newCNCCauseName
        };
        setCncGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return { ...g, causes: [...g.causes, newCause] };
            }
            return g;
        }));
        setNewCNCCauseName('');
        setActiveGroupForNewCNC(null);
    };

    const handleEditCNCGroup = (groupId: string, newName: string) => {
        if (!newName.trim()) return;
        setCncGroups(prev => prev.map(g => g.id === groupId ? { ...g, name: newName } : g));
        setEditingCNCGroupId(null);
    };

    const handleDeleteCNCGroup = (groupId: string) => {
        if (window.confirm("¿Seguro que desea eliminar este grupo y todas sus causas?")) {
            setCncGroups(prev => prev.filter(g => g.id !== groupId));
        }
    };

    const handleEditCNCCause = (groupId: string, causeId: string, newName: string) => {
        if (!newName.trim()) return;
        setCncGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return {
                    ...g,
                    causes: g.causes.map(c => c.id === causeId ? { ...c, name: newName } : c)
                };
            }
            return g;
        }));
        setEditingCNCCauseId(null);
    };

    const handleDeleteCNCCause = (groupId: string, causeId: string) => {
        if (window.confirm("¿Seguro que desea eliminar esta causa?")) {
            setCncGroups(prev => prev.map(g => {
                if (g.id === groupId) {
                    return { ...g, causes: g.causes.filter(c => c.id !== causeId) };
                }
                return g;
            }));
        }
    };

    // Plan Task Modal Logic
    const openPlanTaskModal = (taskId: string) => {
        setPlanningTaskId(taskId);
        setContextMenu({ ...contextMenu, visible: false });

        const taskObj = findTaskById(taskId, tasks);
        const parentTask = findParentTask(taskId, tasks);
        const targetTaskId = (taskObj?.isWeeklySubtask && parentTask) ? parentTask.id : taskId;

        // NEW: Check for pending constraints (ITE Restrictions)
        const pendingConstraints = lookaheadPlans.flatMap(p => p.constraints).filter(c => c.taskId === targetTaskId && c.status === 'Pendiente');

        // 1. Check for "No Aplica Unidades Físicas" restrictions (blocks entire task)
        const naRestriction = pendingConstraints.find(c => c.unitsType === 'na');
        if (naRestriction) {
            alert("Esta tarea cuenta con restricciones activas");
            return;
        }

        // 2. Identify specifically restricted units
        const restrictedUnitIds = new Set<string>();
        pendingConstraints.forEach(c => {
            if (c.unitsType === 'all') {
                // All units for this task are restricted
                units.filter(u => matrixData[`planning_${targetTaskId}_${u.id}`] === '1').forEach(u => restrictedUnitIds.add(u.id));
            } else if (c.unitsType === 'custom' && c.unitIds) {
                c.unitIds.forEach(uid => restrictedUnitIds.add(uid));
            }
        });

        // 1. Find all units marked for this task in Matrix
        const matrixUnits = units.filter(u => matrixData[`planning_${targetTaskId}_${u.id}`] === '1');

        // 2. Determine execution and scheduling states
        const appliesProtocol = protocolConfigs[targetTaskId]?.applies ?? false;

        // Check if we already have allocations for this task in the CURRENT active plan
        const activePlan = weeklyPlans.find(p => p.id === activePlanId);
        const existingAllocations = activePlan?.allocations.filter(a => a.taskId === taskId) || [];

        let displayAllocations: (WeeklyAllocation & { selected: boolean; isRestricted: boolean; isPreviouslyExecuted?: boolean; isReprogrammed?: boolean })[] = [];

        // For each unit, check status
        matrixUnits.forEach(u => {
            const isRestricted = restrictedUnitIds.has(u.id);
            
            // Calculate accumulated compliance from closed plans
            let accumulatedCompliance = 0;
            weeklyPlans
                .filter(p => p.status === 'Cerrada')
                .flatMap(p => p.allocations)
                .forEach(a => {
                    if (a.taskId === taskId && a.unitId === u.id) {
                        accumulatedCompliance += a.percentCumplimiento ?? (a.executed ? 100 : 0);
                    }
                });
            accumulatedCompliance = Math.min(100, accumulatedCompliance);

            // Also check protocol
            const wasProtocolExecuted = appliesProtocol && isProtocolProgressedLocal(targetTaskId, u.id);
            if (wasProtocolExecuted) {
                accumulatedCompliance = 100;
            }

            const isPreviouslyExecuted = accumulatedCompliance >= 100;
            
            const wasScheduled = weeklyPlans
                .filter(p => p.status === 'Cerrada')
                .flatMap(p => p.allocations)
                .some(a => a.taskId === taskId && a.unitId === u.id);
            const isReprogrammed = wasScheduled && !isPreviouslyExecuted;

            const existing = existingAllocations.find(a => a.unitId === u.id);

            if (existing) {
                displayAllocations.push({
                    ...existing,
                    selected: true,
                    isRestricted,
                    isPreviouslyExecuted,
                    isReprogrammed,
                    percentComprometido: existing.percentComprometido ?? (existing.executed ? 100 : 100),
                    percentCumplimiento: existing.percentCumplimiento ?? (existing.executed ? 100 : 0)
                });
            } else {
                const config = protocolConfigs[targetTaskId];
                const group = config?.reviewerGroups?.find(g => g.unitIds.includes(u.id));
                const defaultResponsible = group?.reviewers?.[0]?.id || '';

                displayAllocations.push({
                    taskId: taskId,
                    unitId: u.id,
                    responsibleId: defaultResponsible,
                    days: { L: false, M: false, X: false, J: false, V: false, S: false },
                    executed: null,
                    selected: false,
                    isRestricted,
                    isPreviouslyExecuted,
                    isReprogrammed,
                    percentComprometido: 100 - accumulatedCompliance,
                    percentCumplimiento: 0
                });
            }
        });

        setCurrentAllocations(displayAllocations);
        setPlanTaskModalOpen(true);
    };

    const toggleRowSelection = (index: number) => {
        const newAllocations = [...currentAllocations];
        newAllocations[index].selected = !newAllocations[index].selected;
        setCurrentAllocations(newAllocations);
    };

    const toggleDay = (index: number, day: string) => {
        const newAllocations = [...currentAllocations];
        newAllocations[index] = {
            ...newAllocations[index],
            days: { ...newAllocations[index].days, [day]: !newAllocations[index].days[day] }
        };
        setCurrentAllocations(newAllocations);
    };

    const updateAllocationField = (index: number, field: keyof WeeklyAllocation, value: any) => {
        const newAllocations = [...currentAllocations];
        newAllocations[index] = { ...newAllocations[index], [field]: value };
        setCurrentAllocations(newAllocations);
    };

    const handleSaveAllocations = () => {
        if (activePlanId === null) return;

        const selectedAllocations = currentAllocations.filter(a => a.selected);

        // Validation
        for (const alloc of selectedAllocations) {
            if (!alloc.responsibleId) {
                alert(`Debe asignar un responsable para la unidad: ${units.find(u => u.id === alloc.unitId)?.name}`);
                return;
            }
            const hasDay = Object.values(alloc.days).some(d => d);
            if (!hasDay) {
                alert(`Debe seleccionar al menos un día para la unidad: ${units.find(u => u.id === alloc.unitId)?.name}`);
                return;
            }
        }

        setWeeklyPlans(prevPlans => prevPlans.map(plan => {
            if (plan.id === activePlanId) {
                // Remove old allocations for this task and add current ones (sanitized)
                const otherAllocations = plan.allocations.filter(a => a.taskId !== planningTaskId);
                const newAllocationsToSave = selectedAllocations.map(({ selected, isRestricted, isPreviouslyExecuted, isReprogrammed, ...rest }) => rest);

                return {
                    ...plan,
                    allocations: [...otherAllocations, ...newAllocationsToSave]
                };
            }
            return plan;
        }));

        setPlanTaskModalOpen(false);
    };

    const toggleGanttSuggestions = () => {
        if (!hasPermission('ps_gantt_avance', 'view') && !hasPermission('ps_gantt_avance', 'edit')) return;
        setShowGanttSuggestions(prev => !prev);
    };

    const handleOpenAutoPlanModal = () => {
        if (!hasPermission('ps_gantt_avance', 'edit')) return;
        if (!activePlanId) return;

        const userEmail = currentUser?.email;
        const matched = responsibles.find(r => r.email?.toLowerCase() === userEmail?.toLowerCase());
        setGanttAutoPlanSelectedRespId(matched?.id || responsibles[0]?.id || '');
        setGanttAutoPlanModalOpen(true);
    };

    const handleAutoPlanGanttTasks = (selectedResponsibleId: string) => {
        if (!hasPermission('ps_gantt_avance', 'edit')) return;
        if (!activePlanId) return;
        const plan = weeklyPlans.find(p => p.id === activePlanId);
        if (!plan) return;

        // 1. Get overlapping leaf tasks
        const suggestedTasks = flattenedTasks.filter(t => {
            if (t.type !== 'task' || (t.children && t.children.length > 0)) return false;
            return t.start && t.end && t.start <= plan.endDate && t.end >= plan.startDate;
        });

        if (suggestedTasks.length === 0) {
            alert("No hay partidas proyectadas en la Carta Gantt para las fechas de esta semana.");
            return;
        }

        // Pre-fetch closed plans
        const closedPlans = weeklyPlans.filter(p => p.status === 'Cerrada');

        let addedCount = 0;
        let tasksAdded = new Set<string>();
        const newAllocations = [...plan.allocations];

        suggestedTasks.forEach(task => {
            const taskId = task.id;
            
            // Get all units assigned to this task in Matrix
            const assignedUnits = units.filter(u => matrixData[`planning_${taskId}_${u.id}`] === '1');
            const appliesProtocol = protocolConfigs[taskId]?.applies ?? false;

            assignedUnits.forEach(u => {
                // Check if already in active plan
                const exists = newAllocations.some(a => a.taskId === taskId && a.unitId === u.id);
                if (exists) return;

                // Calculate accumulated compliance from closed plans
                let accumulatedCompliance = 0;
                closedPlans.forEach(cp => {
                    cp.allocations.forEach(a => {
                        if (a.taskId === taskId && a.unitId === u.id) {
                            accumulatedCompliance += a.percentCumplimiento ?? (a.executed ? 100 : 0);
                        }
                    });
                });
                accumulatedCompliance = Math.min(100, accumulatedCompliance);

                // Also check protocol
                const wasProtocolExecuted = appliesProtocol && isProtocolProgressedLocal(taskId, u.id);
                if (wasProtocolExecuted) {
                    accumulatedCompliance = 100;
                }

                if (accumulatedCompliance >= 100) return;

                // Use the selected responsible from the mass assignment modal
                const finalResponsible = selectedResponsibleId || responsibles[0]?.id || '';

                // Add default allocation (Lunes a Viernes)
                newAllocations.push({
                    taskId: taskId,
                    unitId: u.id,
                    responsibleId: finalResponsible,
                    days: { L: true, M: true, X: true, J: true, V: true, S: false },
                    executed: null,
                    percentComprometido: 100 - accumulatedCompliance,
                    percentCumplimiento: 0
                });

                addedCount++;
                tasksAdded.add(task.name);
            });
        });

        if (addedCount === 0) {
            alert("Todas las unidades físicas de las partidas sugeridas ya están programadas o completadas.");
            return;
        }

        // Save updated plan allocations
        setWeeklyPlans(prevPlans => prevPlans.map(p => {
            if (p.id === activePlanId) {
                return { ...p, allocations: newAllocations };
            }
            return p;
        }));

        addActivity({
            type: 'planning',
            title: 'Auto-programación por Carta Gantt',
            description: `Se auto-programaron ${addedCount} unidades físicas para ${tasksAdded.size} partidas sugeridas por Gantt`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        alert(`¡Listo! Se han auto-programado automáticamente ${addedCount} unidades físicas de las partidas recomendadas.`);
    };

    // --- Renderers ---

    // Get currently active plan data
    const _baseActivePlan = weeklyPlans.find(p => p.id === activePlanId);
    const activePlan = _baseActivePlan?.status === 'En Revisión' && draftWeeklyPlan?.id === activePlanId ? draftWeeklyPlan : _baseActivePlan;

    // Get currently active lookahead plan
    const activeLookahead = lookaheadPlans.find(p => p.id === activeLookaheadId);

    // Calculate if there are missing observations for lookahead
    const hasMissingLookaheadComments = useMemo(() => {
        if (!activeLookahead || activeLookahead.status !== 'En Revisión') return false;
        return !isLookaheadFullyResolved(activeLookahead);
    }, [activeLookahead, lookaheadPlans, tasks]);

    // Calculate if there are missing CNCs
    const hasMissingCNC = useMemo(() => {
        if (!activePlan || activePlan.status !== 'En Revisión') return false;

        return activePlan.allocations.some(alloc => {
            const config = protocolConfigs[alloc.taskId];
            const appliesProtocol = config?.applies ?? false;

            // Calculate accumulated compliance from closed plans
            let accumulatedCompliance = 0;
            weeklyPlans
                .filter(p => p.status === 'Cerrada')
                .flatMap(p => p.allocations)
                .forEach(a => {
                    if (a.taskId === alloc.taskId && a.unitId === alloc.unitId) {
                        accumulatedCompliance += a.percentCumplimiento ?? (a.executed ? 100 : 0);
                    }
                });
            accumulatedCompliance = Math.min(100, accumulatedCompliance);

            // Check if protocol was closed (meaning it is 100% completed)
            const wasProtocolExecuted = appliesProtocol && isProtocolProgressedLocal(alloc.taskId, alloc.unitId);
            
            // Effective comprometido and cumplimiento for this week
            const pctComprometido = alloc.percentComprometido ?? (alloc.executed ? 100 : 100);
            let pctCumplimiento = alloc.percentCumplimiento ?? (alloc.executed ? 100 : 0);

            if (wasProtocolExecuted) {
                pctCumplimiento = Math.max(pctCumplimiento, 100 - accumulatedCompliance);
            }

            // If we didn't achieve the commitment, and no CNC is selected, it's missing CNC
            const isFitted = pctCumplimiento >= pctComprometido;
            return !isFitted && !alloc.cncId;
        });
    }, [activePlan, weeklyPlans, savedProtocols, protocolConfigs]);

    // User profile/role helpers for Jefatura / Oficina Técnica
    const isJefaturaOrOficina = useMemo(() => {
        if (!currentUser) return false;
        const isGlobalAdmin = currentUser.globalRole === 'Administrador';
        const isProjectAdmin = currentUser.role === 'admin' || currentUser.role === 'manager';
        if (isGlobalAdmin || isProjectAdmin) return true;
        
        const projectId = projectInfo?.id;
        if (!projectId) return false;
        
        const assignment = currentUser.assignedProjects?.find((ap: any) => ap.projectId === projectId);
        if (!assignment) return false;
        
        const profileId = assignment.profileId;
        return profileId === 'prof-jefe' || profileId === 'prof-oficina';
    }, [currentUser, projectInfo]);

    // Helper to get status string ("si" | "no" | "pendiente")
    const getStatusValue = (alloc: WeeklyAllocation) => {
        if (alloc.executed === true) return "si";
        if (alloc.executed === false) return "no";
        return "pendiente";
    };

    // Helper to check if a weekly allocation row's inputs should be disabled
    const getDisabledStatus = (alloc: WeeklyAllocation) => {
        if (activePlan?.status === 'Cerrada') return true;
        if (activePlan?.status === 'En Planificación') return true;
        
        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
        
        if (activePlan?.status === 'En Ejecución') {
            const isAssigned = responsibles.find(r => r.id === alloc.responsibleId)?.email === currentUser?.email;
            return !(isAdmin || isAssigned);
        }
        
        if (activePlan?.status === 'En Revisión') {
            return !isJefaturaOrOficina;
        }
        
        return true;
    };

    // Recursive Gantt Bar Renderer
    const renderGanttBars = (list: Task[], level = 0): React.ReactNode[] => {
        let rows: React.ReactNode[] = [];
        list.forEach((task) => {
            // Logic to hide weekly subtasks if not in weekly mode (matches left table logic)
            const visibleChildren = (task.children || []).filter(child => {
                if (activeTab !== 'semanal' && child.isWeeklySubtask) return false;
                return true;
            });
            const hasChildren = visibleChildren.length > 0;

            // Calculate Position
            const left = getDateX(task.start || '2026-01-01');
            const taskEnd = getDateX(getDayAfter(task.end || task.start || '2026-01-01'));
            const width = taskEnd - left;

            const isSelected = selectedGanttTaskId === task.id;
            const isPredecessor = taskPathInfo?.predecessors.has(task.id_original_project || -9999);
            const isSuccessor = taskPathInfo?.successors.has(task.id_original_project || -9999);

            let customBarClass = "";
            let customBarStyles: React.CSSProperties = {};

            if (selectedGanttTaskId) {
                if (isSelected) {
                    customBarClass = "ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg scale-y-105 z-25";
                } else if (isPredecessor) {
                    customBarClass = "z-20";
                    customBarStyles.backgroundColor = "#eab308"; // Amarillo brillante
                } else if (isSuccessor) {
                    customBarClass = "z-20";
                    customBarStyles.backgroundColor = "#d946ef"; // Rosado/Fucsia
                } else {
                    customBarClass = "opacity-35"; // Atenuado
                }
            }

            let folderColor = "#1e293b";
            if (task.isCritical) {
                folderColor = "#ef4444";
            }
            if (taskPathInfo) {
                const isPredecessor = task.id_original_project !== undefined && taskPathInfo.predecessors.has(task.id_original_project);
                const isSuccessor = task.id_original_project !== undefined && taskPathInfo.successors.has(task.id_original_project);
                if (isPredecessor) {
                    folderColor = "#eab308";
                } else if (isSuccessor) {
                    folderColor = "#d946ef";
                }
            }

            rows.push(
                <div 
                    key={task.id} 
                    id={`gantt-row-${task.id}`} 
                    className="h-[44px] border-b border-slate-200 dark:border-slate-700 relative group cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGanttTaskId(prev => prev === task.id ? null : task.id);
                    }}
                >
                    {/* Bar */}
                    {task.type === 'folder' ? (
                        <div
                            id={`gantt-bar-${task.id}`}
                            className={`absolute top-[16px] h-[12px] transition-all z-10 ${customBarClass}`}
                            style={{
                                left: `${left}px`,
                                width: `${Math.max(width, 2)}px`,
                                userSelect: 'none',
                            }}
                            title={`${task.name}: ${task.start} - ${task.end} (${task.duration} días)`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedGanttTaskId(prev => prev === task.id ? null : task.id);
                            }}
                        >
                            {/* Thin horizontal line */}
                            <div 
                                className="absolute top-[2px] left-0 right-0 h-[4px]"
                                style={{ backgroundColor: folderColor }}
                            />
                            {/* Left Hook SVG */}
                            <svg 
                                className="absolute left-0 top-[2px] w-[8px] h-[10px]" 
                                viewBox="0 0 8 10" 
                                style={{ color: folderColor }}
                            >
                                <polygon points="0,0 8,0 8,3 4,7 0,3" fill="currentColor" />
                            </svg>
                            {/* Right Hook SVG */}
                            <svg 
                                className="absolute right-0 top-[2px] w-[8px] h-[10px]" 
                                viewBox="0 0 8 10" 
                                style={{ color: folderColor }}
                            >
                                <polygon points="0,0 8,0 8,3 4,7 0,3" fill="currentColor" />
                            </svg>
                        </div>
                    ) : (
                        <div
                            id={`gantt-bar-${task.id}`}
                            className={`absolute top-2 h-5 rounded-md shadow-sm text-[10px] text-white flex items-center px-2 overflow-hidden whitespace-nowrap transition-all z-10
                                ${task.isCritical
                                    ? 'bg-red-500 dark:bg-red-600 hover:bg-red-600'
                                    : 'bg-blue-500 dark:bg-blue-600 hover:bg-blue-600'
                                }
                                ${(isMasterEditing) ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}
                                ${customBarClass}
                            `}
                            style={{
                                left: `${left}px`,
                                width: `${Math.max(width, 2)}px`,
                                userSelect: 'none',
                                ...customBarStyles
                            }}
                            title={`${task.name}: ${task.start} - ${task.end} (${task.duration} días)`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedGanttTaskId(prev => prev === task.id ? null : task.id);
                            }}
                            onMouseDown={(e) => {
                                if (!isMasterEditing || !task.start) return;
                                setDraggingTask({
                                    taskId: task.id,
                                    startX: e.clientX,
                                    originalStart: task.start
                                });
                            }}
                        >
                            {width > 30 && <span>{truncateProgress(task.progress || 0)}%</span>}
                        </div>
                    )}

                    {/* Baseline / Ghost Bar */}
                    {showComparison && comparisonBaseline && (
                        (() => {
                            const findInSnapshot = (id: string, list: Task[]): Task | null => {
                                for (const t of list) {
                                    if (t.id === id) return t;
                                    if (t.children) {
                                        const found = findInSnapshot(id, t.children);
                                        if (found) return found;
                                    }
                                }
                                return null;
                            };

                            const baselineTask = findInSnapshot(task.id, comparisonBaseline.tasksSnapshot);
                            if (!baselineTask || !baselineTask.start) return null;

                            const bLeft = getDateX(baselineTask.start);
                            const bEnd = getDateX(getDayAfter(baselineTask.end || baselineTask.start));
                            const bWidth = bEnd - bLeft;

                            return (
                                <div
                                    className={`absolute top-7 h-1.5 rounded-sm opacity-40 z-0 pointer-events-none
                                        ${task.type === 'folder' ? 'bg-slate-300' : 'bg-slate-400 border border-dashed border-slate-500'}
                                    `}
                                    style={{
                                        left: `${bLeft}px`,
                                        width: `${Math.max(bWidth, 2)}px`
                                    }}
                                    title={`Base: ${baselineTask.start} - ${baselineTask.end}`}
                                />
                            );
                        })()
                    )}
                </div>
            );

            if (task.expanded && hasChildren) {
                rows = [...rows, ...renderGanttBars(visibleChildren, level + 1)];
            }
        });
        return rows;
    };

    const renderTableRows = (list: Task[], level = 0, indexPrefix = ""): React.ReactNode[] => {
        let rows: React.ReactNode[] = [];
        list.forEach((task, index) => {
            const currentIndex = indexPrefix ? `${indexPrefix}.${index + 1}` : `${index + 1}`;
            const paddingLeft = level * 20 + 10;
            const isEditing = activeTab === 'maestra' ? isMasterEditing : isWeeklyEditing;

            // Determine visible children for recursion
            const visibleChildren = (task.children || []).filter(child => {
                // If not in Weekly mode, hide any items flagged as "Weekly Subtasks"
                if (activeTab !== 'semanal' && child.isWeeklySubtask) return false;
                return true;
            });
            const hasChildren = visibleChildren.length > 0;

            // I.T.E Visualization Logic
            let rowBackgroundClass = "";
            let rowBorderClass = "";

            if (activeTab === 'intermedia' && isITEView && task.type === 'task') {
                // Find all constraints for this task across ALL plans
                const allConstraints = lookaheadPlans.flatMap(p => p.constraints).filter(c => c.taskId === task.id);
                // Check if task was ever cleared in any plan
                const isExplicitlyClearedAnywhere = lookaheadPlans.some(p => p.clearedTaskIds?.includes(task.id));

                // Only color if there is some history (constraint exists or was cleared)
                if (allConstraints.length > 0 || isExplicitlyClearedAnywhere) {
                    const hasPending = allConstraints.some(c => c.status === 'Pendiente');

                    if (hasPending) {
                        rowBackgroundClass = "bg-[#fee2e2] dark:bg-red-900/30"; // Soft Red
                    } else {
                        // Green if no pending constraints
                        rowBackgroundClass = "bg-[#f0fdf4] dark:bg-green-900/30"; // Soft Green
                    }
                }
            }

            if (activeTab === 'semanal' && showGanttSuggestions && task.type === 'task' && activePlan && (hasPermission('ps_gantt_avance', 'view') || hasPermission('ps_gantt_avance', 'edit'))) {
                const taskStart = task.start;
                const taskEnd = task.end;
                const weekStart = activePlan.startDate;
                const weekEnd = activePlan.endDate;
                
                if (taskStart && taskEnd && weekStart && weekEnd) {
                    const overlaps = taskStart <= weekEnd && taskEnd >= weekStart;
                    if (overlaps) {
                        rowBackgroundClass = "bg-blue-50/80 dark:bg-blue-950/40 font-bold";
                        rowBorderClass = "border-l-4 border-l-blue-500";
                    }
                }
            }

            const isSelected = selectedGanttTaskId === task.id;
            const isPredecessor = taskPathInfo?.predecessors.has(task.id_original_project || -9999);
            const isSuccessor = taskPathInfo?.successors.has(task.id_original_project || -9999);

            let customRowClass = "";
            if (selectedGanttTaskId) {
                if (isSelected) {
                    customRowClass = "bg-blue-100/60 dark:bg-blue-950/40 border-l-4 border-l-blue-500 font-semibold";
                } else if (isPredecessor) {
                    customRowClass = "bg-yellow-50/70 dark:bg-yellow-950/25 border-l-4 border-l-yellow-500";
                } else if (isSuccessor) {
                    customRowClass = "bg-fuchsia-50/70 dark:bg-fuchsia-950/25 border-l-4 border-l-fuchsia-500";
                } else {
                    customRowClass = "opacity-35";
                }
            }

            const isGraphicSelected = graphicMode && graphicSelectedTaskId === task.id;

            rows.push(
                <tr
                    key={task.id}
                    className={`
                        border-b border-slate-200 dark:border-slate-700 
                        ${task.type === 'folder' ? 'bg-slate-50 dark:bg-slate-800/50' : 'bg-white dark:bg-slate-900'}
                        ${rowBackgroundClass}
                        ${rowBorderClass}
                        hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors h-[44px]
                        ${isBreakdownMode || (activeTab === 'maestra' && isMasterEditing) || graphicMode || true ? 'cursor-pointer' : ''}
                        ${isGraphicSelected ? 'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-l-blue-500 border-b-blue-200 dark:border-b-blue-800' : ''}
                        ${customRowClass}
                    `}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGanttTaskId(prev => prev === task.id ? null : task.id);
                        if (graphicMode && task.type === 'task' && !hasChildren) {
                            setGraphicSelectedTaskId(prev => prev === task.id ? null : task.id);
                        }
                    }}
                    onContextMenu={(e) => handleContextMenu(e, task)}
                >
                    <td className="px-4 py-2 text-xs text-slate-500 font-mono w-12 text-center">{task.id_original_project || "-"}</td>
                    <td 
                        className="px-4 py-2 border-r border-slate-200 dark:border-slate-700"
                        style={tareaColumnWidth !== null ? { width: `${tareaColumnWidth}px`, minWidth: `${tareaColumnWidth}px`, maxWidth: `${tareaColumnWidth}px`, overflow: 'hidden' } : undefined}
                    >
                        <div style={{ paddingLeft: `${paddingLeft}px` }} className="flex items-center gap-2 overflow-hidden w-full">
                            {(task.type === 'folder' || hasChildren) && (
                                <button onClick={() => {
                                    const toggleRecursive = (list: Task[]): Task[] => {
                                        return list.map(t => {
                                            if (t.id === task.id) return { ...t, expanded: !t.expanded };
                                            if (t.children) return { ...t, children: toggleRecursive(t.children) };
                                            return t;
                                        });
                                    };
                                    setTasks(toggleRecursive(tasks));
                                }} className="text-slate-400 hover:text-primary">
                                    <span className="material-symbols-outlined text-[18px]">
                                        {task.expanded ? 'folder_open' : 'folder'}
                                    </span>
                                </button>
                            )}
                            {task.type === 'task' && !hasChildren && (
                                <span className="material-symbols-outlined text-[18px] text-slate-400">
                                    check_circle
                                </span>
                            )}
                            <span className={`text-sm truncate flex-1 ${task.type === 'folder' ? 'font-bold text-slate-800 dark:text-slate-200' : 'text-slate-600 dark:text-slate-300'}`}>
                                <span className="text-xs text-slate-400 mr-1.5 font-mono font-normal">[{currentIndex}]</span>
                                {task.name}
                            </span>
                            {activeTab === 'semanal' && task.type === 'task' && !hasChildren && (() => {
                                const pendingConstraints = lookaheadPlans.flatMap(p => p.constraints).filter(c => c.taskId === task.id && c.status === 'Pendiente');
                                if (pendingConstraints.length > 0) {
                                    return (
                                        <span className="material-symbols-outlined text-[18px] text-amber-500 animate-pulse cursor-help" title="Esta tarea cuenta con restricciones activas">
                                            warning
                                        </span>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    </td>

                    {activeTab === 'semanal' || (activeTab === 'maestra' && !isMasterEditing) ? (
                        <td className="px-4 py-2 text-center border-r border-slate-200 dark:border-slate-700 w-32">
                            {(task.type === 'task' || (task.children && task.children.length > 0)) ? (
                                (() => {
                                    const progress = calculateTaskProgressLocal(task);
                                    const hasWeeklySubtasks = task.children && task.children.some(c => c.isWeeklySubtask);
                                    const isSubtask = task.isWeeklySubtask;
                                    
                                    let textColorClass = 'text-slate-700 dark:text-slate-300';
                                    let barColorClass = 'bg-blue-500';
                                    
                                    if (hasWeeklySubtasks) {
                                        textColorClass = 'text-black dark:text-white font-bold';
                                        barColorClass = 'bg-slate-950 dark:bg-white';
                                    } else if (isSubtask) {
                                        textColorClass = 'text-green-600 dark:text-green-400 font-bold';
                                        barColorClass = 'bg-green-500';
                                    } else if (progress === 100) {
                                        textColorClass = 'text-green-600';
                                        barColorClass = 'bg-green-500';
                                    }
                                    
                                    return (
                                        <div className="flex flex-col gap-1 px-2">
                                            <div className="flex items-center justify-center gap-1">
                                                <span className={`text-[10px] ${textColorClass}`}>{truncateProgress(progress)}%</span>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                <div className={`h-full ${barColorClass}`} style={{ width: `${progress}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                })()
                            ) : (
                                <span className="text-xs text-slate-400 font-medium">-</span>
                            )}
                        </td>
                    ) : null}
                    {activeTab !== 'semanal' && (
                        <>
                            <td className="px-4 py-2 text-center border-r border-slate-200 dark:border-slate-700 w-24">
                                {isMasterEditing && task.type !== 'folder' ? (
                                    <input
                                        type="number"
                                        className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600 text-center"
                                        value={task.duration || 0}
                                        onChange={(e) => {
                                            const newDuration = parseInt(e.target.value) || 0;
                                            const updateRecursive = (list: Task[]): Task[] => {
                                                return list.map(t => {
                                                    if (t.id === task.id) {
                                                        const updatedTask = { ...t, duration: newDuration };
                                                        // Sync End Date
                                                        if (t.start && newDuration > 0) {
                                                            updatedTask.end = calculateEndDate(t.start, newDuration, workingDays, holidays);
                                                        }
                                                        return updatedTask;
                                                    }
                                                    if (t.children) return { ...t, children: updateRecursive(t.children) };
                                                    return t;
                                                });
                                            };
                                            setTasks(prev => {
                                                const updated = updateRecursive(prev);
                                                return syncDependencies(updated, task.id);
                                            });
                                        }}
                                    />
                                ) : (
                                    <span className="text-sm text-slate-600 dark:text-slate-400">
                                        {task.duration ? `${task.duration} días` : '-'}
                                    </span>
                                )}
                            </td>
                            <td className="px-2 py-1 border-r border-slate-200 dark:border-slate-700 w-36">
                                {isMasterEditing && task.type !== 'folder' ? (
                                    <input
                                        type="date"
                                        className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600"
                                        value={task.start || ""}
                                        onChange={(e) => {
                                            const newStart = e.target.value;
                                            const updateRecursive = (list: Task[]): Task[] => {
                                                return list.map(t => {
                                                    if (t.id === task.id) {
                                                        let sDate = new Date(newStart + "T12:00:00");
                                                        while (!isWorkingDay(sDate, workingDays, holidays)) {
                                                            sDate.setDate(sDate.getDate() + 1);
                                                        }
                                                        const normalizedStart = getLocalDateStr(sDate);
                                                        const updatedTask = { ...t, start: normalizedStart };

                                                        // Priority 1: Sync Duration (if End exists)
                                                        if (normalizedStart && t.end) {
                                                            let duration = calculateWorkingDaysDuration(normalizedStart, t.end, workingDays, holidays);
                                                            if (duration < 1) duration = 1;
                                                            updatedTask.duration = duration;
                                                            updatedTask.end = calculateEndDate(normalizedStart, duration, workingDays, holidays);
                                                        }
                                                        // Priority 2: Sync End (if Duration exists but no End)
                                                        else if (normalizedStart && t.duration) {
                                                            updatedTask.end = calculateEndDate(normalizedStart, t.duration, workingDays, holidays);
                                                        }
                                                        return updatedTask;
                                                    }
                                                    if (t.children) return { ...t, children: updateRecursive(t.children) };
                                                    return t;
                                                });
                                            };
                                            setTasks(prev => {
                                                const updated = updateRecursive(prev);
                                                return syncDependencies(updated, task.id);
                                            });
                                        }}
                                    />
                                ) : (
                                    <span className="text-xs text-slate-600 dark:text-slate-300 block px-2 py-1 whitespace-nowrap">{task.start}</span>
                                )}
                            </td>
                            <td className="px-2 py-1 border-r border-slate-200 dark:border-slate-700 w-36">
                                {isMasterEditing && task.type !== 'folder' ? (
                                    <input
                                        type="date"
                                        className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600"
                                        value={task.end || ""}
                                        onChange={(e) => {
                                            const newEnd = e.target.value;
                                            const updateRecursive = (list: Task[]): Task[] => {
                                                return list.map(t => {
                                                    if (t.id === task.id) {
                                                        let eDate = new Date(newEnd + "T12:00:00");
                                                        while (!isWorkingDay(eDate, workingDays, holidays)) {
                                                            eDate.setDate(eDate.getDate() - 1);
                                                        }
                                                        const normalizedEnd = getLocalDateStr(eDate);
                                                        const updatedTask = { ...t, end: normalizedEnd };

                                                        // Sync Duration
                                                        if (t.start && normalizedEnd) {
                                                            let duration = calculateWorkingDaysDuration(t.start, normalizedEnd, workingDays, holidays);
                                                            if (duration < 1) {
                                                                duration = 1;
                                                                updatedTask.end = calculateEndDate(t.start, 1, workingDays, holidays);
                                                            } else {
                                                                updatedTask.duration = duration;
                                                            }
                                                        }
                                                        return updatedTask;
                                                    }
                                                    if (t.children) return { ...t, children: updateRecursive(t.children) };
                                                    return t;
                                                });
                                            };
                                            setTasks(prev => {
                                                const updated = updateRecursive(prev);
                                                return syncDependencies(updated, task.id);
                                            });
                                        }}
                                    />
                                ) : (
                                    <span className="text-xs text-slate-600 dark:text-slate-300 block px-2 py-1 whitespace-nowrap">{task.end}</span>
                                )}
                            </td>
                            {(isMasterEditing || flattenedTasks.some(t => (t.predecessors && t.predecessors.length > 0) || (t.successors && t.successors.length > 0))) && (
                                <>
                                    <td className="px-2 py-1 w-24 border-r border-slate-200 dark:border-slate-700">
                                        {isMasterEditing ? (
                                            <PredecessorInputCell
                                                task={task}
                                                onUpdate={handleUpdatePredecessors}
                                            />
                                        ) : (
                                            <span className="text-xs text-slate-600 dark:text-slate-300 block px-2 py-1 whitespace-nowrap">{task.predecessors?.join(", ") || "-"}</span>
                                        )}
                                    </td>
                                    <td className="px-2 py-1 w-24">
                                        {isMasterEditing ? (
                                            <SuccessorInputCell
                                                task={task}
                                                onUpdate={handleUpdateSuccessors}
                                            />
                                        ) : (
                                            <span className="text-xs text-slate-600 dark:text-slate-300 block px-2 py-1 whitespace-nowrap">{task.successors?.join(", ") || "-"}</span>
                                        )}
                                    </td>
                                </>
                            )}
                        </>
                    )}
                </tr>
            );

            // Recurse if expanded and has visible children
            if (task.expanded && hasChildren) {
                rows = [...rows, ...renderTableRows(visibleChildren, level + 1, currentIndex)];
            }
        });
        return rows;
    };

    return (
        <div 
            className="flex flex-col h-full bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans overflow-hidden"
        >
            {/* Header */}
            <div className="bg-[#1a73e8] dark:bg-blue-900 text-white px-4 md:px-6 pt-4 pb-0 flex flex-col gap-4 shrink-0 shadow-md z-30 overflow-hidden">
                <div className="hidden md:flex items-center gap-6">
                    <div className="flex items-center gap-2 text-sm text-blue-100 relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setCncMenuOpen(!cncMenuOpen); setResponsiblesMenuOpen(false); setConstraintsMenuOpen(false); }}
                            className={`font-semibold text-white flex items-center gap-1 hover:bg-white/10 px-2 py-1 rounded transition-colors ${cncMenuOpen ? 'bg-white/10' : ''}`}
                        >
                            Causas de No Cumplimiento
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>
                        {cncMenuOpen && (
                            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded shadow-lg py-1 w-48 z-50 text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-75">
                                <button
                                    onClick={() => { setCncModalOpen(true); setCncMenuOpen(false); }}
                                    className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm"
                                >
                                    Crear C.N.C
                                </button>
                            </div>
                        )}
                    </div>



                    <div className="flex items-center gap-2 text-sm text-blue-100 relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setConstraintsMenuOpen(!constraintsMenuOpen); setCncMenuOpen(false); setResponsiblesMenuOpen(false); }}
                            className={`font-semibold text-white flex items-center gap-1 hover:bg-white/10 px-2 py-1 rounded transition-colors ${constraintsMenuOpen ? 'bg-white/10' : ''}`}
                        >
                            Restricciones
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>
                        {constraintsMenuOpen && (
                            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded shadow-lg py-1 w-48 z-50 text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-75">
                                <button
                                    onClick={() => { setConstraintsModalOpen(true); setConstraintsMenuOpen(false); }}
                                    className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm"
                                >
                                    Crear restricciones
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 md:gap-0">
                    <div className="flex items-center justify-between w-full md:w-auto pb-2 md:pb-6">
                        <div className="flex items-center gap-3 md:gap-4 truncate">
                            <div className="size-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm shrink-0">
                                <span className="material-symbols-outlined text-[24px]">apartment</span>
                            </div>
                            <div className="truncate">
                                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                                    <h1 className="text-lg md:text-xl font-bold truncate">{projectInfo.name}</h1>
                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold shadow-sm w-fit ${projectStatus.color}`}>
                                        <span className="material-symbols-outlined text-[18px]">{projectStatus.icon}</span>
                                        {projectStatus.label} {projectStatus.delay > 0 && `(${projectStatus.delay}d desvío)`}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile triggers in the blue header bar */}
                        <div className="flex md:hidden items-center gap-1.5 ml-2 shrink-0">
                            <button 
                                onClick={() => window.dispatchEvent(new CustomEvent('open-bug-report'))}
                                className="w-10 h-10 bg-rose-600 border border-white/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                                title="Reportar Falla"
                            >
                                <span className="material-symbols-outlined text-[20px]">bug_report</span>
                            </button>
                            <button 
                                onClick={() => window.dispatchEvent(new CustomEvent('open-ai-assistant'))}
                                className="w-10 h-10 bg-white/20 border border-white/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95 backdrop-blur-sm"
                                title="Asistente IA"
                            >
                                <svg viewBox="0 0 100 100" className="w-[20px] h-[20px]" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M25 50 C25 25, 75 25, 75 50" stroke="currentColor" strokeWidth="6" strokeLinecap="round" fill="none" />
                                    <rect x="18" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                    <rect x="74" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                    <circle cx="40" cy="50" r="4.5" fill="currentColor" />
                                    <circle cx="60" cy="50" r="4.5" fill="currentColor" />
                                    <path d="M42 63 C45 67, 55 67, 58 63" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
                                    <path d="M78 56 C78 72, 68 82, 52 82" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" fill="none" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-end gap-1 overflow-x-auto w-full md:w-auto hide-scrollbar pb-0">
                        {[
                            { id: 'maestra', label: 'Maestra', show: hasPermission('mod_plan_maestra', 'view') },
                            { id: 'intermedia', label: 'Intermedia', show: hasPermission('mod_plan_intermedia', 'view') },
                            { id: 'semanal', label: 'Semanal', show: hasPermission('mod_plan_semanal', 'view') }
                        ].filter(t => t.show).map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setActiveTab(tab.id as any);
                                    setIsBreakdownMode(false);
                                    if (tab.id === 'maestra') { setIsWeeklyEditing(false); }
                                    else if (tab.id === 'semanal') { setIsMasterEditing(false); }
                                }}
                                className={`
                                    flex items-center gap-2 px-4 py-3 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap
                                    ${activeTab === tab.id
                                        ? 'bg-white dark:bg-[#101822] text-[#1a73e8] dark:text-blue-400 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]'
                                        : 'bg-white/10 text-white hover:bg-white/20'}
                                    ${tab.id === 'maestra' ? 'hidden md:flex' : ''}
                                `}
                            >
                                <span className="material-symbols-outlined text-[18px]">
                                    {tab.id === 'maestra' ? 'calendar_month' : tab.id === 'intermedia' ? 'calendar_view_week' : 'view_week'}
                                </span>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="hidden md:flex bg-white dark:bg-[#101822] border-b border-slate-200 dark:border-slate-700 px-6 py-3 items-center justify-between shrink-0 shadow-sm z-20">
                {activeTab === 'maestra' ? (
                    <>
                        {hasPermission('pm_gantt_edit', 'edit') && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        if (isMasterEditing) {
                                            if (forceSyncTasks) {
                                                forceSyncTasks(processedTasks).catch(alert);
                                            }
                                            addActivity({
                                                type: 'planning',
                                                title: 'Planificación modificada',
                                                description: 'Se han guardado cambios en la Carta Gantt Maestra',
                                                user: currentUser?.name || currentUser?.email || 'Usuario'
                                            });
                                        }
                                        setIsMasterEditing(!isMasterEditing);
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all ${isMasterEditing ? 'bg-green-600 text-white border-green-600' : 'text-slate-600 border-slate-300'}`}
                                >
                                    <span className="material-symbols-outlined text-[20px]">{isMasterEditing ? 'save' : 'edit'}</span>
                                    {isMasterEditing ? 'Guardar Modificaciones' : 'Editar Carta Gantt'}
                                </button>
                                {isMasterEditing && (
                                    <button
                                        onClick={() => setCalendarModalOpen(true)}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-all shadow-sm"
                                        title="Configurar los días laborables y excepciones"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">calendar_today</span>
                                        Configuración de Calendario
                                    </button>
                                )}
                            </div>
                        )}
                        {hasPermission('pm_gantt_imp', 'edit') && (
                            <button
                                onClick={() => setImportModalOpen(true)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all"
                            >
                                <span className="material-symbols-outlined text-[20px]">upload_file</span>
                                Importar Carta Gantt
                            </button>
                        )}
                        <button
                            onClick={() => setExportModalOpen(true)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all ${!hasPermission("pm_gantt_exp", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                        >
                            <span className="material-symbols-outlined text-[20px]">download</span>
                            Exportar Carta Gantt
                        </button>
                        {hasPermission('pm_reprog', 'edit') && (
                            <button
                                onClick={() => setReprogrammingModalOpen(true)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-all"
                            >
                                <span className="material-symbols-outlined text-[20px]">update</span>
                            Cargar Reprogramación
                        </button>
                        )}
                        <button
                            onClick={() => setHistoryModalOpen(true)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all ${!hasPermission("pm_historial", "view") ? "opacity-50 pointer-events-none" : ""}`}
                        >
                            <span className="material-symbols-outlined text-[20px]">history</span>
                            Historial
                        </button>
                        
                        <div className="flex border border-slate-300 rounded-lg overflow-hidden ml-4 shadow-sm">
                            <button
                                onClick={() => setGraphicMode(false)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-bold transition-colors ${!graphicMode ? 'bg-blue-100 text-blue-700' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span className="material-symbols-outlined text-[18px]">table_chart</span>
                                Modo Carta Gantt
                            </button>
                            <button
                                onClick={() => setGraphicMode(true)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-bold transition-colors border-l border-slate-300 ${graphicMode ? 'bg-blue-100 text-blue-700' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span className="material-symbols-outlined text-[18px]">map</span>
                                Modo Avance Gráfico
                            </button>
                        </div>
                        <button
                            onClick={() => setSyncProgressModalOpen(true)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 transition-all ml-auto ${!hasPermission("pm_sync", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                        >
                            <span className="material-symbols-outlined text-[20px]">history_toggle_off</span>
                            Sincronizar Avance Semanal
                        </button>
                    </>
                ) : activeTab === 'intermedia' ? (
                    <div className="flex gap-2 w-full">
                        {!activeLookaheadId ? (
                            <>
                                <button
                                    onClick={() => setActiveLookaheadId(null)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all ${!hasPermission("pi_historial", "view") ? "opacity-50 pointer-events-none" : ""}`}
                                >
                                    <span className="material-symbols-outlined text-[20px]">history</span>
                                    Historial
                                </button>
                                <button
                                    onClick={() => setIsITEView(!isITEView)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all ${isITEView ? 'bg-amber-100 text-amber-700 border-amber-300' : 'border-slate-300 text-slate-600 hover:bg-slate-50'} ${!hasPermission("pi_ite", "view") ? "opacity-50 pointer-events-none" : ""}`}
                                >
                                    <span className="material-symbols-outlined text-[20px]">assignment_turned_in</span>
                                    I.T.E
                                </button>
                                <button
                                    onClick={handleOpenLookaheadModal}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-purple-600 text-white border border-purple-600 hover:bg-purple-700 transition-all ml-auto ${!hasPermission("pi_generar", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                >
                                    <span className="material-symbols-outlined text-[20px]">add_road</span>
                                    Generar Planificación Intermedia
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setIsITEView(!isITEView)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all ${isITEView ? 'bg-amber-100 text-amber-700 border-amber-300' : 'border-slate-300 text-slate-600 hover:bg-slate-50'} ${!hasPermission("pi_ite", "view") ? "opacity-50 pointer-events-none" : ""}`}
                                >
                                    <span className="material-symbols-outlined text-[20px]">assignment_turned_in</span>
                                    I.T.E
                                </button>
                                {activeLookahead?.status !== 'Cerrada' && (
                                    <button
                                        onClick={handleSaveLookaheadPlan}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-green-600 text-white border border-green-600 hover:bg-green-700 transition-all ml-auto ${!hasPermission("pi_generar", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                    >
                                        <span className="material-symbols-outlined text-[20px]">save</span>
                                        Guardar Planificación
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                ) : activeTab === 'semanal' && isWeeklyEditing ? (
                    <button
                        onClick={handleSaveWeeklyPlanFromToolbar}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-green-600 text-white border border-green-600 hover:bg-green-700 transition-all ${!hasPermission("ps_crear", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                    >
                        <span className="material-symbols-outlined text-[20px]">save</span>
                        Guardar Planificación
                    </button>
                ) : activeTab === 'semanal' && isBreakdownMode ? (
                    <button
                        onClick={() => setIsBreakdownMode(false)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-green-600 text-white border border-green-600 hover:bg-green-700 transition-all"
                    >
                        <span className="material-symbols-outlined text-[20px]">save</span>
                        Guardar Desglose
                    </button>
                ) : (
                    <div className="text-sm text-slate-500 italic flex items-center gap-2">
                        <span className="material-symbols-outlined text-lg">visibility</span>
                        Modo visualización
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden relative">
                {/* Left Side: Table (Task List) */}
                <div
                    id="planning-left-pane"
                    className={`
                        ${leftPaneWidth === null ? ((activeTab === 'semanal' || activeTab === 'intermedia') ? 'w-[35%]' : 'w-[45%]') : ''} 
                        hidden md:flex flex-col border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-[#101822] z-10 shadow-[4px_0_10px_rgba(0,0,0,0.02)] shrink-0
                    `}
                    style={leftPaneWidth !== null ? { width: `${leftPaneWidth}px`, minWidth: `${leftPaneWidth}px`, maxWidth: `${leftPaneWidth}px` } : undefined}
                >
                    <div 
                        className="flex-1 overflow-auto"
                        ref={leftScrollRef}
                        onScroll={handleLeftScroll}
                        onClick={() => setSelectedGanttTaskId(null)}
                    >
                        <table className="w-full text-left border-collapse min-w-[300px]">
                            <thead className="sticky top-0 z-10 bg-white dark:bg-[#101822] shadow-sm">
                                <tr className="border-b border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-400 uppercase tracking-wider h-[45px] whitespace-nowrap">
                                    <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-12 text-center align-middle">#</th>
                                    <th 
                                        id="planning-tarea-header"
                                        className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 align-middle relative group"
                                        style={tareaColumnWidth !== null ? { width: `${tareaColumnWidth}px`, minWidth: `${tareaColumnWidth}px`, maxWidth: `${tareaColumnWidth}px` } : undefined}
                                    >
                                        {activeTab === 'semanal' ? (
                                            <div className="flex flex-col gap-3 py-2">
                                                {!isWeeklyEditing && !isBreakdownMode && (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <button onClick={() => { setActivePlanId(null); setIsWeeklyEditing(false); }} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-colors normal-case font-medium text-xs ${activePlanId === null ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white border-slate-300 dark:border-slate-600' : 'border-transparent text-slate-500 hover:text-slate-700'} ${!hasPermission("ps_historial", "view") ? "opacity-50 pointer-events-none" : ""}`}>
                                                            <span className="material-symbols-outlined text-[16px]">history</span>
                                                            Historial
                                                        </button>
                                                        <button onClick={() => setNewPlanModalOpen(true)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-blue-600 transition-colors normal-case font-medium text-xs shadow-sm ${!hasPermission("ps_crear", "edit") ? "opacity-50 pointer-events-none" : ""}`}>
                                                            <span className="material-symbols-outlined text-[16px]">add</span>
                                                            Crear Planificación
                                                        </button>
                                                        <button onClick={handleBreakdownClick} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border border-primary text-primary hover:bg-primary/5 transition-colors normal-case font-medium text-xs shadow-sm ${!hasPermission("ps_desglose", "edit") ? "opacity-50 pointer-events-none" : ""}`}>
                                                            <span className="material-symbols-outlined text-[16px]">account_tree</span>
                                                            Desglose
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="flex items-center justify-between">
                                                    <span>Tarea {isBreakdownMode && <span className="text-amber-600 text-[10px] ml-2 font-bold">(Desglose)</span>} {isWeeklyEditing && <span className="text-primary text-[10px] ml-2 font-bold">(Planificando)</span>}</span>
                                                </div>
                                            </div>
                                        ) : activeTab === 'intermedia' ? (
                                            <div className="flex items-center justify-between">
                                                <span>Tarea (Análisis de Restricciones)</span>
                                                {activeLookaheadId && (
                                                    <button onClick={() => {
                                                        if (activeLookahead?.status === 'En Planificación') {
                                                            setExitWarningTarget('intermedia');
                                                            setShowExitWarningModal(true);
                                                        } else {
                                                            setActiveLookaheadId(null);
                                                        }
                                                    }} className="text-xs flex items-center gap-1 text-slate-500 hover:text-purple-600">
                                                        <span className="material-symbols-outlined text-[14px]">arrow_back</span> Volver a Lista
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between">
                                                <span>Tarea</span>
                                                {isMasterEditing && (
                                                    <button onClick={() => handleCreateFolderClick(null)} className="flex items-center gap-1 text-[10px] bg-primary/10 text-primary px-2 py-1 rounded hover:bg-primary/20">
                                                        <span className="material-symbols-outlined text-[14px]">create_new_folder</span>
                                                        Crear Carpeta
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        <div 
                                            onMouseDown={handleResizeMouseDown} 
                                            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary bg-slate-200/20 dark:bg-slate-700/20 transition-colors z-20"
                                            title="Arrastra para cambiar el ancho de la columna Tarea"
                                        />
                                    </th>
                                    {activeTab === 'semanal' || (activeTab === 'maestra' && !isMasterEditing) ? (
                                        <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 text-center w-32 align-middle">% Avance</th>
                                    ) : null}
                                    {activeTab !== 'semanal' && (
                                        <>
                                            <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 text-center w-24 align-middle">Duración</th>
                                            <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-36 align-middle border-r dark:border-slate-700">Fecha Inicio</th>
                                            <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-36 align-middle border-r dark:border-slate-700">Fecha Termino</th>
                                            {(isMasterEditing || flattenedTasks.some(t => (t.predecessors && t.predecessors.length > 0) || (t.successors && t.successors.length > 0))) && (
                                                <>
                                                    <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-24 align-middle border-r dark:border-slate-700">Predecesor</th>
                                                    <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-24 align-middle">Sucesor</th>
                                                </>
                                            )}
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {renderTableRows(processedTasks)}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Splitter Bar between Tasks and Gantt */}
                <div 
                    onMouseDown={handleSplitterMouseDown} 
                    className="hidden md:block w-1.5 hover:w-2 bg-slate-200/80 hover:bg-primary dark:bg-slate-700 dark:hover:bg-blue-500 cursor-col-resize transition-all shrink-0 z-20 border-l border-r border-slate-300 dark:border-slate-600"
                    title="Arrastra para ajustar la división"
                />

                {/* Right Side: Content Area (Gantt, Weekly Plan Detail, or Lookahead) */}
                <div 
                    className="flex-1 overflow-auto bg-slate-50 dark:bg-[#0d131b] relative gantt-scroll"
                    ref={rightScrollRef}
                    onScroll={handleRightScroll}
                    onClick={() => setSelectedGanttTaskId(null)}
                >
                    {activeTab === 'maestra' && graphicMode ? (
                        <div className="h-full w-full p-4 flex flex-col">
                            <div className="flex justify-between items-center mb-2 px-2">
                                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">Avance Gráfico</h2>
                                <button onClick={() => setGraphicMode(false)} className="px-3 py-1.5 border border-slate-300 rounded text-slate-600 text-xs font-bold hover:bg-slate-50">
                                    Cerrar Visor
                                </button>
                            </div>
                            <div className="flex-1 relative">
                                <GraphicProgressViewer 
                                    activeProjectId={projectInfo.id}
                                    tasks={processedTasks}
                                    units={units}
                                    levels={levels}
                                    matrixData={matrixData}
                                    protocolConfigs={protocolConfigs}
                                    projectInfo={projectInfo}
                                    savedProtocols={savedProtocols}
                                    weeklyPlans={weeklyPlans}
                                    selectedTaskId={graphicSelectedTaskId}
                                    onClose={() => setGraphicMode(false)}
                                />
                            </div>
                        </div>
                    ) : activeTab === 'intermedia' ? (
                        <div className="h-full p-2 md:p-6 flex flex-col">
                            {activeLookaheadId ? (
                                // Lookahead Plan Detail (Constraints Analysis)
                                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 flex-1 min-h-0 flex flex-col overflow-hidden">
                                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-purple-50/50 dark:bg-purple-900/10">
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <div className="flex items-center gap-2 md:gap-3">
                                                            <button 
                                                                onClick={() => {
                                                                    if (activeLookahead?.status !== 'Cerrada') {
                                                                        setExitWarningTarget('intermedia');
                                                                        setShowExitWarningModal(true);
                                                                    } else {
                                                                        setActiveLookaheadId(null);
                                                                    }
                                                                }} 
                                                                className="text-slate-400 hover:text-slate-600 flex items-center justify-center"
                                                            >
                                                                    <span className="material-symbols-outlined text-[20px] md:text-[24px]">arrow_back</span>
                                                            </button>
                                                            <span className="text-purple-600 font-bold text-sm uppercase tracking-wide">Planificación Intermedia</span>
                                                            <span className="text-slate-400 text-xs">•</span>
                                                            <span className="text-slate-600 dark:text-slate-300 font-bold text-sm">Semana: {activeLookahead?.startDate} al {activeLookahead?.endDate}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 ml-7 md:ml-9">
                                                            <p className="text-xs text-slate-500">Análisis de restricciones para tareas proyectadas a {activeLookahead?.weeksAhead} semanas.</p>
                                                            <span className="text-slate-400 text-xs">•</span>
                                                            <span className={`text-xs font-bold ${
                                                                activeLookahead?.status === 'En Planificación' ? 'text-blue-600' :
                                                                activeLookahead?.status === 'En Ejecución' ? 'text-green-600' : 
                                                                activeLookahead?.status === 'En Revisión' ? 'text-amber-500' : 
                                                                'text-slate-600'
                                                            }`}>Estado: {activeLookahead?.status}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        {/* En Planificación */}
                                                        {activeLookahead?.status === 'En Planificación' && (
                                                            <button 
                                                                onClick={() => handlePublishLookahead(activeLookahead.id)} 
                                                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">send</span>
                                                                <span>Publicar Planificación</span>
                                                            </button>
                                                        )}

                                                        {/* En Ejecución */}
                                                        {activeLookahead?.status === 'En Ejecución' && (
                                                            <>
                                                                <button 
                                                                    onClick={() => handleStartLookaheadReview(activeLookahead.id)} 
                                                                    className="bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded text-xs font-bold transition-colors"
                                                                >
                                                                    Revisar Planificación Intermedia
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleRevertLookaheadToDraft(activeLookahead.id)} 
                                                                    className="bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">edit_note</span>
                                                                    <span>Editar Planificación</span>
                                                                </button>
                                                            </>
                                                        )}

                                                        {/* En Revisión */}
                                                        {activeLookahead?.status === 'En Revisión' && (
                                                            <>
                                                                <button 
                                                                    onClick={() => handleBackToLookaheadExecution(activeLookahead.id)} 
                                                                    className="bg-white text-slate-600 hover:bg-slate-100 border border-slate-300 px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                                                    <span>Volver a Ejecución</span>
                                                                </button>
                                                                {hasMissingLookaheadComments && (
                                                                    <div className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-[10px] md:text-xs font-bold flex items-center gap-1 animate-pulse border border-red-200">
                                                                        <span className="material-symbols-outlined text-[14px]">warning</span>
                                                                        <span>Faltan comentarios de justificación</span>
                                                                    </div>
                                                                )}
                                                                <button 
                                                                    onClick={() => handleCloseLookahead(activeLookahead.id)} 
                                                                    disabled={hasMissingLookaheadComments}
                                                                    className={`px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1 border ${
                                                                        hasMissingLookaheadComments 
                                                                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                                            : 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200'
                                                                    }`}
                                                                    title={hasMissingLookaheadComments ? "No se puede finalizar la revisión porque hay restricciones sin liberar que no cuentan con observaciones/justificaciones de incumplimiento" : "Finalizar Revisión"}
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                                    <span>Finalizar Revisión</span>
                                                                </button>
                                                            </>
                                                        )}

                                                        {/* Cerrada */}
                                                        {activeLookahead?.status === 'Cerrada' && (
                                                            <button 
                                                                onClick={() => handleReopenLookahead(activeLookahead.id)} 
                                                                className="bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">lock_open</span>
                                                                <span>Volver a Abrir</span>
                                                            </button>
                                                        )}

                                                        {/* Exportar (Oculto en En Planificación) */}
                                                        {activeLookahead?.status !== 'En Planificación' && (
                                                            <button onClick={() => exportLookaheadToExcel(activeLookahead.id)} className="px-3 py-1.5 border border-green-300 bg-green-50 text-green-700 rounded text-xs font-bold hover:bg-green-100 flex items-center gap-1">
                                                                <span className="material-symbols-outlined text-[16px]">download</span>
                                                                <span>Exportar tareas con Restricciones</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                    <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950">
                                        <table className="hidden md:table w-full text-left border-collapse">
                                            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                                                <tr>
                                                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Tarea Vigente</th>
                                                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-32">Inicio</th>
                                                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-32">Termino</th>
                                                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Restricciones Identificadas</th>
                                                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-32 text-center">No Aplica Restricción</th>
                                                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-10"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                {(() => {
                                                    if (!activeLookahead) return null;
                                                    let tasksInWindow = getTasksForLookahead(activeLookahead.startDate, activeLookahead.endDate);

                                                    // Filter for ITE Mode
                                                    if (isITEView) {
                                                        tasksInWindow = tasksInWindow.filter(task => {
                                                            const isCleared = activeLookahead.clearedTaskIds?.includes(task.id);
                                                            const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                                            const hasConstraints = constraints.length > 0;
                                                            const allConstraintsReleased = hasConstraints && constraints.every(c => c.status === 'Liberado');

                                                            // Task is executable if manually cleared OR has all constraints released
                                                            return isCleared || allConstraintsReleased;
                                                        });
                                                    }

                                                    if (tasksInWindow.length === 0) {
                                                        return <tr><td colSpan={6} className="p-8 text-center text-slate-400 italic">No hay tareas programadas para esta semana futura (o no cumplen criterio I.T.E).</td></tr>;
                                                    }

                                                    return tasksInWindow.map(task => {
                                                        const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                                        const isCleared = activeLookahead.clearedTaskIds?.includes(task.id);

                                                        return (
                                                            <React.Fragment key={task.id}>
                                                                <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                                                                    <td className="px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-200">{task.name}</td>
                                                                    <td className="px-4 py-3 text-xs text-slate-500">{task.start}</td>
                                                                    <td className="px-4 py-3 text-xs text-slate-500">{task.end}</td>
                                                                    <td className="px-4 py-3">
                                                                        <button
                                                                            onClick={() => handleOpenAddConstraintModal(task.id)}
                                                                            disabled={isCleared || activeLookahead.status === 'Cerrada'}
                                                                            className={`text-xs font-bold flex items-center gap-1 ${isCleared || activeLookahead.status === 'Cerrada' ? 'text-slate-300 cursor-not-allowed' : 'text-purple-600 hover:text-purple-700'}`}
                                                                        >
                                                                            <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                                                            Agregar Restricción
                                                                        </button>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={!!isCleared}
                                                                            disabled={activeLookahead.status === 'Cerrada'}
                                                                            onChange={() => handleToggleTaskCleared(activeLookahead.id, task.id)}
                                                                            className="rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer size-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        />
                                                                    </td>
                                                                    <td></td>
                                                                </tr>
                                                                {constraints.length > 0 && (
                                                                    <tr className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                                                                        <td colSpan={6} className="p-0">
                                                                            <div className="pl-4 pr-4 py-2 bg-slate-50/50 dark:bg-slate-800/20">
                                                                                <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
                                                                                    <table className="w-full text-left">
                                                                                        <thead className="bg-slate-100 dark:bg-slate-700/50 text-[10px] uppercase text-slate-500 font-bold tracking-wider border-b border-slate-200 dark:border-slate-700">
                                                                                            <tr>
                                                                                                <th className="px-3 py-2 w-32">Tipo</th>
                                                                                                <th className="px-3 py-2 w-48">Responsable</th>
                                                                                                <th className="px-3 py-2 w-32">Fecha Requerida</th>
                                                                                                <th className="px-3 py-2">Detalle</th>
                                                                                                <th className="px-3 py-2 w-48 text-center">Unidades Físicas</th>
                                                                                                <th className="px-3 py-2 w-32 text-center">Estado</th>
                                                                                                <th className="px-3 py-2 w-10"></th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                                                            {constraints.map(c => {
                                                                                                let typeName = 'General';
                                                                                                constraintGroups.forEach(g => {
                                                                                                    const def = g.definitions.find(d => d.id === c.typeId);
                                                                                                    if (def) typeName = def.name;
                                                                                                });
                                                                                                const respName = responsibles.find(r => r.id === c.responsibleId)?.name || 'Sin asignar';

                                                                                                return (
                                                                                                    <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                                                                                        <td className="px-3 py-2">
                                                                                                            <span className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-[10px] font-bold px-2 py-0.5 rounded border border-purple-200 dark:border-purple-800 truncate block text-center">
                                                                                                                {typeName}
                                                                                                            </span>
                                                                                                        </td>
                                                                                                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300 truncate" title={respName}>
                                                                                                            {respName}
                                                                                                        </td>
                                                                                                        <td className="px-3 py-2">
                                                                                                            <input
                                                                                                                type="date"
                                                                                                                disabled={activeLookahead.status === 'Cerrada'}
                                                                                                                className={`text-xs border border-slate-300 dark:border-slate-600 rounded p-1 w-full text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 focus:ring-1 focus:ring-purple-500 outline-none ${activeLookahead.status === 'Cerrada' ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                                                                                value={c.requiredDate}
                                                                                                                onChange={(e) => handleUpdateConstraint(activeLookahead.id, c.id, 'requiredDate', e.target.value)}
                                                                                                            />
                                                                                                        </td>
                                                                                                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                                                                                                            {c.description}
                                                                                                        </td>
                                                                                                        <td className="px-3 py-2 text-xs text-center">
                                                                                                            {(!c.unitsType || c.unitsType === 'na') ? (
                                                                                                                <span className="text-slate-400 italic">No aplica Unidades físicas</span>
                                                                                                            ) : (
                                                                                                                <button
                                                                                                                    onClick={() => { setActiveConstraintForUnits(c.id); setUnitsViewModalOpen(true); }}
                                                                                                                    className="text-primary hover:underline font-bold"
                                                                                                                >
                                                                                                                    Si aplica a Unidades Fisicas
                                                                                                                </button>
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td className="px-3 py-2 text-center">
                                                                                                            <select
                                                                                onChange={(e) => {
                                                                                    const newVal = e.target.value;
                                                                                    if (newVal === 'Liberado' || newVal === 'NO LIBERADO') {
                                                                                        setConstraintObsData({ planId: activeLookahead.id, constraintId: c.id, currentStatus: newVal, observations: c.observations || '', description: c.description });
                                                                                        setConstraintObsModalOpen(true);
                                                                                    } else {
                                                                                        handleUpdateConstraint(activeLookahead.id, c.id, 'status', newVal);
                                                                                        handleUpdateConstraint(activeLookahead.id, c.id, 'observations', '');
                                                                                    }
                                                                                }}
                                                                                disabled={activeLookahead.status === 'Cerrada' || !(() => {
                                                                                    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
                                                                                    const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                                    return isAdmin || isAssigned;
                                                                                })()}
                                                                                className={`w-full text-[10px] border rounded p-1 outline-none font-bold uppercase ${
                                                                                    (activeLookahead.status !== 'Cerrada' && (() => {
                                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
                                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                                        return isAdmin || isAssigned;
                                                                                    })()) ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                                                                                } ${c.status === 'Liberado' ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'}`}
                                                                                value={c.status}
                                                                            >
                                                                                <option value="Pendiente">Pendiente</option>
                                                                                <option value="Liberado">Liberado</option>
                                                                                <option value="NO LIBERADO">NO LIBERADO</option>
                                                                            </select>
                                                                                                        </td>
                                                                                                        <td className="px-3 py-2 text-center">
                                                                                                            <button 
                                                                                                                onClick={() => handleDeleteConstraint(activeLookahead.id, c.id)} 
                                                                                                                disabled={activeLookahead.status === 'Cerrada'}
                                                                                                                className={`text-slate-400 hover:text-red-500 ${activeLookahead.status === 'Cerrada' ? 'opacity-30 cursor-not-allowed' : ''}`}
                                                                                                            >
                                                                                                                <span className="material-symbols-outlined text-[16px]">delete</span>
                                                                                                            </button>
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </table>

                                        {/* --- VISTA MÓVIL (PLANIFICACIÓN INTERMEDIA / ITE) --- */}
                                        <div className="block md:hidden p-4 space-y-4 pb-24">
                                            {/* Filtro Móvil ITE */}
                                            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                                                <button 
                                                    onClick={() => setMobileFilterITE('all')}
                                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterITE === 'all' ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">public</span> Todas
                                                </button>
                                                <button 
                                                    onClick={() => setMobileFilterITE('mine')}
                                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterITE === 'mine' ? 'bg-primary text-white shadow flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">person</span> Mías
                                                </button>
                                            </div>

                                            {(() => {
                                                if (!activeLookahead) return null;
                                                let tasksInWindow = getTasksForLookahead(activeLookahead.startDate, activeLookahead.endDate);

                                                if (isITEView) {
                                                    tasksInWindow = tasksInWindow.filter(task => {
                                                        const isCleared = activeLookahead.clearedTaskIds?.includes(task.id);
                                                        const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                                        const hasConstraints = constraints.length > 0;
                                                        const allConstraintsReleased = hasConstraints && constraints.every(c => c.status === 'Liberado');
                                                        return isCleared || allConstraintsReleased;
                                                    });
                                                }

                                                if (tasksInWindow.length === 0) {
                                                    return <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">No hay tareas programadas para esta semana futura.</div>;
                                                }

                                                // Extract all constraints mapped with tasks
                                                let mobileConstraints = tasksInWindow.flatMap(task => {
                                                    const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                                    return constraints.map(c => ({ task, constraint: c }));
                                                });
                                                
                                                if (mobileFilterITE === 'mine' && currentUser) {
                                                    mobileConstraints = mobileConstraints.filter(mc => {
                                                        const resp = responsibles.find(r => r.id === mc.constraint.responsibleId);
                                                        return resp && resp.email === currentUser.email;
                                                    });
                                                }

                                                if(mobileConstraints.length === 0){
                                                     return <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">No se han registrado restricciones en las tareas vigentes, o no tienes restricciones asignadas.</div>;
                                                }

                                                return mobileConstraints.map(({ task, constraint: c }) => {
                                                    let typeName = 'General';
                                                    constraintGroups.forEach(g => {
                                                        const def = g.definitions.find(d => d.id === c.typeId);
                                                        if (def) typeName = def.name;
                                                    });
                                                    const respName = responsibles.find(r => r.id === c.responsibleId)?.name || 'Sin asignar';
                                                    const isLiberado = c.status === 'Liberado';

                                                        return (<div key={c.id} className={`rounded-xl shadow-sm flex items-center justify-between p-3 gap-3 border transition-colors ${
                                                            isLiberado 
                                                            ? 'bg-[#52be80] border-[#229954] text-slate-900 dark:bg-[#27ae60] dark:text-white dark:border-[#1e8449]' 
                                                            : 'bg-[#85c1e9] border-[#2e86c1] text-slate-900 dark:bg-[#3498db] dark:text-white dark:border-[#2874a6]'
                                                        }`}>
                                                            <div className="flex-1 text-[11px] font-bold leading-snug">
                                                                {task.name} | {c.requiredDate} | {typeName} | {c.description} | {respName}
                                                            </div>
                                                            <div className="flex items-center gap-1.5 shrink-0">
                                                                <button 
                                                                    onClick={() => {
                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                        if (!isAdmin && !isAssigned) return;

                                                                        if (!isLiberado) {
                                                                            setConstraintObsData({ planId: activeLookahead.id, constraintId: c.id, currentStatus: 'Liberado', observations: c.observations || '', description: c.description });
                                                                            setConstraintObsModalOpen(true);
                                                                        } else {
                                                                            handleUpdateConstraint(activeLookahead.id, c.id, 'status', 'Pendiente');
                                                                            handleUpdateConstraint(activeLookahead.id, c.id, 'observations', '');
                                                                        }
                                                                    }}
                                                                    disabled={activeLookahead.status === 'Cerrada' || !(() => {
                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                        return isAdmin || isAssigned;
                                                                    })()}
                                                                    className={`flex items-center justify-center font-black text-[10px] w-8 h-8 rounded shrink-0 border uppercase transition-colors ${
                                                                        (activeLookahead.status !== 'Cerrada' && (() => {
                                                                            const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
                                                                            const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                            return isAdmin || isAssigned;
                                                                        })()) ? 'active:scale-95' : 'opacity-50 cursor-not-allowed'
                                                                    } ${
                                                                        isLiberado 
                                                                        ? 'bg-[#d5f5e3] border-[#229954] text-[#1e8449] hover:bg-[#abebc6]' 
                                                                        : 'bg-[#d6eaf8] border-[#2e86c1] text-[#2874a6] hover:bg-[#aed6f1]'
                                                                    }`}
                                                                >
                                                                    {isLiberado ? 'SI' : 'NO'}
                                                                </button>
                                                                <button className="flex items-center justify-center w-8 h-8 rounded shrink-0 hover:bg-black/10 transition-colors">
                                                                    <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>chat_bubble</span>
                                                                </button>
                                                            </div>
                                                        </div>);
                                                });
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Lookahead History View
                                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 flex-1 min-h-0 flex flex-col overflow-hidden">
                                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex justify-between items-center">
                                        <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">Historial Planificación Intermedia</h3>
                                        <div className="text-xs text-slate-500">Last Planner System</div>
                                    </div>
                                    <div className="flex-1 overflow-auto">
                                        {lookaheadPlans.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                                <span className="material-symbols-outlined text-4xl mb-2">calendar_month</span>
                                                <p className="text-sm">No hay planificaciones intermedias generadas.</p>
                                                <p className="text-xs mt-2 max-w-xs text-center">Utilice el botón "Generar Planificación Intermedia" para proyectar restricciones futuras.</p>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="hidden md:table w-full text-left border-collapse">
                                                    <thead className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                                        <tr>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">N° Planificación</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Semana Objetivo</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Horizonte</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Estado</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Restricciones</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-center">PCR</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right">Ver</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                        {[...lookaheadPlans].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map((plan, index) => {
                                                            const pendingConstraints = plan.constraints.filter(c => c.status === 'Pendiente').length;
                                                            const notLiberadoConstraints = plan.constraints.filter(c => c.status === 'NO LIBERADO').length;
                                                            const totalConstraints = plan.constraints.length;
                                                            const closedConstraints = plan.constraints.filter(c => c.status === 'Liberado').length;
                                                            const pcr = totalConstraints > 0 ? Math.round((closedConstraints / totalConstraints) * 100) : 100;
                                                            
                                                            return (
                                                                <tr key={plan.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                                    <td className="px-6 py-5">
                                                                        <span className="font-bold text-sm text-purple-700 dark:text-purple-400">Planificación Intermedia #{index + 1}</span>
                                                                    </td>
                                                                    <td className="px-6 py-5">
                                                                        <div className="flex flex-col">
                                                                            <span className="font-bold text-sm text-slate-700 dark:text-slate-300">{plan.startDate}</span>
                                                                            <span className="text-xs text-slate-500">al {plan.endDate}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-5 text-sm text-slate-600 dark:text-slate-400">
                                                                        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-bold">Vista a {plan.weeksAhead} semanas</span>
                                                                    </td>
                                                                    <td className="px-6 py-5">
                                                                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold shadow-sm ${
                                                                            plan.status === 'En Planificación' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                                            plan.status === 'En Ejecución' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                                            plan.status === 'En Revisión' ? 'bg-yellow-400 text-slate-900 border border-yellow-500/20' :
                                                                            'bg-slate-300 text-slate-600 border border-slate-400/20'
                                                                        }`}>
                                                                            {plan.status === 'Cerrada' ? 'Cerrado' : plan.status}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-6 py-5">
                                                                        {pendingConstraints > 0 || notLiberadoConstraints > 0 ? (
                                                                            <div className="flex flex-col gap-1">
                                                                                {pendingConstraints > 0 && (
                                                                                    <span className="text-amber-600 font-bold text-xs flex items-center gap-1">
                                                                                        <span className="material-symbols-outlined text-[16px]">warning</span>
                                                                                        {pendingConstraints} Pendientes
                                                                                    </span>
                                                                                )}
                                                                                {notLiberadoConstraints > 0 && (
                                                                                    <span className="text-red-600 font-bold text-xs flex items-center gap-1">
                                                                                        <span className="material-symbols-outlined text-[16px]">cancel</span>
                                                                                        {notLiberadoConstraints} No Liberadas
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-green-600 font-bold text-xs flex items-center gap-1">
                                                                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                                                Todo Liberado
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-5 text-center">
                                                                        <span className="font-bold text-sm text-slate-700 dark:text-slate-300">{pcr}%</span>
                                                                    </td>
                                                                    <td className="px-6 py-5 text-right flex justify-end">
                                                                        <button
                                                                            onClick={() => {
                                                                                setEditingLookaheadId(plan.id);
                                                                                setLookaheadRange({ start: plan.startDate, end: plan.endDate });
                                                                                setLookaheadModalOpen(true);
                                                                            }}
                                                                            disabled={plan.status === 'Cerrada'}
                                                                            className={`text-slate-400 hover:text-purple-600 transition-colors p-2 hover:bg-slate-100 rounded-full ${plan.status === 'Cerrada' ? 'opacity-30 cursor-not-allowed hover:text-slate-400 hover:bg-transparent' : ''}`}
                                                                            title="Editar fechas de la planificación"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[20px]">edit_calendar</span>
                                                                        </button>
                                                                        <button
                                                                            onClick={() => {
                                                                                setLookaheadPlansBackup(JSON.parse(JSON.stringify(lookaheadPlans)));
                                                                                setActiveLookaheadId(plan.id);
                                                                            }}
                                                                            className="text-slate-400 hover:text-purple-600 transition-colors p-2 hover:bg-slate-100 rounded-full"
                                                                            title="Ver Plan"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[22px]">search</span>
                                                                        </button>
                                                                        {(hasPermission('pi_eliminar', 'view') || hasPermission('pi_eliminar', 'edit')) && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    if (hasPermission('pi_eliminar', 'edit')) {
                                                                                        handleDeleteLookahead(plan.id);
                                                                                    }
                                                                                }}
                                                                                disabled={!hasPermission('pi_eliminar', 'edit') || plan.status === 'Cerrada'}
                                                                                className={`p-2 rounded-full transition-colors ${
                                                                                    (!hasPermission('pi_eliminar', 'edit') || plan.status === 'Cerrada')
                                                                                    ? 'text-slate-200 cursor-not-allowed dark:text-slate-800'
                                                                                    : 'text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20'
                                                                                }`}
                                                                                title={plan.status === 'Cerrada' ? "Planificación cerrada (No se puede eliminar)" : !hasPermission('pi_eliminar', 'edit') ? "Eliminar Programación (Solo Lectura)" : "Eliminar Programación"}
                                                                            >
                                                                                <span className="material-symbols-outlined text-[20px]">delete</span>
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                <div className="block md:hidden p-4 space-y-3 pb-24">
                                                    {[...lookaheadPlans].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map((plan, index) => {
                                                        const pendingConstraints = plan.constraints.filter(c => c.status === 'Pendiente').length;
                                                        const notLiberadoConstraints = plan.constraints.filter(c => c.status === 'NO LIBERADO').length;
                                                        
                                                        return (
                                                            <div key={plan.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-3 flex flex-col gap-2">
                                                                <div className="flex justify-between items-start">
                                                                    <div>
                                                                        <h4 className="font-bold text-purple-700 dark:text-purple-400 text-sm">Planificación #{index + 1}</h4>
                                                                        <p className="text-xs text-slate-500">{plan.startDate} al {plan.endDate}</p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        {pendingConstraints > 0 || notLiberadoConstraints > 0 ? (
                                                                            <div className="flex flex-col items-end gap-1">
                                                                                {pendingConstraints > 0 && (
                                                                                    <span className="text-amber-600 font-bold text-xs flex items-center gap-1">
                                                                                        <span className="material-symbols-outlined text-[14px]">warning</span> {pendingConstraints} Pend.
                                                                                    </span>
                                                                                )}
                                                                                {notLiberadoConstraints > 0 && (
                                                                                    <span className="text-red-600 font-bold text-xs flex items-center gap-1">
                                                                                        <span className="material-symbols-outlined text-[14px]">cancel</span> {notLiberadoConstraints} No Lib.
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-green-600 font-bold text-xs flex items-center gap-1 justify-end">
                                                                                <span className="material-symbols-outlined text-[14px]">check_circle</span> Liberado
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-2 mt-2">
                                                                    <button
                                                                        onClick={() => {
                                                                            setLookaheadPlansBackup(JSON.parse(JSON.stringify(lookaheadPlans)));
                                                                            setActiveLookaheadId(plan.id);
                                                                        }}
                                                                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2.5 rounded-lg text-xs uppercase flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px]">touch_app</span> Ver / Editar Plan
                                                                    </button>
                                                                    {(hasPermission('pi_eliminar', 'view') || hasPermission('pi_eliminar', 'edit')) && (
                                                                        <button
                                                                            onClick={() => {
                                                                                if (hasPermission('pi_eliminar', 'edit')) {
                                                                                    handleDeleteLookahead(plan.id);
                                                                                }
                                                                            }}
                                                                            disabled={!hasPermission('pi_eliminar', 'edit') || plan.status === 'Cerrada'}
                                                                            className={`px-3 py-2.5 rounded-lg border flex items-center justify-center transition-all ${
                                                                                (!hasPermission('pi_eliminar', 'edit') || plan.status === 'Cerrada')
                                                                                ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed dark:bg-slate-800 dark:border-slate-800 opacity-50'
                                                                                : 'bg-red-50 border-red-200 text-red-600 hover:bg-red-500 hover:text-white'
                                                                            }`}
                                                                            title={plan.status === 'Cerrada' ? "Planificación cerrada (No se puede eliminar)" : !hasPermission('pi_eliminar', 'edit') ? "Eliminar (Solo Lectura)" : "Eliminar"}
                                                                        >
                                                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : activeTab === 'semanal' ? (
                        <div className="h-full p-2 md:p-6 flex flex-col">
                            {/* ... (Existing Weekly Plan View Code) ... */}
                            {activePlanId ? (
                                // Plan Detail View
                                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 flex-1 min-h-0 flex flex-col overflow-hidden">
                                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                            <div className="w-full md:w-auto">
                                                <div className="flex items-center gap-2 md:gap-3">
                                                    <button 
                                                        onClick={() => {
                                                            if (isWeeklyEditing) {
                                                                setExitWarningTarget('semanal');
                                                                setShowExitWarningModal(true);
                                                            } else {
                                                                setActivePlanId(null);
                                                            }
                                                        }} 
                                                        className="text-slate-400 hover:text-slate-600"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px] md:text-[24px]">arrow_back</span>
                                                    </button>
                                                    <h2 className="text-base md:text-lg font-bold text-slate-800 dark:text-white">Planificación #{activePlan?.id}</h2>
                                                </div>
                                                <div className="flex items-center mt-1 text-xs md:text-sm ml-7 md:ml-9">
                                                    <p className="text-slate-500 dark:text-slate-400">Fecha: <span className="font-medium text-slate-900 dark:text-white">{activePlan?.startDate}</span> - <span className="font-medium text-slate-900 dark:text-white">{activePlan?.endDate}</span></p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3 md:gap-4 w-full md:w-auto justify-end">
                                                <div className="text-right">
                                                    <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Estado</p>
                                                    <p className={`text-xs md:text-sm font-bold ${
                                                        activePlan?.status === 'En Planificación' ? 'text-blue-600' :
                                                        activePlan?.status === 'En Ejecución' ? 'text-green-600' : 
                                                        activePlan?.status === 'En Revisión' ? 'text-amber-500' : 
                                                        'text-slate-600'
                                                    }`}>{activePlan?.status}</p>
                                                </div>

                                                {hasMissingCNC && (
                                                    <div className="bg-red-100 text-red-700 px-2 py-1 md:px-3 rounded-full text-[10px] md:text-xs font-bold flex items-center gap-1 animate-pulse border border-red-200">
                                                        <span className="material-symbols-outlined text-[14px] md:text-[16px]">warning</span>
                                                        <span className="hidden md:inline">Falta completar C.N.C</span>
                                                        <span className="inline md:hidden">C.N.C pendiente</span>
                                                    </div>
                                                )}

                                                {/* En Planificación */}
                                                {activePlan?.status === 'En Planificación' && (
                                                    <>
                                                        {!isMobile && (hasPermission('ps_gantt_avance', 'view') || hasPermission('ps_gantt_avance', 'edit')) && (
                                                            <button 
                                                                onClick={toggleGanttSuggestions}
                                                                className={`px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-all border flex items-center gap-1.5 ${
                                                                    !hasPermission('ps_gantt_avance', 'edit')
                                                                        ? 'bg-slate-100/70 dark:bg-slate-800/30 text-slate-400 border-slate-200 dark:border-slate-800 cursor-pointer opacity-75 shadow-none'
                                                                        : showGanttSuggestions 
                                                                            ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600 shadow-md animate-pulse' 
                                                                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200'
                                                                }`}
                                                                title={!hasPermission('ps_gantt_avance', 'edit') ? "Visualizar sugerencias (Solo Lectura)" : "Avance por Carta Gantt"}
                                                            >
                                                                <span className="material-symbols-outlined text-[16px] md:text-[18px]">
                                                                    {!hasPermission('ps_gantt_avance', 'edit') ? 'visibility' : 'auto_awesome'}
                                                                </span>
                                                                <span>Avance por Carta Gantt</span>
                                                                {!hasPermission('ps_gantt_avance', 'edit') && (
                                                                    <span className="text-[9px] uppercase font-black bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded text-slate-500">Solo Ver</span>
                                                                )}
                                                            </button>
                                                        )}
                                                        {!isMobile && (
                                                            <button
                                                                onClick={() => {
                                                                    setAdditionalModalData({
                                                                        index: null,
                                                                        levelId: '',
                                                                        unitId: '',
                                                                        additionalTaskName: '',
                                                                        responsibleId: '',
                                                                        days: { L: false, M: false, X: false, J: false, V: false, S: false }
                                                                    });
                                                                    setAdditionalModalOpen(true);
                                                                }}
                                                                className="bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1.5"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px] md:text-[18px]">add_circle</span>
                                                                <span>Tareas adicionales</span>
                                                            </button>
                                                        )}
                                                        <button 
                                                            onClick={() => handlePublishPlan(activePlan.id)} 
                                                            className="bg-green-600 hover:bg-green-700 text-white px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px] md:text-[18px]">send</span>
                                                            <span>Publicar Planificación</span>
                                                        </button>
                                                    </>
                                                )}

                                                {/* En Ejecución */}
                                                {activePlan?.status === 'En Ejecución' && (
                                                    <>
                                                        <button 
                                                            onClick={() => handleStartReview(activePlan.id)} 
                                                            className="bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors"
                                                        >
                                                            Revisar Planificación Semanal
                                                        </button>
                                                        <button 
                                                            onClick={() => handleRevertToDraft(activePlan.id)} 
                                                            className="bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px] md:text-[18px]">edit_note</span>
                                                            <span>Editar Planificación</span>
                                                        </button>
                                                    </>
                                                )}

                                                {/* En Revisión */}
                                                {activePlan?.status === 'En Revisión' && (
                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleBackToExecution(activePlan.id)} className="bg-white text-slate-600 hover:bg-slate-100 border border-slate-300 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1">
                                                            <span className="material-symbols-outlined text-[16px] md:text-[18px]">arrow_back</span>
                                                            <span className="hidden md:inline">Volver a Ejecución</span>
                                                        </button>
                                                        <button onClick={() => handleSaveReview(activePlan.id)} className="bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1">
                                                            <span className="material-symbols-outlined text-[16px] md:text-[18px]">save</span>
                                                            <span className="hidden md:inline">Guardar</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleFinalClosePlan(activePlan.id)}
                                                            disabled={hasMissingCNC}
                                                            className={`
                                                                px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-all border flex items-center gap-1
                                                                ${hasMissingCNC
                                                                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                                    : 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200 hover:shadow-sm'
                                                                }
                                                            `}
                                                        >
                                                            <span className="material-symbols-outlined text-[16px] md:text-[18px]">check_circle</span>
                                                            Finalizar Revisión
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Cerrada */}
                                                {activePlan?.status === 'Cerrada' && (
                                                    <button 
                                                        onClick={() => handleReopenPlan(activePlan.id)} 
                                                        className="bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px] md:text-[18px]">lock_open</span>
                                                        <span>Volver a Abrir</span>
                                                    </button>
                                                )}

                                                {/* Botón de Exportar (Oculto en En Planificación) */}
                                                {activePlan?.status !== 'En Planificación' && (
                                                    <button
                                                        onClick={() => exportWeeklyPlanToExcel(activePlan!.id)}
                                                        className="bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px] md:text-[18px]">download</span>
                                                        <span>Exportar las Tareas</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950">
                                        {showGanttSuggestions && activePlan && (hasPermission('ps_gantt_avance', 'view') || hasPermission('ps_gantt_avance', 'edit')) && (
                                            <div className="p-4 bg-blue-50/75 dark:bg-blue-950/20 border-b border-blue-200 dark:border-blue-900/50 animate-in slide-in-from-top-4 duration-300">
                                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                                                    <div className="flex items-center gap-2">
                                                        <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-[20px] md:text-[24px]">auto_awesome</span>
                                                        <div>
                                                            <h3 className="text-xs md:text-sm font-extrabold text-blue-800 dark:text-blue-400 uppercase tracking-wide">
                                                                Partidas Sugeridas por Carta Gantt
                                                            </h3>
                                                            <p className="text-[10px] md:text-xs text-blue-600/80 dark:text-blue-400/80 font-medium">
                                                                Programadas según Carta Gantt para esta semana y pendientes de asignación.
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={handleOpenAutoPlanModal}
                                                        disabled={!hasPermission('ps_gantt_avance', 'edit')}
                                                        className={`px-3 md:px-4 py-2 text-[11px] font-black uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 self-stretch sm:self-auto justify-center ${
                                                            !hasPermission('ps_gantt_avance', 'edit')
                                                                ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 border border-slate-300 dark:border-slate-700 cursor-not-allowed opacity-50 shadow-none pointer-events-none'
                                                                : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white shadow-sm hover:shadow active:scale-95'
                                                        }`}
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">checklist</span>
                                                        Auto-Programar Sugeridas
                                                    </button>
                                                </div>
                                                
                                                {(() => {
                                                    const suggestedTasks = flattenedTasks.filter(t => {
                                                        if (t.type !== 'task' || (t.children && t.children.length > 0)) return false;
                                                        return t.start && t.end && t.start <= activePlan.endDate && t.end >= activePlan.startDate;
                                                    });
                                                    
                                                    if (suggestedTasks.length === 0) {
                                                        return (
                                                            <div className="flex items-center gap-2 text-xs text-slate-500 italic p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                                                                <span className="material-symbols-outlined text-slate-400 text-[18px]">info</span>
                                                                No hay partidas proyectadas en la Carta Gantt para el rango de esta semana.
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    return (
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                            {suggestedTasks.map(t => {
                                                                const isAlreadyScheduled = activePlan.allocations.some(a => a.taskId === t.id);
                                                                return (
                                                                    <div 
                                                                        key={t.id} 
                                                                        className={`p-3 rounded-xl border flex justify-between items-center gap-3 transition-all ${
                                                                            isAlreadyScheduled 
                                                                                ? 'bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-900/30' 
                                                                                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-500 shadow-sm'
                                                                        }`}
                                                                    >
                                                                        <div className="min-w-0 flex-1">
                                                                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block truncate" title={t.name}>
                                                                                {t.name}
                                                                            </span>
                                                                            <span className="text-[10px] text-slate-400 dark:text-slate-500 block mt-0.5 font-medium">
                                                                                Proyectada: {t.start} al {t.end}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex-shrink-0">
                                                                            {isAlreadyScheduled ? (
                                                                                <span className="text-[10px] font-black uppercase text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                                                                    <span className="material-symbols-outlined text-[14px]">check</span> Programada
                                                                                </span>
                                                                            ) : (
                                                                                <button 
                                                                                    onClick={() => openPlanTaskModal(t.id)} 
                                                                                    disabled={!hasPermission('ps_gantt_avance', 'edit')}
                                                                                    className={`px-2.5 py-1.5 text-[10px] font-bold uppercase rounded-lg border transition-colors ${
                                                                                        !hasPermission('ps_gantt_avance', 'edit')
                                                                                            ? 'bg-slate-100 dark:bg-slate-800/50 text-slate-400 border-slate-200 dark:border-slate-800 cursor-not-allowed opacity-50 pointer-events-none shadow-none'
                                                                                            : 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/60 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900/50'
                                                                                    }`}
                                                                                >
                                                                                    Programar
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                        <table className="hidden md:table w-full text-left border-collapse">
                                            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                                                <tr>
                                                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Nivel</th>
                                                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Unidades Físicas</th>
                                                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Tarea</th>
                                                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Responsable</th>
                                                    <th className="px-2 py-2 text-xs font-semibold text-slate-500 text-center w-8">L</th>
                                                    <th className="px-2 py-2 text-xs font-semibold text-slate-500 text-center w-8">M</th>
                                                    <th className="px-2 py-2 text-xs font-semibold text-slate-500 text-center w-8">X</th>
                                                    <th className="px-2 py-2 text-xs font-semibold text-slate-500 text-center w-8">J</th>
                                                    <th className="px-2 py-2 text-xs font-semibold text-slate-500 text-center w-8">V</th>
                                                    <th className="px-2 py-2 text-xs font-semibold text-slate-500 text-center w-8">S</th>
                                                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase w-28 text-center bg-slate-50 dark:bg-slate-800">% Comprometido</th>
                                                    {(activePlan?.status === 'En Ejecución' || activePlan?.status === 'En Revisión' || activePlan?.status === 'Cerrada') && (
                                                        <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase w-28 text-center bg-slate-50 dark:bg-slate-800">Estado</th>
                                                    )}
                                                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase w-32 text-center bg-slate-50 dark:bg-slate-800">% Cumplimiento</th>
                                                    {(activePlan?.status === 'En Revisión' || activePlan?.status === 'Cerrada') && (
                                                        <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase w-48 bg-amber-50 dark:bg-amber-900/10">C.N.C</th>
                                                    )}
                                                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase w-10 text-right"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                {activePlan && activePlan.allocations.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={activePlan?.status === 'En Revisión' || activePlan?.status === 'Cerrada' ? 15 : 13} className="px-4 py-12 text-center text-slate-400 italic">
                                                            No hay tareas programadas. Utilice "Click Derecho" en una tarea del menú izquierdo y seleccione "Programar tarea".
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    activePlan?.allocations.map((alloc, idx) => {
                                                        const unit = units.find(u => u.id === alloc.unitId);
                                                        const unitName = unit?.name || 'N/A';
                                                        const levelName = levels.find(l => l.id === unit?.levelId)?.name || '-';
                                                        const taskName = alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : findTaskName(alloc.taskId, tasks);
                                                        const responsibleName = responsibles.find(r => r.id === alloc.responsibleId)?.name || '-';
                                                        const config = !alloc.isAdditional ? protocolConfigs[alloc.taskId] : null;
                                                        const appliesProtocol = config?.applies ?? false;

                                                        // Calculate accumulated compliance from other closed weekly plans
                                                        let accumulatedCompliance = 0;
                                                        weeklyPlans
                                                            .filter(p => p.status === 'Cerrada' && p.id !== activePlan.id)
                                                            .flatMap(p => p.allocations)
                                                            .forEach(a => {
                                                                if (a.taskId === alloc.taskId && a.unitId === alloc.unitId) {
                                                                    accumulatedCompliance += a.percentCumplimiento ?? (a.executed ? 100 : 0);
                                                                }
                                                            });
                                                        accumulatedCompliance = Math.min(100, accumulatedCompliance);
                                                        const isProtocolClosed = !alloc.isAdditional && appliesProtocol
                                                            ? isProtocolProgressedLocal(alloc.taskId, alloc.unitId)
                                                            : false;

                                                        const pctComprometido = alloc.percentComprometido ?? (alloc.executed ? 100 : 100);
                                                        let pctCumplimiento = alloc.percentCumplimiento ?? (alloc.executed ? 100 : 0);

                                                        if (isProtocolClosed) {
                                                            pctCumplimiento = 100 - accumulatedCompliance;
                                                        }

                                                        const isExecuted = !!alloc.executed;

                                                        const isManualEntryAllowed = !appliesProtocol || (accumulatedCompliance + pctComprometido < 100);

                                                        return (
                                                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                                <td className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">{levelName}</td>
                                                                <td className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 font-medium">{unitName}</td>
                                                                <td className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 relative">
                                                                    <div className="flex items-center gap-2">
                                                                        {alloc.isAdditional ? (
                                                                            <span className="flex items-center gap-1.5">
                                                                                <span className="bg-purple-100 text-purple-700 text-[10px] font-bold px-1.5 py-0.5 rounded dark:bg-purple-900/30 dark:text-purple-300">Adicional</span>
                                                                                <span>{taskName}</span>
                                                                            </span>
                                                                        ) : (
                                                                            <span>{taskName}</span>
                                                                        )}
                                                                        {getStatusValue(alloc) === 'no' && alloc.comentario && (
                                                                            <div className="relative inline-block">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setActiveTooltipAllocIdx(activeTooltipAllocIdx === idx ? null : idx)}
                                                                                    className="text-amber-500 hover:text-amber-600 flex items-center transition-colors focus:outline-none"
                                                                                    title="Ver justificación de incumplimiento"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px]">chat</span>
                                                                                </button>
                                                                                {activeTooltipAllocIdx === idx && (
                                                                                    <div className="absolute z-[999] bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-slate-900 text-white text-xs rounded-lg p-2.5 shadow-xl border border-slate-700 animate-in fade-in slide-in-from-bottom-2">
                                                                                        <div className="flex justify-between items-start mb-1 font-bold text-amber-400">
                                                                                            <span>Justificación</span>
                                                                                            <button type="button" onClick={(e) => { e.stopPropagation(); setActiveTooltipAllocIdx(null); }} className="text-slate-400 hover:text-white"><span className="material-symbols-outlined text-[12px]">close</span></button>
                                                                                        </div>
                                                                                        <p className="font-normal text-slate-200 break-words">{alloc.comentario}</p>
                                                                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400">{responsibleName}</td>
                                                                {['L', 'M', 'X', 'J', 'V', 'S'].map(day => (
                                                                    <td key={day} className="px-2 py-2 text-center">
                                                                        {alloc.days[day] && <span className="text-xs font-bold text-slate-600 dark:text-slate-400">x</span>}
                                                                    </td>
                                                                ))}
                                                                <td className="px-4 py-2 text-sm text-center text-slate-600 dark:text-slate-400">
                                                                    {pctComprometido}%
                                                                </td>
                                                                {(activePlan.status === 'En Ejecución' || activePlan.status === 'En Revisión' || activePlan.status === 'Cerrada') && (
                                                                     <td className="px-4 py-2 text-center">
                                                                         <div className="flex flex-col gap-1.5 items-center">
                                                                             <select
                                                                                 value={getStatusValue(alloc)}
                                                                                 disabled={getDisabledStatus(alloc)}
                                                                                 onChange={(e) => {
                                                                                     const val = e.target.value;
                                                                                     if (val === 'pendiente') {
                                                                                         updateMainAllocationField(activePlan.id, idx, 'executed', null);
                                                                                         updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', 0);
                                                                                     } else if (val === 'si') {
                                                                                         if (appliesProtocol && !isProtocolClosed) {
                                                                                             alert("No se puede marcar como SI porque el protocolo de calidad asociado no ha sido cerrado.");
                                                                                             return;
                                                                                         }
                                                                                         updateMainAllocationField(activePlan.id, idx, 'executed', true);
                                                                                         updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', 100);
                                                                                     } else if (val === 'no') {
                                                                                         const currentPct = alloc.percentCumplimiento ?? 0;
                                                                                         updateMainAllocationField(activePlan.id, idx, 'executed', false);
                                                                                         updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', currentPct >= 100 ? 0 : currentPct);
                                                                                     }
                                                                                 }}
                                                                                 className={`text-xs border rounded p-1.5 transition-all font-bold focus:outline-none focus:ring-1 ${
                                                                                     getStatusValue(alloc) === 'si'
                                                                                     ? 'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300 focus:ring-emerald-500'
                                                                                     : getStatusValue(alloc) === 'no'
                                                                                     ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-950/40 dark:border-red-800 dark:text-red-300 focus:ring-red-500'
                                                                                     : 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-300 focus:ring-amber-500'
                                                                                 }`}
                                                                             >
                                                                                 <option value="pendiente" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">Pendiente</option>
                                                                                 <option value="si" disabled={appliesProtocol && !isProtocolClosed} className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">Sí</option>
                                                                                 <option value="no" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">No</option>
                                                                             </select>
                                                                             {getStatusValue(alloc) === 'no' && (
                                                                                 <input
                                                                                     type="text"
                                                                                     placeholder="Justificación (obligatoria)"
                                                                                     disabled={getDisabledStatus(alloc)}
                                                                                     className="w-full min-w-[120px] max-w-[200px] text-xs p-1 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white placeholder:italic"
                                                                                     value={alloc.comentario || ''}
                                                                                     onChange={(e) => updateMainAllocationField(activePlan.id, idx, 'comentario', e.target.value)}
                                                                                 />
                                                                             )}
                                                                         </div>
                                                                     </td>
                                                                 )}
                                                                <td className="px-4 py-2 text-sm text-center">
                                                                    {(() => {
                                                                        const status = getStatusValue(alloc);
                                                                        if (status === 'si') {
                                                                            return (
                                                                                <span className="text-emerald-600 dark:text-emerald-400 font-bold text-xs bg-emerald-50 dark:bg-emerald-950/20 px-2 py-1 rounded border border-emerald-200 dark:border-emerald-850 flex items-center justify-center gap-1 mx-auto w-fit">
                                                                                    100%
                                                                                </span>
                                                                            );
                                                                        } else if (status === 'no') {
                                                                            const isDisabled = getDisabledStatus(alloc);
                                                                            if (activePlan.status === 'Cerrada') {
                                                                                return <span className="text-red-600 dark:text-red-400 font-bold">{pctCumplimiento}%</span>;
                                                                            }
                                                                            return (
                                                                                <div className="flex items-center justify-center gap-1">
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        max={Math.min(99, 100 - accumulatedCompliance)}
                                                                                        disabled={isDisabled}
                                                                                        className="w-16 text-center text-xs p-1 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white font-bold text-red-650 dark:text-red-400 bg-red-50/30"
                                                                                        value={pctCumplimiento >= 100 ? 0 : pctCumplimiento}
                                                                                        onChange={(e) => {
                                                                                            let val = parseFloat(e.target.value) || 0;
                                                                                            const maxVal = Math.min(99, 100 - accumulatedCompliance);
                                                                                            if (val > maxVal) {
                                                                                                alert(`Para tareas en estado 'No', el avance máximo es ${maxVal}%.`);
                                                                                                val = maxVal;
                                                                                            }
                                                                                            if (val < 0) val = 0;
                                                                                            updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', val);
                                                                                        }}
                                                                                    />
                                                                                    <span className="text-xs text-slate-500">%</span>
                                                                                </div>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <span className="text-amber-650 dark:text-amber-400 font-bold text-xs bg-amber-50 dark:bg-amber-950/20 px-2 py-1 rounded border border-amber-200 dark:border-amber-850 flex items-center justify-center gap-1 mx-auto w-fit">
                                                                                    0%
                                                                                </span>
                                                                            );
                                                                        }
                                                                    })()}
                                                                </td>
                                                                {(activePlan.status === 'En Revisión' || activePlan.status === 'Cerrada') && (
                                                                    <td className="px-4 py-2 bg-amber-50/30 dark:bg-amber-900/5">
                                                                        {getStatusValue(alloc) === 'si' ? (
                                                                            <span className="text-xs text-slate-300 italic">No Aplica</span>
                                                                        ) : (
                                                                            <div className="relative">
                                                                                <SearchableCNCSelect
                                                                                    value={alloc.cncId || ''}
                                                                                    onChange={(val) => updateMainAllocationField(activePlan.id, idx, 'cncId', val)}
                                                                                    groups={cncGroups}
                                                                                    disabled={activePlan.status === 'Cerrada' || !isJefaturaOrOficina}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                )}
                                                                <td className="px-4 py-2 text-right">
                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                        {activePlan.status === 'En Planificación' && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    if (alloc.isAdditional) {
                                                                                        setEditingAdditionalIndex(idx);
                                                                                        setAdditionalModalData({
                                                                                            index: idx,
                                                                                            levelId: units.find(u => u.id === alloc.unitId)?.levelId || '',
                                                                                            unitId: alloc.unitId,
                                                                                            additionalTaskName: alloc.additionalTaskName || '',
                                                                                            responsibleId: alloc.responsibleId,
                                                                                            days: { ...alloc.days }
                                                                                        });
                                                                                        setAdditionalModalOpen(true);
                                                                                    } else {
                                                                                        setEditingAllocationIndex(idx);
                                                                                        const allocCopy = JSON.parse(JSON.stringify(alloc));
                                                                                        allocCopy.percentComprometido = allocCopy.percentComprometido ?? 100;
                                                                                        setEditingAllocationData(allocCopy);
                                                                                        setIsEditingAllocation(true);
                                                                                    }
                                                                                }}
                                                                                className="text-slate-400 hover:text-purple-600 transition-colors"
                                                                                title="Editar asignación"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[18px]">edit</span>
                                                                            </button>
                                                                        )}
                                                                        {activePlan.status === 'En Planificación' && (
                                                                            <button
                                                                                onClick={() => handleDeleteAllocation(activePlan.id, idx)}
                                                                                className="text-slate-400 hover:text-red-500 transition-colors"
                                                                                title="Eliminar actividad"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>

                                        {/* --- VISTA MÓVIL (PLANIFICACIÓN SEMANAL) --- */}
                                        <div className="block md:hidden p-4 space-y-4 pb-24">
                                            {/* Filtro Móvil Semanal */}
                                            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                                                <button 
                                                    onClick={() => setMobileFilterWeekly('all')}
                                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterWeekly === 'all' ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">public</span> General
                                                </button>
                                                <button 
                                                    onClick={() => setMobileFilterWeekly('mine')}
                                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterWeekly === 'mine' ? 'bg-primary text-white shadow flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">person</span> Mis Tareas
                                                </button>
                                            </div>

                                            {(() => {
                                                if (!activePlan || activePlan.allocations.length === 0) {
                                                    return (
                                                        <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                                                            No hay tareas programadas.
                                                        </div>
                                                    );
                                                }

                                                let allocationsForMobile = activePlan.allocations;
                                                if (mobileFilterWeekly === 'mine' && currentUser) {
                                                    allocationsForMobile = allocationsForMobile.filter(alloc => {
                                                        const resp = responsibles.find(r => r.id === alloc.responsibleId);
                                                        return resp && resp.email === currentUser.email;
                                                    });
                                                }

                                                if (allocationsForMobile.length === 0) {
                                                    return (
                                                        <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                                                            No tienes tareas asignadas en esta planificación.
                                                        </div>
                                                    );
                                                }

                                                return allocationsForMobile.map((alloc, idx) => {
                                                    const targetIdx = activePlan.allocations.findIndex(a => a.taskId === alloc.taskId && a.unitId === alloc.unitId);
                                                    const actualIdx = targetIdx >= 0 ? targetIdx : idx;
                                                    const unit = units.find(u => u.id === alloc.unitId);
                                                    const unitName = unit?.name || 'N/A';
                                                    const levelName = levels.find(l => l.id === unit?.levelId)?.name || '-';
                                                    const taskName = alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : findTaskName(alloc.taskId, tasks);
                                                    const responsibleName = responsibles.find(r => r.id === alloc.responsibleId)?.name || 'Sin Asignar';
                                                    const config = !alloc.isAdditional ? protocolConfigs[alloc.taskId] : null;
                                                    const appliesProtocol = config?.applies ?? false;
 
                                                    // Calculate accumulated compliance from other closed weekly plans
                                                    let accumulatedCompliance = 0;
                                                    weeklyPlans
                                                        .filter(p => p.status === 'Cerrada' && p.id !== activePlan.id)
                                                        .flatMap(p => p.allocations)
                                                        .forEach(a => {
                                                            if (a.taskId === alloc.taskId && a.unitId === alloc.unitId) {
                                                                accumulatedCompliance += a.percentCumplimiento ?? (a.executed ? 100 : 0);
                                                            }
                                                        });
                                                    accumulatedCompliance = Math.min(100, accumulatedCompliance);

                                                    const isProtocolClosed = !alloc.isAdditional && appliesProtocol
                                                        ? isProtocolProgressedLocal(alloc.taskId, alloc.unitId)
                                                        : false;

                                                    const pctComprometido = alloc.percentComprometido ?? (alloc.executed ? 100 : 100);
                                                    let pctCumplimiento = alloc.percentCumplimiento ?? (alloc.executed ? 100 : 0);

                                                    if (isProtocolClosed) {
                                                        pctCumplimiento = 100 - accumulatedCompliance;
                                                    }

                                                    const isExecuted = !!alloc.executed;
                                                    const isManualEntryAllowed = !appliesProtocol || (accumulatedCompliance + pctComprometido < 100);
                                                    const activeDays = ['L', 'M', 'X', 'J', 'V', 'S'].filter(d => alloc.days[d as keyof typeof alloc.days]);

                                                    return (
                                                        <div key={`${alloc.taskId}_${alloc.unitId}`} className={`rounded-xl shadow-sm flex flex-col p-3 gap-2 border transition-colors relative ${
                                                            getStatusValue(alloc) === 'si'
                                                            ? 'bg-[#52be80] border-[#229954] text-slate-900 dark:bg-[#27ae60] dark:text-white dark:border-[#1e8449]' 
                                                            : getStatusValue(alloc) === 'no'
                                                            ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-900 text-red-900 dark:text-red-300'
                                                            : 'bg-[#fcf3cf] border-[#f4d03f] text-slate-900 dark:bg-[#f1c40f]/20 dark:text-white dark:border-[#f1c40f]/40'
                                                        }`}>
                                                            <div className="flex items-center justify-between gap-3">
                                                                <div className="flex flex-col flex-1 gap-1.5 pr-2">
                                                                    <div className="text-[13px] font-black leading-tight drop-shadow-sm flex items-center gap-2">
                                                                        {alloc.isAdditional && (
                                                                            <span className="bg-purple-100 text-purple-700 text-[9px] font-bold px-1 py-0.5 rounded mr-1.5 dark:bg-purple-900/30 dark:text-purple-300">ADICIONAL</span>
                                                                        )}
                                                                        <span className="flex-1">{taskName}</span>
                                                                        {getStatusValue(alloc) === 'no' && alloc.comentario && (
                                                                            <div className="relative">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setActiveTooltipAllocIdx(activeTooltipAllocIdx === actualIdx ? null : actualIdx)}
                                                                                    className="text-amber-500 dark:text-amber-400 p-1 flex items-center focus:outline-none"
                                                                                    title="Ver justificación"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px]">chat</span>
                                                                                </button>
                                                                                {activeTooltipAllocIdx === actualIdx && (
                                                                                    <div className="absolute z-[999] right-0 bottom-full mb-2 w-56 bg-slate-950 text-white text-xs rounded-lg p-2.5 shadow-xl border border-slate-800 animate-in fade-in slide-in-from-bottom-2">
                                                                                        <div className="flex justify-between items-start mb-1 font-bold text-amber-400">
                                                                                            <span>Justificación</span>
                                                                                            <button type="button" onClick={(e) => { e.stopPropagation(); setActiveTooltipAllocIdx(null); }} className="text-slate-400 hover:text-white"><span className="material-symbols-outlined text-[12px]">close</span></button>
                                                                                        </div>
                                                                                        <p className="font-normal text-slate-200 break-words">{alloc.comentario}</p>
                                                                                        <div className="absolute top-full right-3 border-4 border-transparent border-t-slate-950"></div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-1 text-[10px] font-bold opacity-90">
                                                                        <span className="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded shadow-sm">{levelName} - {unitName}</span>
                                                                        <span className="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded shadow-sm">{responsibleName}</span>
                                                                        <span className="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded shadow-sm flex items-center gap-0.5"><span className="material-symbols-outlined text-[10px]">calendar_today</span>{activeDays.join(', ')}</span>
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-1.5 text-[10px] font-bold mt-1 text-slate-800 dark:text-slate-200">
                                                                        <span className="bg-white/20 dark:bg-black/20 px-1.5 py-0.5 rounded shadow-sm">Meta: {pctComprometido}%</span>
                                                                        <span className="bg-white/20 dark:bg-black/20 px-1.5 py-0.5 rounded shadow-sm">Acum: {accumulatedCompliance}%</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="flex flex-col gap-2 shrink-0 bg-white/50 dark:bg-black/20 p-2 rounded-lg border border-black/10 text-slate-800 dark:text-slate-200 w-full mt-2">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <span className="text-[10px] font-black opacity-85 uppercase">Estado</span>
                                                                    <select
                                                                        value={getStatusValue(alloc)}
                                                                        disabled={getDisabledStatus(alloc)}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            if (val === 'pendiente') {
                                                                                updateMainAllocationField(activePlan.id, actualIdx, 'executed', null);
                                                                                updateMainAllocationField(activePlan.id, actualIdx, 'percentCumplimiento', 0);
                                                                            } else if (val === 'si') {
                                                                                if (appliesProtocol && !isProtocolClosed) {
                                                                                    alert("No se puede marcar como SI porque el protocolo de calidad asociado no ha sido cerrado.");
                                                                                    return;
                                                                                }
                                                                                updateMainAllocationField(activePlan.id, actualIdx, 'executed', true);
                                                                                updateMainAllocationField(activePlan.id, actualIdx, 'percentCumplimiento', 100);
                                                                            } else if (val === 'no') {
                                                                                const currentPct = alloc.percentCumplimiento ?? 0;
                                                                                updateMainAllocationField(activePlan.id, actualIdx, 'executed', false);
                                                                                updateMainAllocationField(activePlan.id, actualIdx, 'percentCumplimiento', currentPct >= 100 ? 0 : currentPct);
                                                                            }
                                                                        }}
                                                                        className={`text-xs border rounded p-1.5 transition-all font-bold focus:outline-none ${
                                                                            getStatusValue(alloc) === 'si'
                                                                            ? 'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-850 dark:text-emerald-300'
                                                                            : getStatusValue(alloc) === 'no'
                                                                            ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-950/40 dark:border-red-850 dark:text-red-300'
                                                                            : 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-950/40 dark:border-amber-850 dark:text-amber-300'
                                                                        }`}
                                                                    >
                                                                        <option value="pendiente" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">Pendiente</option>
                                                                        <option value="si" disabled={appliesProtocol && !isProtocolClosed} className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">Sí</option>
                                                                        <option value="no" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">No</option>
                                                                    </select>
                                                                </div>
                                                                {getStatusValue(alloc) === 'si' && (
                                                                    <div className="flex items-center justify-between text-xs">
                                                                        <span className="font-bold">Cumplimiento:</span>
                                                                        <span className="text-green-600 font-bold">100%</span>
                                                                    </div>
                                                                )}
                                                                {getStatusValue(alloc) === 'no' && (
                                                                    <>
                                                                        <div className="flex items-center justify-between gap-2">
                                                                            <span className="text-[10px] font-black opacity-85 uppercase">Cumplimiento</span>
                                                                            <div className="flex items-center gap-1">
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max={Math.min(99, 100 - accumulatedCompliance)}
                                                                                    disabled={getDisabledStatus(alloc)}
                                                                                    className="w-16 text-center text-xs p-1 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                                                                                    value={pctCumplimiento >= 100 ? 0 : pctCumplimiento}
                                                                                    onChange={(e) => {
                                                                                        let val = parseFloat(e.target.value) || 0;
                                                                                        const maxVal = Math.min(99, 100 - accumulatedCompliance);
                                                                                        if (val > maxVal) {
                                                                                            alert(`Para tareas en estado 'No', el avance máximo es ${maxVal}%.`);
                                                                                            val = maxVal;
                                                                                        }
                                                                                        if (val < 0) val = 0;
                                                                                        updateMainAllocationField(activePlan.id, actualIdx, 'percentCumplimiento', val);
                                                                                    }}
                                                                                />
                                                                                <span className="text-xs">%</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex flex-col gap-1">
                                                                            <span className="text-[10px] font-black opacity-85 uppercase">Justificación</span>
                                                                            <input
                                                                                type="text"
                                                                                placeholder="Justificación (obligatoria)"
                                                                                disabled={getDisabledStatus(alloc)}
                                                                                className="w-full text-xs p-1.5 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white placeholder:italic"
                                                                                value={alloc.comentario || ''}
                                                                                onChange={(e) => updateMainAllocationField(activePlan.id, actualIdx, 'comentario', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {getStatusValue(alloc) === 'pendiente' && (
                                                                    <div className="flex items-center justify-between text-xs text-slate-400">
                                                                        <span>Cumplimiento:</span>
                                                                        <span>0%</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            {(activePlan.status === 'En Revisión' || activePlan.status === 'Cerrada') && getStatusValue(alloc) !== 'si' && (
                                                                <div className="mt-2 pt-2 border-t border-black/10 dark:border-white/10 w-full">
                                                                    <label className="block text-[10px] uppercase font-black opacity-70 mb-1 ml-1 text-slate-900 dark:text-white">Causas de No Cumplimiento</label>
                                                                    <div className="bg-white/80 dark:bg-[#101822]/80 rounded p-1">
                                                                        <SearchableCNCSelect
                                                                            value={alloc.cncId || ''}
                                                                            onChange={(val) => updateMainAllocationField(activePlan.id, actualIdx, 'cncId', val)}
                                                                            groups={cncGroups}
                                                                            disabled={activePlan.status === 'Cerrada' || !isJefaturaOrOficina}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {activePlan.status === 'En Planificación' && (
                                                                <div className="absolute top-1 right-1 flex items-center gap-1 opacity-60">
                                                                    {!alloc.isAdditional && (
                                                                        <button
                                                                            onClick={() => {
                                                                                setEditingAllocationIndex(actualIdx);
                                                                                const allocCopy = JSON.parse(JSON.stringify(alloc));
                                                                                allocCopy.percentComprometido = allocCopy.percentComprometido ?? 100;
                                                                                setEditingAllocationData(allocCopy);
                                                                                setIsEditingAllocation(true);
                                                                            }}
                                                                            className="hover:text-purple-700 dark:hover:text-purple-300 transition-colors p-1"
                                                                            title="Editar asignación"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[14px]">edit</span>
                                                                        </button>
                                                                    )}
                                                                    {!alloc.isAdditional && (
                                                                        <button
                                                                            onClick={() => handleDeleteAllocation(activePlan.id, actualIdx)}
                                                                            className="hover:text-red-700 dark:hover:text-red-400 transition-colors p-1"
                                                                            title="Eliminar asignación"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[14px]">close</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // History View (Default)
                                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex-1 min-h-0 flex flex-col overflow-hidden">
                                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                                        <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">Historial de Planificaciones</h3>
                                    </div>
                                    <div className="flex-1 overflow-auto">
                                        {weeklyPlans.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                                <span className="material-symbols-outlined text-4xl mb-2">history</span>
                                                <p className="text-sm">No hay planificaciones registradas.</p>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="hidden md:table w-full text-left border-collapse">
                                                    <thead className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                                        <tr>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">#</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Fecha de Inicio</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Fecha de Termino</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Estado</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-center">PPC</th>
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right">Ver</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                        {[...weeklyPlans].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map(plan => {
                                                            let statusLabel: string = plan.status;
                                                            let statusClasses = '';

                                                            if (plan.status === 'En Planificación') {
                                                                statusLabel = 'En Planificación';
                                                                statusClasses = 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
                                                            } else if (plan.status === 'En Ejecución') {
                                                                statusLabel = 'En Ejecución';
                                                                statusClasses = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
                                                            } else if (plan.status === 'En Revisión') {
                                                                statusLabel = 'En Revisión';
                                                                statusClasses = 'bg-yellow-400 text-slate-900 border border-yellow-500/20';
                                                            } else if (plan.status === 'Cerrada') {
                                                                statusLabel = 'Cerrado';
                                                                statusClasses = 'bg-slate-300 text-slate-600 border border-slate-400/20';
                                                            }

                                                            const totalAllocations = plan.allocations ? plan.allocations.length : 0;
                                                            const executedAllocations = plan.allocations ? plan.allocations.filter(a => a.executed).length : 0;
                                                            const ppc = totalAllocations > 0 ? Math.round((executedAllocations / totalAllocations) * 100) : 0;

                                                            return (
                                                                <tr key={plan.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                                    <td className="px-6 py-5 font-bold text-sm text-slate-700 dark:text-slate-300">Planificación #{plan.id}</td>
                                                                    <td className="px-6 py-5 text-sm text-slate-600 dark:text-slate-400 font-medium">{plan.startDate}</td>
                                                                    <td className="px-6 py-5 text-sm text-slate-600 dark:text-slate-400 font-medium">{plan.endDate}</td>
                                                                    <td className="px-6 py-5">
                                                                        <span className={`inline-flex px-4 py-1.5 rounded-full text-xs font-bold shadow-sm ${statusClasses}`}>
                                                                            {statusLabel}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-6 py-5 text-center">
                                                                        <span className="font-bold text-sm text-slate-700 dark:text-slate-300">{ppc}%</span>
                                                                    </td>
                                                                    <td className="px-6 py-5 text-right flex justify-end">
                                                                        <button
                                                                            onClick={() => {
                                                                                setEditingWeeklyPlanId(plan.id);
                                                                                setWeeklyPlanDates({ start: plan.startDate, end: plan.endDate });
                                                                                setNewPlanModalOpen(true);
                                                                            }}
                                                                            className="text-slate-400 hover:text-primary transition-colors p-2 hover:bg-slate-100 rounded-full"
                                                                            title="Editar fechas de la planificación"
                                                                            disabled={plan.status === 'Cerrada'}
                                                                        >
                                                                            <span className="material-symbols-outlined text-[20px]">edit_calendar</span>
                                                                        </button>
                                                                        <button
                                                                            onClick={() => {
                                                                                setWeeklyPlansBackup(JSON.parse(JSON.stringify(weeklyPlans)));
                                                                                setActivePlanId(plan.id);
                                                                                if (plan.status === 'En Planificación') {
                                                                                    setIsWeeklyEditing(true);
                                                                                } else {
                                                                                    setIsWeeklyEditing(false);
                                                                                }
                                                                            }}
                                                                            className="text-slate-400 hover:text-primary transition-colors p-2 hover:bg-slate-100 rounded-full"
                                                                            title="Ver Plan"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[22px]">search</span>
                                                                        </button>
                                                                        {(hasPermission('ps_eliminar', 'view') || hasPermission('ps_eliminar', 'edit')) && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    if (hasPermission('ps_eliminar', 'edit')) {
                                                                                        handleDeleteWeeklyPlan(plan.id);
                                                                                    }
                                                                                }}
                                                                                disabled={!hasPermission('ps_eliminar', 'edit')}
                                                                                className={`p-2 rounded-full transition-colors ${
                                                                                    !hasPermission('ps_eliminar', 'edit')
                                                                                    ? 'text-slate-200 cursor-not-allowed dark:text-slate-800'
                                                                                    : 'text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20'
                                                                                }`}
                                                                                title={!hasPermission('ps_eliminar', 'edit') ? "Eliminar Programación (Solo Lectura)" : "Eliminar Programación"}
                                                                            >
                                                                                <span className="material-symbols-outlined text-[20px]">delete</span>
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                <div className="block md:hidden p-4 space-y-3 pb-24">
                                                    {[...weeklyPlans].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map(plan => {
                                                        let statusClasses = '';
                                                        let statusLabel: string = plan.status;
                                                        if (plan.status === 'En Planificación') { statusClasses = 'bg-[#85c1e9] text-slate-900 border-[#2e86c1]'; statusLabel = 'EN PLANIFICACIÓN'; }
                                                        else if (plan.status === 'En Ejecución') { statusClasses = 'bg-[#52be80] text-slate-900 border-[#229954]'; statusLabel = 'EJECUTANDO'; }
                                                        else if (plan.status === 'En Revisión') { statusClasses = 'bg-yellow-400 text-slate-900 border-yellow-500'; statusLabel = 'POR REVISAR'; }
                                                        else if (plan.status === 'Cerrada') { statusClasses = 'bg-slate-300 text-slate-800 border-slate-400'; statusLabel = 'FINALIZADA'; }
                                                        
                                                        return (
                                                            <div key={plan.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-3 flex flex-col gap-2">
                                                                <div className="flex justify-between items-start">
                                                                    <div>
                                                                        <h4 className="font-bold text-[#1a73e8] dark:text-blue-400 text-sm">Planificación #{plan.id}</h4>
                                                                        <p className="text-xs text-slate-500 font-medium mt-0.5">{plan.startDate} al {plan.endDate}</p>
                                                                    </div>
                                                                    <span className={`text-[9px] font-bold px-2 py-1 rounded border shadow-sm ${statusClasses}`}>
                                                                        {statusLabel}
                                                                    </span>
                                                                </div>
                                                                <div className="flex gap-2 mt-2">
                                                                    <button
                                                                        onClick={() => {
                                                                            setWeeklyPlansBackup(JSON.parse(JSON.stringify(weeklyPlans)));
                                                                            setActivePlanId(plan.id);
                                                                            setIsWeeklyEditing(plan.status === 'En Planificación');
                                                                        }}
                                                                        className="flex-1 bg-[#1a73e8] hover:bg-blue-600 text-white font-bold py-2.5 rounded-lg text-xs uppercase flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px]">touch_app</span> Entrar a Revisar
                                                                    </button>
                                                                    {(hasPermission('ps_eliminar', 'view') || hasPermission('ps_eliminar', 'edit')) && (
                                                                        <button
                                                                            onClick={() => {
                                                                                if (hasPermission('ps_eliminar', 'edit')) {
                                                                                    handleDeleteWeeklyPlan(plan.id);
                                                                                }
                                                                            }}
                                                                            disabled={!hasPermission('ps_eliminar', 'edit')}
                                                                            className={`px-3 py-2.5 rounded-lg border flex items-center justify-center transition-all ${
                                                                                !hasPermission('ps_eliminar', 'edit')
                                                                                ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed dark:bg-slate-800 dark:border-slate-800'
                                                                                : 'bg-red-50 border-red-200 text-red-600 hover:bg-red-500 hover:text-white'
                                                                            }`}
                                                                            title={!hasPermission('ps_eliminar', 'edit') ? "Eliminar (Solo Lectura)" : "Eliminar"}
                                                                        >
                                                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        // Standard Gantt View for Master
                        <>
                            <div className="min-w-max">
                                {/* ... (Existing Gantt code) ... */}
                                <div className="sticky top-0 z-30 flex flex-col border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-[#101822] shadow-sm h-[45px] box-border">
                                    {/* Top Header */}
                                    <div className="flex h-[24px] border-b border-slate-100 dark:border-slate-800">
                                        {timelineHeaders.map((h, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center justify-center border-r border-slate-100 dark:border-slate-800 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 uppercase tracking-wide px-1 overflow-hidden text-ellipsis whitespace-nowrap shrink-0"
                                                style={{ width: `${h.width}px` }}
                                            >
                                                {h.label}
                                            </div>
                                        ))}
                                    </div>
                                    {/* Bottom Header */}
                                    <div className="flex h-[20px] bg-white dark:bg-[#101822]">
                                        {timelineColumns.map((date, i) => {
                                            let widthClass = "w-[100px]";
                                            let label: React.ReactNode = "";

                                            if (zoomLevel === 'days') {
                                                widthClass = "w-[30px]";
                                                const weekdayLetters = ["D", "L", "M", "M", "J", "V", "S"];
                                                label = (
                                                    <div className="flex flex-col items-center justify-center text-[10px] text-slate-500 font-semibold h-full leading-none">
                                                        <span>{date.getDate()}</span>
                                                        <span className="text-[8px] text-slate-400 font-medium mt-0.5">{weekdayLetters[date.getDay()]}</span>
                                                    </div>
                                                );
                                            } else if (zoomLevel === 'weeks') {
                                                widthClass = "w-[100px]";
                                                label = <span>{date.getDate()}</span>;
                                            } else if (zoomLevel === 'months') {
                                                widthClass = "w-[120px]";
                                                const shortMonths = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
                                                label = <span>{shortMonths[date.getMonth()]}</span>;
                                            } else { // 'years'
                                                widthClass = "w-[250px]";
                                                label = <span>{date.getFullYear()}</span>;
                                            }

                                            return (
                                                <div
                                                    key={i}
                                                    className={`${widthClass} shrink-0 border-r border-slate-100 dark:border-slate-800 flex items-center justify-center text-[11px] text-slate-500 font-medium`}
                                                >
                                                    {label}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="relative">
                                    <DependencyArrows
                                        viewStartDate={gridStartDate}
                                        visibleTasks={visibleFlattenedTasks}
                                        zoomLevel={zoomLevel}
                                        workingDays={workingDays}
                                        holidays={holidays}
                                        timelineColumns={timelineColumns}
                                        selectedGanttTaskId={selectedGanttTaskId}
                                        taskPathInfo={taskPathInfo}
                                    />
                                    {renderGanttBars(processedTasks)}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* NEW Lookahead Modal */}
            {
                lookaheadModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[400px] border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">{editingLookaheadId ? 'Editar Fechas' : 'Planificación Intermedia'}</h3>
                                <button onClick={() => { setLookaheadModalOpen(false); setEditingLookaheadId(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-6 flex flex-col gap-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Inicio</label>
                                        <input
                                            type="date"
                                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none"
                                            value={lookaheadRange.start}
                                            onChange={(e) => setLookaheadRange({ ...lookaheadRange, start: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Término</label>
                                        <input
                                            type="date"
                                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none"
                                            value={lookaheadRange.end}
                                            onChange={(e) => setLookaheadRange({ ...lookaheadRange, end: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-purple-600 dark:text-purple-300 font-medium mb-1 uppercase">Horizonte</p>
                                        <div className="text-sm font-bold text-slate-800 dark:text-white">
                                            {calculatedWeeksAhead} Semana{calculatedWeeksAhead !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    <span className="material-symbols-outlined text-purple-300 text-3xl">date_range</span>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                                <button
                                    onClick={() => { setLookaheadModalOpen(false); setEditingLookaheadId(null); }}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreateLookaheadPlan}
                                    className="px-6 py-2 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors shadow-sm"
                                >
                                    {editingLookaheadId ? 'Guardar Cambios' : 'Generar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* NEW ADD CONSTRAINT MODAL */}
            {
                constraintModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[500px] border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-amber-500">warning</span>
                                    Restricción
                                </h3>
                                <button onClick={() => setConstraintModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-6 flex flex-col gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Tipo de Restricción</label>
                                    <select
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none text-sm"
                                        value={newConstraintData.typeId}
                                        onChange={(e) => setNewConstraintData({ ...newConstraintData, typeId: e.target.value })}
                                    >
                                        <option value="">Seleccionar Tipo...</option>
                                        {constraintGroups.map(group => (
                                            <optgroup key={group.id} label={group.name}>
                                                {group.definitions.map(def => (
                                                    <option key={def.id} value={def.id}>{def.name}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Responsable</label>
                                    <select
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none text-sm"
                                        value={newConstraintData.responsibleId}
                                        onChange={(e) => setNewConstraintData({ ...newConstraintData, responsibleId: e.target.value })}
                                    >
                                        <option value="">Seleccionar Responsable...</option>
                                        {responsibles.map(r => (
                                            <option key={r.id} value={r.id}>{r.name} ({r.role})</option>
                                        ))}
                                    </select>
                                </div>


                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Requerida</label>
                                    <input
                                        type="date"
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none text-sm"
                                        value={newConstraintData.requiredDate}
                                        onChange={(e) => setNewConstraintData({ ...newConstraintData, requiredDate: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Restricción (Detalle)</label>
                                    <textarea
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-3 border focus:ring-1 focus:ring-purple-500 outline-none text-sm resize-none h-24"
                                        placeholder="Describa el detalle de la restricción..."
                                        value={newConstraintData.description}
                                        onChange={(e) => setNewConstraintData({ ...newConstraintData, description: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Unidades físicas</label>
                                    <button
                                        onClick={() => setUnitSelectionModalOpen(true)}
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-sm text-left flex items-center justify-between"
                                    >
                                        <span className={newConstraintData.unitsType === 'na' ? 'text-slate-400' : 'text-slate-700 dark:text-slate-200'}>
                                            {newConstraintData.unitsType === 'na' ? 'No aplica Unidades físicas' :
                                                newConstraintData.unitsType === 'all' ? 'Todas las unidades (Seleccionado)' :
                                                    `Si aplica (${newConstraintData.unitIds.length} unidades)`}
                                        </span>
                                        <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                                    </button>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                                <button
                                    onClick={() => setConstraintModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveConstraint}
                                    className="px-6 py-2 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors shadow-sm"
                                >
                                    Guardar Restricción
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Context Menu and Modals remain mostly same, just ensuring correct functions */}
            {/* ... (Existing Modals) ... */}
            {
                contextMenu.visible && (
                    <div className="fixed z-50 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 w-52" style={{ top: contextMenu.y, left: contextMenu.x }} onClick={(e) => e.stopPropagation()}>
                        {/* Weekly View Context Menu */}
                        {activeTab === 'semanal' && isWeeklyEditing && (
                            <button onClick={() => contextMenu.taskId && openPlanTaskModal(contextMenu.taskId)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2">
                                <span className="material-symbols-outlined text-[18px]">calendar_today</span>
                                Programar tarea
                            </button>
                        )}

                        {/* Breakdown (Desglose) Mode Context Menu */}
                        {isBreakdownMode && (
                            <>
                                <button onClick={() => contextMenu.taskId && handleCreateSubtaskClick(contextMenu.taskId)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">add_task</span>
                                    Crear Subtarea
                                </button>
                                <button
                                    onClick={() => {
                                        const task = tasks.find(t => t.id === contextMenu.taskId) || tasks.flatMap(t => t.children || []).find(c => c.id === contextMenu.taskId);
                                        if (task) handleEditNameClick(task);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[18px]">edit</span>
                                    Editar
                                </button>
                                <button
                                    onClick={() => contextMenu.taskId && handleDeleteItem(contextMenu.taskId)}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[18px]">delete</span>
                                    Eliminar
                                </button>
                            </>
                        )}

                        {/* Master Plan (Carta Gantt) Editing Context Menu */}
                        {activeTab === 'maestra' && isMasterEditing && (
                            <>
                                {(() => {
                                    const t = findTaskById(contextMenu.taskId, tasks);
                                    return t?.type === 'folder' ? (
                                        <>
                                            <button onClick={() => contextMenu.taskId && handleCreateSubtaskClick(contextMenu.taskId)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[18px]">add_task</span>
                                                Crear Tarea
                                            </button>
                                            <button onClick={() => contextMenu.taskId && handleCreateFolderClick(contextMenu.taskId)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[18px]">create_new_folder</span>
                                                Crear Subcarpeta
                                            </button>
                                        </>
                                    ) : null;
                                })()}
                                <button
                                    onClick={() => {
                                        const task = findTaskById(contextMenu.taskId, tasks);
                                        if (task) handleEditNameClick(task);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[18px]">edit</span>
                                    Editar
                                </button>
                                <button
                                    onClick={() => contextMenu.taskId && handleMoveItemClick(contextMenu.taskId)}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[18px]">drive_file_move</span>
                                    Mover
                                </button>
                                <button
                                    onClick={() => contextMenu.taskId && handleDeleteItem(contextMenu.taskId)}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[18px]">delete</span>
                                    Eliminar
                                </button>
                                <div className="border-t border-slate-100 dark:border-slate-700 my-1"></div>
                                <button
                                    onClick={() => contextMenu.taskId && handleToggleCritical(contextMenu.taskId)}
                                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2 font-bold"
                                >
                                    <span className="material-symbols-outlined text-[18px]">priority_high</span>
                                    Marcar/Desmarcar Ruta Crítica
                                </button>
                            </>
                        )}
                    </div>
                )
            }

            {/* Input Modal */}
            {
                modalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl p-6 w-full max-w-md border border-slate-200 dark:border-slate-800">
                            <h3 className="text-lg font-bold mb-4 text-slate-900 dark:text-white">{editingItemId ? "Editar Nombre" : (modalMode === 'folder' ? "Nueva Carpeta" : "Nueva Tarea")}</h3>
                            <input autoFocus type="text" value={inputName} onChange={(e) => setInputName(e.target.value)} className="w-full rounded-lg border-slate-300 dark:border-slate-700 p-2 border mb-4" onKeyDown={(e) => e.key === 'Enter' && confirmInputModal()} />
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Cancelar</button>
                                <button onClick={confirmInputModal} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-blue-600">{editingItemId ? "Guardar" : "Crear"}</button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* Move Item Modal */}
            {
                moveModalOpen && movingItemId && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary">drive_file_move</span>
                                    Mover Elemento
                                </h3>
                                <button onClick={() => setMoveModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-500">
                                Seleccione la carpeta o ubicación de destino para el elemento:
                                <strong className="block text-slate-700 dark:text-slate-200 mt-1 text-sm font-semibold truncate">
                                    {findTaskById(movingItemId, tasks)?.name || 'Elemento'}
                                </strong>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 space-y-2">
                                {/* Option 1: Root Level */}
                                <button
                                    onClick={() => handleMoveItem(null)}
                                    className="w-full text-left p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-primary/5 hover:border-primary transition-all flex items-center gap-2.5 group"
                                >
                                    <span className="material-symbols-outlined text-slate-400 group-hover:text-primary">home_work</span>
                                    <div>
                                        <span className="font-bold text-slate-800 dark:text-white text-sm block">[ Raíz del Proyecto ]</span>
                                        <span className="text-[10px] text-slate-400">Mover al nivel principal de la Carta Gantt</span>
                                    </div>
                                </button>

                                <div className="border-b border-slate-100 dark:border-slate-800 my-3"></div>

                                {/* Option 2: Folders list */}
                                {(() => {
                                    const validFolders = getValidTargetFolders(tasks, movingItemId);
                                    if (validFolders.length === 0) {
                                        return (
                                            <p className="text-center text-slate-400 italic py-4 text-xs">
                                                No hay otras carpetas de destino válidas disponibles.
                                            </p>
                                        );
                                    }
                                    return (
                                        <div className="space-y-1.5">
                                            <p className="text-[10px] uppercase font-black tracking-wider text-slate-400 mb-2">Carpetas Disponibles</p>
                                            {validFolders.map(folder => (
                                                <button
                                                    key={folder.id}
                                                    onClick={() => handleMoveItem(folder.id)}
                                                    style={{ paddingLeft: `${folder.level * 16 + 12}px` }}
                                                    className="w-full text-left p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 hover:bg-primary/5 hover:border-primary transition-all flex items-center gap-2 group"
                                                >
                                                    <span className="material-symbols-outlined text-amber-500 group-hover:text-primary text-[18px]">folder</span>
                                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{folder.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                                <button
                                    onClick={() => setMoveModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* CNC Import Modal */}
            {
                cncModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-orange-500">warning</span>
                                    Importar Causas de No Cumplimiento desde Maestro Global
                                </h3>
                                <button onClick={() => setCncModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {globalCNCGroups.length === 0 ? (
                                    <div className="text-center py-10 text-slate-400">
                                        <p>No hay grupos en el Maestro Global.</p>
                                    </div>
                                ) : globalCNCGroups.map(group => (
                                    <div key={group.id} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                        <div className="bg-slate-50 dark:bg-slate-800/80 px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                            <span className="font-bold text-slate-800 dark:text-white">{group.name}</span>
                                            <button 
                                                onClick={() => {
                                                    const causeIds = group.causes.map(c => c.id);
                                                    const allIncluded = causeIds.every(id => cncGroups.some(g => g.causes.some(c => c.id === id)));
                                                    
                                                    let newGroups = [...cncGroups];
                                                    
                                                    if (allIncluded) {
                                                        newGroups = newGroups.map(g => ({
                                                            ...g,
                                                            causes: g.causes.filter(c => !causeIds.includes(c.id))
                                                        })).filter(g => g.causes.length > 0);
                                                    } else {
                                                        let targetGroup = newGroups.find(g => g.id === group.id);
                                                        if (!targetGroup) {
                                                            targetGroup = { id: group.id, name: group.name, causes: [] };
                                                            newGroups.push(targetGroup);
                                                        }
                                                        
                                                        causeIds.forEach(id => {
                                                            if (!targetGroup.causes.some(c => c.id === id)) {
                                                                const cause = group.causes.find(c => c.id === id);
                                                                targetGroup.causes.push(cause);
                                                            }
                                                        });
                                                    }
                                                    setCncGroups([...newGroups]);
                                                }}
                                                className="text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-2 py-1 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                                            >
                                                Seleccionar Todo
                                            </button>
                                        </div>
                                        <div className="p-2 bg-white dark:bg-slate-900 grid grid-cols-2 gap-2">
                                            {group.causes.map(cause => {
                                                const isIncluded = cncGroups.some(g => g.causes.some(c => c.id === cause.id));
                                                return (
                                                    <label key={cause.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                                                        <input 
                                                            type="checkbox" 
                                                            className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                                                            checked={isIncluded}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    let targetGroup = cncGroups.find(g => g.id === group.id);
                                                                    if (targetGroup) {
                                                                        setCncGroups(cncGroups.map(g => g.id === group.id ? { ...g, causes: [...g.causes, cause] } : g));
                                                                    } else {
                                                                        setCncGroups([...cncGroups, { id: group.id, name: group.name, causes: [cause] }]);
                                                                    }
                                                                } else {
                                                                    setCncGroups(cncGroups.map(g => {
                                                                        if (g.id === group.id) {
                                                                            return { ...g, causes: g.causes.filter(c => c.id !== cause.id) };
                                                                        }
                                                                        return g;
                                                                    }).filter(g => g.causes.length > 0));
                                                                }
                                                            }}
                                                        />
                                                        <span className="text-sm text-slate-700 dark:text-slate-300">{cause.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end">
                                <button onClick={() => setCncModalOpen(false)} className="bg-primary text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-600 transition-colors">
                                    Cerrar y Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* Constraints Import Modal */}
            {
                constraintsModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-purple-500">block</span>
                                    Importar Restricciones desde Maestro Global
                                </h3>
                                <button onClick={() => setConstraintsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {globalConstraintGroups.length === 0 ? (
                                    <div className="text-center py-10 text-slate-400">
                                        <p>No hay grupos en el Maestro Global.</p>
                                    </div>
                                ) : globalConstraintGroups.map(group => (
                                    <div key={group.id} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                        <div className="bg-slate-50 dark:bg-slate-800/80 px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                            <span className="font-bold text-slate-800 dark:text-white">{group.name}</span>
                                            <button 
                                                onClick={() => {
                                                    const defIds = group.definitions.map(d => d.id);
                                                    const allIncluded = defIds.every(id => constraintGroups.some(g => g.definitions.some(d => d.id === id)));
                                                    
                                                    let newGroups = [...constraintGroups];
                                                    
                                                    if (allIncluded) {
                                                        newGroups = newGroups.map(g => ({
                                                            ...g,
                                                            definitions: g.definitions.filter(d => !defIds.includes(d.id))
                                                        })).filter(g => g.definitions.length > 0);
                                                    } else {
                                                        let targetGroup = newGroups.find(g => g.id === group.id);
                                                        if (!targetGroup) {
                                                            targetGroup = { id: group.id, name: group.name, definitions: [] };
                                                            newGroups.push(targetGroup);
                                                        }
                                                        
                                                        defIds.forEach(id => {
                                                            if (!targetGroup.definitions.some(d => d.id === id)) {
                                                                const def = group.definitions.find(d => d.id === id);
                                                                targetGroup.definitions.push(def);
                                                            }
                                                        });
                                                    }
                                                    setConstraintGroups([...newGroups]);
                                                }}
                                                className="text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-2 py-1 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                                            >
                                                Seleccionar Todo
                                            </button>
                                        </div>
                                        <div className="p-2 bg-white dark:bg-slate-900 grid grid-cols-2 gap-2">
                                            {group.definitions.map(def => {
                                                const isIncluded = constraintGroups.some(g => g.definitions.some(d => d.id === def.id));
                                                return (
                                                    <label key={def.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                                                        <input 
                                                            type="checkbox" 
                                                            className="rounded border-slate-300 text-purple-500 focus:ring-purple-500"
                                                            checked={isIncluded}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    let targetGroup = constraintGroups.find(g => g.id === group.id);
                                                                    if (targetGroup) {
                                                                        setConstraintGroups(constraintGroups.map(g => g.id === group.id ? { ...g, definitions: [...g.definitions, def] } : g));
                                                                    } else {
                                                                        setConstraintGroups([...constraintGroups, { id: group.id, name: group.name, definitions: [def] }]);
                                                                    }
                                                                } else {
                                                                    setConstraintGroups(constraintGroups.map(g => {
                                                                        if (g.id === group.id) {
                                                                            return { ...g, definitions: g.definitions.filter(d => d.id !== def.id) };
                                                                        }
                                                                        return g;
                                                                    }).filter(g => g.definitions.length > 0));
                                                                }
                                                            }}
                                                        />
                                                        <span className="text-sm text-slate-700 dark:text-slate-300">{def.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end">
                                <button onClick={() => setConstraintsModalOpen(false)} className="bg-primary text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-600 transition-colors">
                                    Cerrar y Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Plan Task Modal */}
            {
                planTaskModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-6xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">Programar Tarea</h3>
                                <div className="flex items-center gap-4">
                                    {activePlanId && (
                                        <button
                                            onClick={() => exportWeeklyPlanToExcel(activePlanId)}
                                            className="bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 px-3 py-1.5 rounded text-sm font-bold transition-colors flex items-center gap-1"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">download</span>
                                            <span>Exportar las Tareas</span>
                                        </button>
                                    )}
                                    <button onClick={() => setPlanTaskModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                                </div>
                            </div>
                            <div className="px-6 pb-6 pt-0 overflow-y-auto relative">
                                <table className="w-full text-left border-collapse">
                                    <thead className="sticky top-0 z-[50] bg-slate-50 dark:bg-slate-800 shadow-md border-b dark:border-slate-700">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-10 text-center bg-slate-50 dark:bg-slate-800">
                                                <span className="material-symbols-outlined text-[16px]">check_box</span>
                                            </th>
                                            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase bg-slate-50 dark:bg-slate-800">Nivel</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase bg-slate-50 dark:bg-slate-800">Unidades Físicas</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase bg-slate-50 dark:bg-slate-800">Tarea</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-48 bg-slate-50 dark:bg-slate-800">Responsable</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-28 text-center bg-slate-50 dark:bg-slate-800">% Comprometido</th>
                                            <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-28 text-center bg-slate-50 dark:bg-slate-800">% Cumplimiento Acum.</th>
                                            <th className="px-2 py-3 text-xs font-semibold text-slate-500 text-center w-10 bg-slate-50 dark:bg-slate-800">L</th>
                                            <th className="px-2 py-3 text-xs font-semibold text-slate-500 text-center w-10 bg-slate-50 dark:bg-slate-800">M</th>
                                            <th className="px-2 py-3 text-xs font-semibold text-slate-500 text-center w-10 bg-slate-50 dark:bg-slate-800">X</th>
                                            <th className="px-2 py-3 text-xs font-semibold text-slate-500 text-center w-10 bg-slate-50 dark:bg-slate-800">J</th>
                                            <th className="px-2 py-3 text-xs font-semibold text-slate-500 text-center w-10 bg-slate-50 dark:bg-slate-800">V</th>
                                            <th className="px-2 py-3 text-xs font-semibold text-slate-500 text-center w-10 bg-slate-50 dark:bg-slate-800">S</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                        {currentAllocations.length === 0 ? (
                                            <tr><td colSpan={13} className="px-4 py-8 text-center text-slate-400 italic">No hay Unidades Físicas asignadas a esta tarea en el EDT (Marcadas con "1").</td></tr>
                                        ) : (
                                            currentAllocations.map((alloc, idx) => {
                                                const allocUnit = units.find(u => u.id === alloc.unitId);
                                                const unitName = allocUnit?.name;
                                                const allocLevelName = levels.find(l => l.id === allocUnit?.levelId)?.name || '-';
                                                const taskName = planningTaskId ? findTaskName(planningTaskId, tasks) : "";

                                                return (
                                                    <tr key={alloc.unitId} className={`hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${(!alloc.selected && !alloc.isRestricted && !alloc.isPreviouslyExecuted) ? 'opacity-60' : ''} ${alloc.isRestricted ? 'bg-red-50/30 dark:bg-red-900/10' : ''} ${alloc.isPreviouslyExecuted ? 'bg-green-50/20 dark:bg-green-900/10' : ''}`}>
                                                        <td className="px-4 py-3 text-center">
                                                            {alloc.isPreviouslyExecuted ? (
                                                                <div className="flex items-center justify-center cursor-not-allowed" title="Tarea Ejecutada">
                                                                    <span className="material-symbols-outlined text-green-500 text-[20px]">check_circle</span>
                                                                </div>
                                                            ) : alloc.isRestricted ? (
                                                                <div className="flex items-center justify-center cursor-help" title="Cuenta con restricciones Activas">
                                                                    <span className="material-symbols-outlined text-amber-500 text-[20px] animate-pulse">warning</span>
                                                                </div>
                                                            ) : (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={alloc.selected}
                                                                    onChange={() => toggleRowSelection(idx)}
                                                                    className="rounded border-slate-300 text-primary focus:ring-primary size-4 cursor-pointer"
                                                                />
                                                            )}
                                                        </td>
                                                        <td className={`px-4 py-3 text-sm text-slate-500 dark:text-slate-400`}>{allocLevelName}</td>
                                                        <td className={`px-4 py-3 text-sm text-slate-700 dark:text-slate-300 font-medium`}>{unitName}</td>
                                                        <td className={`px-4 py-3 text-sm text-slate-600 dark:text-slate-400`}>
                                                            <div className="flex items-center gap-2">
                                                                {taskName}
                                                                {alloc.isReprogrammed && !alloc.isPreviouslyExecuted && (
                                                                    <span className="material-symbols-outlined text-[16px] text-blue-500" title="Reprogramable">update</span>
                                                                )}
                                                                {alloc.isPreviouslyExecuted && (
                                                                    <span className="text-[10px] uppercase font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded border border-green-200">Ejecutado</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <select
                                                                className="w-full text-xs border border-slate-300 rounded p-1.5 bg-white dark:bg-slate-800 dark:border-slate-600 outline-none focus:border-primary disabled:bg-slate-100 dark:disabled:bg-slate-900 disabled:cursor-not-allowed"
                                                                value={alloc.responsibleId}
                                                                onChange={(e) => updateAllocationField(idx, 'responsibleId', e.target.value)}
                                                                disabled={!alloc.selected || alloc.isPreviouslyExecuted}
                                                            >
                                                                <option value="">Seleccionar...</option>
                                                                {responsibles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.role})</option>)}
                                                            </select>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                             {(() => {
                                                                 let accumulatedCompliance = 0;
                                                                 weeklyPlans
                                                                     .filter(p => p.status === 'Cerrada')
                                                                     .flatMap(p => p.allocations)
                                                                     .forEach(a => {
                                                                         if (a.taskId === alloc.taskId && a.unitId === alloc.unitId) {
                                                                             accumulatedCompliance += a.percentCumplimiento ?? (a.executed ? 100 : 0);
                                                                         }
                                                                     });
                                                                 accumulatedCompliance = Math.min(100, accumulatedCompliance);
                                                                 const maxComp = 100 - accumulatedCompliance;

                                                                 return (
                                                                     <input
                                                                         type="number"
                                                                         min="0"
                                                                         max={maxComp}
                                                                         disabled={!alloc.selected || alloc.isPreviouslyExecuted}
                                                                         className="w-20 text-center text-xs p-1.5 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white disabled:opacity-50"
                                                                         value={alloc.percentComprometido ?? maxComp}
                                                                         onChange={(e) => {
                                                                             const val = parseFloat(e.target.value) || 0;
                                                                             if (val > maxComp) {
                                                                                 alert(`El porcentaje comprometido no puede superar el saldo restante (${maxComp}%).`);
                                                                                 updateAllocationField(idx, 'percentComprometido', maxComp);
                                                                             } else if (val < 0) {
                                                                                 updateAllocationField(idx, 'percentComprometido', 0);
                                                                             } else {
                                                                                 updateAllocationField(idx, 'percentComprometido', val);
                                                                             }
                                                                         }}
                                                                     />
                                                                 );
                                                             })()}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-sm font-semibold text-slate-500 dark:text-slate-400">
                                                             {(() => {
                                                                 let accumulatedCompliance = 0;
                                                                 weeklyPlans
                                                                     .filter(p => p.status === 'Cerrada')
                                                                     .flatMap(p => p.allocations)
                                                                     .forEach(a => {
                                                                         if (a.taskId === alloc.taskId && a.unitId === alloc.unitId) {
                                                                             accumulatedCompliance += a.percentCumplimiento ?? (a.executed ? 100 : 0);
                                                                         }
                                                                     });
                                                                 return `${Math.min(100, accumulatedCompliance)}%`;
                                                             })()}
                                                        </td>
                                                        {['L', 'M', 'X', 'J', 'V', 'S'].map(day => (
                                                            <td key={day} className={`px-2 py-3 text-center select-none ${alloc.selected && !alloc.isPreviouslyExecuted ? 'cursor-pointer' : 'cursor-not-allowed'}`} onClick={() => alloc.selected && !alloc.isPreviouslyExecuted && toggleDay(idx, day)}>
                                                                <div className={`size-7 rounded mx-auto flex items-center justify-center border transition-colors ${alloc.days[day] ? (alloc.isPreviouslyExecuted ? 'bg-green-500 border-green-500 text-white opacity-50' : 'bg-primary border-primary text-white') : 'bg-white border-slate-300 text-transparent hover:bg-slate-50 hover:border-slate-400 dark:bg-slate-800 dark:border-slate-600'} ${alloc.isPreviouslyExecuted ? 'opacity-50' : ''}`}>
                                                                    <span className="font-bold text-xs">X</span>
                                                                </div>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                                <button onClick={() => setPlanTaskModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded">Cancelar</button>
                                <button onClick={handleSaveAllocations} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded shadow-sm">Guardar Programación</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Nueva Planificación Modal (Weekly) */}
            {
                newPlanModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[400px] border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">{editingWeeklyPlanId ? 'Editar Fechas Semanales' : 'Nueva Planificación Semanal'}</h3>
                                <button onClick={() => { setNewPlanModalOpen(false); setEditingWeeklyPlanId(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-6 flex flex-col gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Inicio</label>
                                    <input
                                        type="date"
                                        value={weeklyPlanDates.start}
                                        onChange={(e) => setWeeklyPlanDates({ ...weeklyPlanDates, start: e.target.value })}
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-primary outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Término</label>
                                    <input
                                        type="date"
                                        value={weeklyPlanDates.end}
                                        onChange={(e) => setWeeklyPlanDates({ ...weeklyPlanDates, end: e.target.value })}
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-primary outline-none"
                                    />
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                                <button
                                    onClick={() => { setNewPlanModalOpen(false); setEditingWeeklyPlanId(null); }}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreateWeeklyPlan}
                                    className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded transition-colors shadow-sm"
                                >
                                    {editingWeeklyPlanId ? 'Guardar Cambios' : 'Comenzar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* MOCK Document Manager Modal */}
            {
                docManagerModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-[600px] border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col animate-scale-up">
                            <div className="flex justify-between items-center p-5 border-b border-slate-100 dark:border-slate-800 bg-emerald-500 text-white">
                                <div>
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        <span className="material-symbols-outlined">folder_managed</span>
                                        Seleccionar desde Gestor Documental
                                    </h3>
                                    <p className="text-emerald-100 text-xs mt-1">
                                        Mostrando archivos vinculados a {projectInfo.name}
                                    </p>
                                </div>
                                <button onClick={() => setDocManagerModalOpen(false)} className="hover:bg-white/20 p-2 rounded-full transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="p-0 max-h-[400px] overflow-y-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0">
                                        <tr>
                                            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Archivo</th>
                                            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Estado</th>
                                            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {[
                                            { id: 1, name: 'Cronograma_Maestro_V2.mpp', date: 'Hoy, 10:30', version: 2, status: 'Vigente' },
                                            { id: 2, name: 'Cronograma_Maestro_V1.mpp', date: '20 Mar 2026', version: 1, status: 'Histórico' }
                                        ].map(doc => (
                                            <tr key={doc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                <td className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl flex items-center justify-center">
                                                            <span className="material-symbols-outlined">account_tree</span>
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-sm text-slate-800 dark:text-white">{doc.name}</p>
                                                            <p className="text-xs text-slate-500 mt-0.5">Subido el {doc.date} • Versión {doc.version}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${doc.status === 'Vigente'
                                                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                                        }`}>
                                                        {doc.status}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-right">
                                                    <button
                                                        onClick={() => {
                                                            alert(`Simulación: Archivo "${doc.name}" seleccionado correctamente del Gestor Documental.\n\nEn la versión de producción (Supabase), esto descargará e inyectará el MS Project transparente para el Project Manager.`);
                                                            setDocManagerModalOpen(false);
                                                        }}
                                                        className="px-4 py-2 bg-slate-100 hover:bg-emerald-500 text-slate-700 hover:text-white dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-emerald-600 text-sm font-bold rounded-lg transition-colors"
                                                    >
                                                        Importar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-[#1e2c3c] flex justify-between items-center border-t border-slate-100 dark:border-slate-800">
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[14px]">info</span>
                                    Mostrando datos simulados
                                </span>
                                <button
                                    onClick={() => setDocManagerModalOpen(false)}
                                    className="px-5 py-2 text-sm font-bold text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Calendar Configuration Modal */}
            {calendarModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 max-h-[85vh]">
                        {/* Header */}
                        <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1e2c3c]">
                            <div>
                                <h3 className="text-xl font-black text-slate-800 dark:text-white tracking-tight">Configuración de Calendario</h3>
                                <p className="text-sm text-slate-500 font-medium mt-1">Configura la jornada laboral y los días festivos del proyecto (estilo MS Project)</p>
                            </div>
                            <button onClick={() => setCalendarModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors bg-white dark:bg-slate-800 p-2 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto space-y-6 bg-white dark:bg-[#1a2634] flex-1">
                            {/* Standard Work Week */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Jornada Laboral Estándar</h4>
                                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Selecciona los días hábiles de la semana para la ejecución de las tareas:</p>
                                    <div className="grid grid-cols-7 gap-2">
                                        {['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((day, idx) => {
                                            const isChecked = tempWorkingDays[idx];
                                            return (
                                                <label
                                                    key={day}
                                                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                        isChecked
                                                            ? 'bg-blue-50/70 border-blue-500 text-blue-700 dark:bg-blue-900/20 dark:border-blue-500 dark:text-blue-400 font-bold'
                                                            : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400 dark:hover:bg-slate-800/50'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only"
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                            const updated = [...tempWorkingDays];
                                                            updated[idx] = e.target.checked;
                                                            setTempWorkingDays(updated);
                                                        }}
                                                    />
                                                    <span className="text-xs uppercase font-semibold">{day.slice(0, 3)}</span>
                                                    <span className="material-symbols-outlined text-lg mt-1">
                                                        {isChecked ? 'check_circle' : 'circle'}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Exceptions */}
                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Excepciones (Días Festivos / No Laborables)</h4>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTempHolidays([...tempHolidays, { date: getLocalDateStr(new Date()), name: 'Nuevo Festivo' }]);
                                        }}
                                        className="flex items-center gap-1 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline bg-blue-50 dark:bg-blue-950/30 px-3 py-1.5 rounded-lg border border-blue-100 dark:border-blue-900/40"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">add</span>
                                        + Agregar Día Festivo
                                    </button>
                                </div>

                                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm">
                                    <div className="max-h-64 overflow-y-auto">
                                        {tempHolidays.length === 0 ? (
                                            <div className="p-8 text-center text-slate-400 dark:text-slate-500 flex flex-col items-center">
                                                <span className="material-symbols-outlined text-3xl mb-1">event_busy</span>
                                                <p className="text-xs font-medium">No hay días festivos configurados.</p>
                                            </div>
                                        ) : (
                                            <table className="w-full text-left border-collapse">
                                                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                                                    <tr>
                                                        <th className="px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider w-2/3">Descripción del Festivo</th>
                                                        <th className="px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider w-1/3">Fecha</th>
                                                        <th className="px-4 py-3 w-12 text-center"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                    {tempHolidays.map((holiday, idx) => (
                                                        <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20">
                                                            <td className="px-4 py-2">
                                                                <input
                                                                    type="text"
                                                                    value={holiday.name}
                                                                    onChange={(e) => {
                                                                        const updated = [...tempHolidays];
                                                                        updated[idx].name = e.target.value;
                                                                        setTempHolidays(updated);
                                                                    }}
                                                                    className="w-full text-xs p-2 border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 dark:text-white focus:border-blue-500 outline-none"
                                                                    placeholder="Nombre del festivo (ej. Año Nuevo)"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2">
                                                                <input
                                                                    type="date"
                                                                    value={holiday.date}
                                                                    onChange={(e) => {
                                                                        const updated = [...tempHolidays];
                                                                        updated[idx].date = e.target.value;
                                                                        setTempHolidays(updated);
                                                                    }}
                                                                    className="w-full text-xs p-2 border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 dark:text-white focus:border-blue-500 outline-none"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setTempHolidays(tempHolidays.filter((_, i) => i !== idx));
                                                                    }}
                                                                    className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                                                                >
                                                                    <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 bg-slate-50 dark:bg-[#1e2c3c] border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 shrink-0">
                            <button
                                onClick={() => setCalendarModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveCalendar}
                                className="px-6 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
                            >
                                <span className="material-symbols-outlined text-[18px]">save</span>
                                Guardar Configuración
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Gantt Modal */}
            {
                importModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                        <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col animate-scale-up">
                            <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1e2c3c]">
                                <div>
                                    <h3 className="text-xl font-black text-slate-800 dark:text-white tracking-tight">Importar Carta Gantt</h3>
                                    <p className="text-sm text-slate-500 font-medium mt-1">Selecciona el origen del archivo de planificación</p>
                                </div>
                                <button onClick={() => setImportModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors bg-white dark:bg-slate-800 p-2 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/50 dark:bg-[#0d131b]/50">
                                {/* Option 1: Local PC */}
                                <label className="relative group flex flex-col items-center justify-center p-8 bg-white dark:bg-[#1a2634] border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl hover:border-primary dark:hover:border-primary hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all cursor-pointer overflow-hidden">
                                    <input
                                        type="file"
                                        accept=".xml,.mpp"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        onChange={handleFileImport}
                                    />
                                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-primary rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                                        <span className="material-symbols-outlined text-3xl">upload_file</span>
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-800 dark:text-white mb-2 text-center group-hover:text-primary transition-colors">Subir desde mi PC</h4>
                                    <p className="text-xs text-slate-500 text-center font-medium px-4">Archivos locales .xml o .mpp</p>

                                    <div className="absolute inset-0 border-2 border-primary rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"></div>
                                </label>

                                {/* Option 2: Document Manager */}
                                <div
                                    onClick={() => {
                                        setDocManagerModalType('import');
                                        setDocManagerModalOpen(true);
                                        setImportModalOpen(false);
                                    }}
                                    className="relative group flex flex-col items-center justify-center p-8 bg-white dark:bg-[#1a2634] border-2 border-slate-200 dark:border-slate-800 rounded-2xl hover:border-emerald-500 dark:hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-all cursor-pointer shadow-sm"
                                >
                                    <div className="absolute top-4 right-4 flex h-6 w-6">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-6 w-6 bg-emerald-500 text-white items-center justify-center">
                                            <span className="material-symbols-outlined text-[14px]">star</span>
                                        </span>
                                    </div>

                                    <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                                        <span className="material-symbols-outlined text-3xl">folder_managed</span>
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-800 dark:text-white mb-2 text-center group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">Gestor Documental</h4>
                                    <p className="text-xs text-slate-500 text-center font-medium px-4">Seleccionar un archivo previamente cargado o vigente</p>
                                </div>
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-[#1e2c3c] flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    onClick={() => setImportModalOpen(false)}
                                    className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* NEW Unit Selection Modal */}
            {
                unitSelectionModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-[500px] border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary">account_tree</span>
                                    Seleccionar Unidades Físicas
                                </h3>
                                <button onClick={() => setUnitSelectionModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-4 flex gap-2 border-b border-slate-100 dark:border-slate-800">
                                <button
                                    onClick={() => {
                                        setNewConstraintData({ ...newConstraintData, unitsType: 'na', unitIds: [] });
                                        setUnitSelectionModalOpen(false);
                                    }}
                                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-bold transition-all ${newConstraintData.unitsType === 'na' ? 'bg-slate-100 border-slate-400 text-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                >
                                    No Aplica
                                </button>
                                <button
                                    onClick={() => {
                                        const availableUnitsForTask = activeConstraintTask ? units.filter(u => matrixData[`planning_${activeConstraintTask}_${u.id}`] === '1') : [];
                                        setNewConstraintData({
                                            ...newConstraintData,
                                            unitsType: 'all',
                                            unitIds: availableUnitsForTask.map(u => u.id)
                                        });
                                    }}
                                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-bold transition-all ${newConstraintData.unitsType === 'all' ? 'bg-primary border-primary text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                >
                                    Seleccionar Todo
                                </button>
                            </div>
                                <div className="overflow-x-auto p-4">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                                <th className="px-3 py-2 w-10 text-center"></th>
                                                <th className="px-3 py-2 text-xs font-bold text-slate-500 uppercase">Nivel</th>
                                                <th className="px-3 py-2 text-xs font-bold text-slate-500 uppercase">Sitio</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {activeConstraintTask && units.filter(u => matrixData[`planning_${activeConstraintTask}_${u.id}`] === '1').map(unit => {
                                                const level = levels.find(l => l.id === unit.levelId);
                                                return (
                                                    <tr
                                                        key={unit.id}
                                                        onClick={() => {
                                                            let newUnits = [...newConstraintData.unitIds];
                                                            if (newUnits.includes(unit.id)) {
                                                                newUnits = newUnits.filter(id => id !== unit.id);
                                                            } else {
                                                                newUnits.push(unit.id);
                                                            }
                                                            setNewConstraintData({
                                                                ...newConstraintData,
                                                                unitsType: newUnits.length > 0 ? 'custom' : 'na',
                                                                unitIds: newUnits
                                                            });
                                                        }}
                                                        className={`cursor-pointer transition-colors ${newConstraintData.unitIds.includes(unit.id) ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                                    >
                                                        <td className="px-3 py-3 text-center">
                                                            <div className={`size-5 mx-auto rounded flex items-center justify-center border ${newConstraintData.unitIds.includes(unit.id) ? 'bg-primary border-primary text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'}`}>
                                                                {newConstraintData.unitIds.includes(unit.id) && <span className="material-symbols-outlined text-[14px] font-bold">check</span>}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-3 text-sm text-slate-500 font-medium">
                                                            {level ? level.name : '-'}
                                                        </td>
                                                        <td className={`px-3 py-3 text-sm ${newConstraintData.unitIds.includes(unit.id) ? 'text-primary font-bold' : 'text-slate-700 dark:text-slate-300'}`}>
                                                            {unit.name}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                                <button onClick={() => setUnitSelectionModalOpen(false)} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded shadow-sm">Confirmar</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* NEW Units View Modal (Read Only) */}
            {
                unitsViewModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-[400px] border border-slate-200 dark:border-slate-800 flex flex-col max-h-[70vh]">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary">visibility</span>
                                    Unidades Físicas
                                </h3>
                                <button onClick={() => setUnitsViewModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                {(() => {
                                    const constraint = lookaheadPlans.flatMap(p => p.constraints).find(c => c.id === activeConstraintForUnits);
                                    if (!constraint) return null;

                                    if (constraint.unitsType === 'all') {
                                        return <div className="text-sm font-bold text-primary p-4 bg-primary/5 rounded-lg border border-primary/10 text-center">Aplica a TODAS las Unidades Físicas</div>;
                                    }

                                    return (
                                        <div className="space-y-2">
                                            {constraint.unitIds?.map(uid => {
                                                const unit = units.find(u => u.id === uid);
                                                return (
                                                    <div key={uid} className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                                        <span className="material-symbols-outlined text-[18px] text-slate-400">check_circle</span>
                                                        {unit?.name}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                                <button onClick={() => setUnitsViewModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors">Cerrar</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Export Gantt Modal */}
            {
                exportModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[400px] border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">Exportar Carta Gantt</h3>
                                <button onClick={() => setExportModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-6 flex flex-col gap-4">
                                <p className="text-sm text-slate-600 dark:text-slate-400">Generar archivo compatible con:</p>
                                <div className="flex flex-col gap-2">
                                    <button 
                                        onClick={handleExportToMSProject}
                                        className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left group w-full"
                                    >
                                        <span className="material-symbols-outlined text-blue-600 group-hover:scale-110 transition-transform">calendar_month</span>
                                        <div>
                                            <p className="text-sm font-bold text-slate-800 dark:text-white">Microsoft Project (.xml)</p>
                                            <p className="text-xs text-slate-500">Formato de intercambio estándar</p>
                                        </div>
                                    </button>
                                    <button 
                                        onClick={handleExportToMSProject}
                                        className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left group w-full"
                                    >
                                        <span className="material-symbols-outlined text-indigo-600 group-hover:scale-110 transition-transform">article</span>
                                        <div>
                                            <p className="text-sm font-bold text-slate-800 dark:text-white">Primavera P6 (.xml)</p>
                                            <p className="text-xs text-slate-500">Esquema XML compatible (MSPDI)</p>
                                        </div>
                                    </button>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                                <button
                                    onClick={() => setExportModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* Reprogramming History Modal */}
            {historyModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">history</span>
                                Historial de Reprogramaciones
                            </h3>
                            <button onClick={() => setHistoryModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6">
                            {baselines.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                                    <span className="material-symbols-outlined text-5xl mb-2">history</span>
                                    <p>No hay reprogramaciones guardadas.</p>
                                </div>
                            ) : (
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-700">
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">N°</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Fecha Carga</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Inicio-Termino</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Comentario</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-right">Comparar</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {baselines.map(b => (
                                            <tr key={b.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${comparisonBaselineId === b.id ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                                                <td className="px-4 py-4 font-bold text-blue-600">#{b.versionNumber}</td>
                                                <td className="px-4 py-4 text-xs text-slate-600 dark:text-slate-400">{new Date(b.uploadDate).toLocaleString()}</td>
                                                <td className="px-4 py-4 text-xs font-medium text-slate-700 dark:text-slate-300">
                                                    {b.startDate} al {b.endDate}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate" title={b.comment}>
                                                    {b.comment}
                                                </td>
                                                <td className="px-4 py-4 text-right">
                                                    <button
                                                        onClick={() => {
                                                            if (comparisonBaselineId === b.id) {
                                                                setComparisonBaselineId(null);
                                                                setShowComparison(false);
                                                            } else {
                                                                setComparisonBaselineId(b.id);
                                                                setShowComparison(true);
                                                                setHistoryModalOpen(false);
                                                            }
                                                        }}
                                                        className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${comparisonBaselineId === b.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600'}`}
                                                    >
                                                        {comparisonBaselineId === b.id ? 'Comparando' : 'Comparar'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                            {showComparison && (
                                <button
                                    onClick={() => { setShowComparison(false); setComparisonBaselineId(null); }}
                                    className="mr-auto text-red-600 hover:text-red-700 text-sm font-bold flex items-center gap-1"
                                >
                                    <span className="material-symbols-outlined text-lg">visibility_off</span>
                                    Quitar Comparación
                                </button>
                            )}
                            <button onClick={() => setHistoryModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors">Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reprogramming Upload Modal */}
            {reprogrammingModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">update</span>
                                Cargar Reprogramación
                            </h3>
                        </div>
                        <div className="p-6 flex flex-col gap-4">
                            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800 flex gap-3 text-amber-700 dark:text-amber-400">
                                <span className="material-symbols-outlined shrink-0 text-xl">info</span>
                                <p className="text-xs font-medium">Esta acción guardará una copia de la Carta Gantt actual en el historial y fusionará el nuevo archivo respetando los avances existentes.</p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Motivo / Comentario</label>
                                <textarea
                                    className="w-full border rounded-lg p-3 text-sm dark:bg-slate-800 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 h-24"
                                    placeholder="Describa el motivo de esta reprogramación..."
                                    value={reprogrammingComment}
                                    onChange={(e) => setReprogrammingComment(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3 mt-2">
                                {/* Opción 1: PC Local */}
                                <div className="relative group">
                                    <input
                                        type="file"
                                        accept=".xml,.mpp"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        id="reprogramming-file-input"
                                    />
                                    <div className="flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 group-hover:border-blue-500 transition-all text-center">
                                        <span className="material-symbols-outlined text-2xl text-slate-400 group-hover:text-blue-500 mb-1">upload_file</span>
                                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300 group-hover:text-blue-600">Mi PC Local</span>
                                    </div>
                                </div>
                                {/* Opción 2: Gestor Documental */}
                                <div
                                    onClick={() => {
                                        setDocManagerModalType('reprogram');
                                        setDocManagerModalOpen(true);
                                        setReprogrammingModalOpen(false);
                                    }}
                                    className="flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-500 transition-all cursor-pointer text-center relative"
                                >
                                    <span className="absolute top-2 right-2 flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                                    </span>
                                    <span className="material-symbols-outlined text-2xl text-slate-400 hover:text-emerald-500 mb-1">folder_managed</span>
                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Gestor Documental</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                            <button
                                onClick={() => setReprogrammingModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    if (!reprogrammingComment.trim()) {
                                        alert("Por favor, ingrese un motivo para la reprogramación.");
                                        return;
                                    }
                                    const input = document.getElementById('reprogramming-file-input') as HTMLInputElement;
                                    if (input?.files?.[0]) {
                                        (window as any).__REPROGRAMMING_COMMENT = reprogrammingComment;
                                        handleFileImport({ target: input } as any);
                                    } else {
                                        alert("Por favor, seleccione un archivo.");
                                    }
                                }}
                                className="px-6 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors shadow-sm"
                            >
                                Procesar Reprogramación
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sync Progress Modal */}
            {syncProgressModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
                        <div className="bg-[#1a73e8] p-6 text-white flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <span className="material-symbols-outlined">history_toggle_off</span>
                                    Sincronizar Avance Semanal
                                </h2>
                                <p className="text-blue-100 text-sm mt-1">Registre el avance fotografiado para una fecha específica.</p>
                                <div className="mt-3 flex items-center gap-2 bg-blue-800/40 px-3 py-1.5 rounded-lg border border-blue-400/30 w-fit">
                                    <span className="material-symbols-outlined text-[16px]">info</span>
                                    <span className="text-[10px] font-medium leading-tight">
                                        Cada sincronización captura el avance <b>calculado en vivo</b> del proyecto.<br />
                                        Este valor puede diferir de la curva S histórica. Puedes usar "Re-Sincronizar" para actualizar una fecha.
                                    </span>
                                </div>
                            </div>
                            <button onClick={() => setSyncProgressModalOpen(false)} className="hover:bg-white/20 p-2 rounded-full transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl mb-6 flex items-center justify-between border border-blue-100 dark:border-blue-900/30">
                                <div>
                                    <label className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block mb-1">Día de Sincronización</label>
                                    <select
                                        className="bg-transparent font-bold text-slate-800 dark:text-white outline-none cursor-pointer"
                                        value={syncDay}
                                        onChange={(e) => setSyncDay(parseInt(e.target.value))}
                                    >
                                        <option value={1}>Lunes</option>
                                        <option value={2}>Martes</option>
                                        <option value={3}>Miércoles</option>
                                        <option value={4}>Jueves</option>
                                        <option value={5}>Viernes</option>
                                        <option value={6}>Sábado</option>
                                        <option value={0}>Domingo</option>
                                    </select>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Avance Calculado (En vivo)</p>
                                    <p className="text-xl font-black text-blue-600 dark:text-blue-400">{truncateProgress(calculateTotalProjectProgress())}%</p>
                                </div>
                            </div>

                            <div className="max-h-[400px] overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                                        <tr className="border-b border-slate-200 dark:border-slate-700">
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase">Semana</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase">Fecha</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase">Estado Sincronización</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase text-center">% Semanal</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase text-center">Atraso C.S.</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase text-center">Atraso R.C.</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase text-right">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {(() => {
                                            const allDates = getDatesForWeekday(projectInfo.startDate, projectInfo.endDate, syncDay);
                                            return allDates.map((date, index) => {
                                                const record = progressHistory.find(r => r.date === date);
                                                let weeklyGain: number | null = null;

                                                if (record) {
                                                    const prevDate = allDates[index - 1];
                                                    const prevRecord = prevDate ? progressHistory.find(r => r.date === prevDate) : null;
                                                    if (prevRecord) {
                                                        weeklyGain = record.progress - prevRecord.progress;
                                                    } else if (index === 0) {
                                                        weeklyGain = record.progress;
                                                    }
                                                }

                                                return (
                                                    <tr key={date} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                                        <td className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-tight">Semana {index + 1}</td>
                                                        <td className="px-4 py-4 text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">{date}</td>
                                                        <td className="px-4 py-4">
                                                            {record ? (
                                                                <span className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px] font-bold px-2.5 py-1 rounded-full border border-green-200 dark:border-green-800 flex items-center gap-1 w-fit whitespace-nowrap">
                                                                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                                                    Acumulado: {truncateProgress(record.progress)}%
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-400 text-[10px] italic">Pendiente</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 text-center">
                                                            {weeklyGain !== null && (
                                                                <span className="text-blue-600 dark:text-blue-400 font-bold text-xs">
                                                                    {weeklyGain >= 0 ? '+' : ''}{truncateProgress(weeklyGain)}%
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 text-center">
                                                            <span className={`text-[11px] font-bold ${record?.delayCurvaS && record.delayCurvaS > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                                                                {record ? `${record.delayCurvaS || 0}d` : '--'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-4 text-center">
                                                            {record ? (
                                                                <span className={`text-[11px] font-bold ${record.delayRutaCritica && record.delayRutaCritica > 0 ? 'text-orange-500' : 'text-slate-400'}`}>
                                                                    {record.delayRutaCritica || 0}d
                                                                </span>
                                                            ) : (
                                                                <input
                                                                    type="number"
                                                                    className="w-12 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 text-xs text-center outline-none focus:ring-1 focus:ring-blue-500"
                                                                    placeholder="0"
                                                                    min="0"
                                                                    value={manualCriticalPathDelays[date] || 0}
                                                                    onChange={(e) => setManualCriticalPathDelays(prev => ({ ...prev, [date]: parseInt(e.target.value) || 0 }))}
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 text-right">
                                                            <button
                                                                onClick={() => {
                                                                    const currentProgress = calculateTotalProjectProgress();
                                                                    const curveData = calculateSCurve(tasks, progressHistory);
                                                                    const delayCS = calculateDelayDays(curveData, currentProgress, date);
                                                                    handleSyncProgressToDate(date, delayCS, manualCriticalPathDelays[date] || 0);
                                                                }}
                                                                className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all shadow-sm whitespace-nowrap ${record ? 'bg-slate-100 text-slate-500 hover:bg-slate-200' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                                            >
                                                                {record ? 'Re-Sincronizar' : 'Sincronizar'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                            <button
                                onClick={() => setSyncProgressModalOpen(false)}
                                className="px-6 py-2 text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Observaciones al Liberar / No Liberar Restricción */}
            {constraintObsModalOpen && constraintObsData && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[70]">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-fade-in-up">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                            <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase">
                                {constraintObsData.currentStatus === 'NO LIBERADO' ? 'No Liberar Restricción' : 'Liberar Restricción'}
                            </h3>
                            <button onClick={() => setConstraintObsModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-5 space-y-4 text-xs">
                            <div className="text-slate-500 mb-2">
                                Has marcado como <strong className="text-slate-700 dark:text-slate-300">{constraintObsData.currentStatus}</strong> la restricción: <strong className="text-slate-700 dark:text-slate-300">{constraintObsData.description}</strong>. Ingresa alguna observación o evidencia si es necesario.
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Observaciones</label>
                                <textarea
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-primary/50 text-slate-900 dark:text-white text-xs h-24 resize-none"
                                    placeholder={constraintObsData.currentStatus === 'NO LIBERADO' ? "Justifica por qué no se liberó esta restricción..." : "Justifica la liberación de esta restricción..."}
                                    value={constraintObsData.observations}
                                    onChange={(e) => setConstraintObsData({ ...constraintObsData, observations: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
                            <button
                                onClick={() => setConstraintObsModalOpen(false)}
                                className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleReleaseConstraintWithObservation(constraintObsData.planId, constraintObsData.constraintId, constraintObsData.observations, constraintObsData.currentStatus as any)}
                                className={`px-5 py-2 text-white rounded-xl text-xs font-bold shadow-md active:scale-95 transition-all ${constraintObsData.currentStatus === 'NO LIBERADO' ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-500/20' : 'bg-green-600 hover:bg-green-700 shadow-green-500/20'}`}
                            >
                                {constraintObsData.currentStatus === 'NO LIBERADO' ? 'Confirmar No Liberado' : 'Confirmar Liberación'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Asignación Masiva de Responsable (Gantt Auto-Plan) */}
            {ganttAutoPlanModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">group</span>
                                <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">
                                    Asignación Masiva
                                </h3>
                            </div>
                            <button onClick={() => setGanttAutoPlanModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        
                        <div className="p-5 space-y-4">
                            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                Selecciona el responsable a quien se le asignarán todas las unidades físicas autoprogramadas sugeridas por la Carta Gantt:
                            </p>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Responsable</label>
                                <select
                                    value={ganttAutoPlanSelectedRespId}
                                    onChange={(e) => setGanttAutoPlanSelectedRespId(e.target.value)}
                                    className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                                >
                                    <option value="">-- Sin asignar --</option>
                                    {responsibles.map(r => (
                                        <option key={r.id} value={r.id}>
                                            {r.name} ({r.role})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        <div className="p-4 bg-slate-50 dark:bg-slate-850/50 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
                            <button
                                onClick={() => setGanttAutoPlanModalOpen(false)}
                                className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    handleAutoPlanGanttTasks(ganttAutoPlanSelectedRespId);
                                    setGanttAutoPlanModalOpen(false);
                                }}
                                className="px-5 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 active:scale-95 transition-all"
                            >
                                Auto-Programar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Editar Asignación Semanal Individual (Icono de lápiz) */}
            {isEditingAllocation && editingAllocationData && (() => {
                const taskObjForModal = findTaskById(editingAllocationData.taskId, tasks);
                const parentTaskForModal = findParentTask(editingAllocationData.taskId, tasks);
                const targetTaskIdForModal = (taskObjForModal?.isWeeklySubtask && parentTaskForModal) ? parentTaskForModal.id : editingAllocationData.taskId;
                const appliesProtocolForModal = protocolConfigs[targetTaskIdForModal]?.applies ?? false;

                let accumulatedComplianceForModal = 0;
                weeklyPlans
                    .filter(p => p.status === 'Cerrada')
                    .flatMap(p => p.allocations)
                    .forEach(a => {
                        if (a.taskId === editingAllocationData.taskId && a.unitId === editingAllocationData.unitId) {
                            accumulatedComplianceForModal += a.percentCumplimiento ?? (a.executed ? 100 : 0);
                        }
                    });
                accumulatedComplianceForModal = Math.min(100, accumulatedComplianceForModal);

                if (appliesProtocolForModal && isProtocolProgressedLocal(targetTaskIdForModal, editingAllocationData.unitId)) {
                    accumulatedComplianceForModal = 100;
                }

                const balanceForModal = Math.max(0, 100 - accumulatedComplianceForModal);

                return (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-purple-600 dark:text-purple-400">edit</span>
                                    <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">
                                        Editar Programación
                                    </h3>
                                </div>
                                <button onClick={() => { setIsEditingAllocation(false); setEditingAllocationData(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            
                            <div className="p-5 space-y-4">
                                <div>
                                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Partida / Tarea</h4>
                                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-850 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                                        {findTaskName(editingAllocationData.taskId, tasks)}
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Unidad Física</h4>
                                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-850 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                                        {units.find(u => u.id === editingAllocationData.unitId)?.name || 'N/A'}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Responsable</label>
                                    <select
                                        value={editingAllocationData.responsibleId}
                                        onChange={(e) => setEditingAllocationData(prev => prev ? { ...prev, responsibleId: e.target.value } : null)}
                                        className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                                    >
                                        <option value="">-- Sin asignar --</option>
                                        {responsibles.map(r => (
                                            <option key={r.id} value={r.id}>
                                                {r.name} ({r.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">% Comprometido</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max={balanceForModal}
                                        value={editingAllocationData.percentComprometido ?? 100}
                                        disabled={activePlan?.status === 'Cerrada'}
                                        onChange={(e) => {
                                            const val = Math.min(balanceForModal, Math.max(0, parseFloat(e.target.value) || 0));
                                            setEditingAllocationData(prev => prev ? { ...prev, percentComprometido: val } : null);
                                        }}
                                        className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1 ml-1">
                                        Saldo restante: {balanceForModal}% (Acumulado anterior: {accumulatedComplianceForModal}%)
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Días de Ejecución</label>
                                    <div className="flex justify-between bg-slate-50 dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
                                        {['L', 'M', 'X', 'J', 'V', 'S'].map(day => (
                                            <button
                                                key={day}
                                                type="button"
                                                onClick={() => {
                                                    setEditingAllocationData(prev => {
                                                        if (!prev) return null;
                                                        return {
                                                            ...prev,
                                                            days: { ...prev.days, [day]: !prev.days[day as keyof typeof prev.days] }
                                                        };
                                                    });
                                                }}
                                                className={`w-8 h-8 rounded-xl text-xs font-black transition-all flex items-center justify-center ${
                                                    editingAllocationData.days[day as keyof typeof editingAllocationData.days]
                                                        ? 'bg-purple-600 text-white shadow-md'
                                                        : 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600 hover:border-purple-500'
                                                }`}
                                            >
                                                {day}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="p-4 bg-slate-50 dark:bg-slate-850/50 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
                                <button
                                    onClick={() => { setIsEditingAllocation(false); setEditingAllocationData(null); }}
                                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => {
                                        if (!editingAllocationData.responsibleId) {
                                            alert("Debe seleccionar un responsable.");
                                            return;
                                        }
                                        if (!Object.values(editingAllocationData.days).some(d => d)) {
                                            alert("Debe seleccionar al menos un día de ejecución.");
                                            return;
                                        }
                                        
                                        if (draftWeeklyPlan && draftWeeklyPlan.id === activePlanId) {
                                            setDraftWeeklyPlan(prev => {
                                                if (!prev) return prev;
                                                const newAllocations = [...prev.allocations];
                                                newAllocations[editingAllocationIndex] = {
                                                    ...newAllocations[editingAllocationIndex],
                                                    responsibleId: editingAllocationData.responsibleId,
                                                    days: editingAllocationData.days,
                                                    percentComprometido: editingAllocationData.percentComprometido
                                                };
                                                return { ...prev, allocations: newAllocations };
                                            });
                                        } else {
                                            setWeeklyPlans(prevPlans => prevPlans.map(plan => {
                                                if (plan.id === activePlanId) {
                                                    const newAllocations = [...plan.allocations];
                                                    newAllocations[editingAllocationIndex] = {
                                                        ...newAllocations[editingAllocationIndex],
                                                        responsibleId: editingAllocationData.responsibleId,
                                                        days: editingAllocationData.days,
                                                        percentComprometido: editingAllocationData.percentComprometido
                                                    };
                                                    return { ...plan, allocations: newAllocations };
                                                }
                                                return plan;
                                            }));
                                        }
                                        
                                        setIsEditingAllocation(false);
                                        setEditingAllocationData(null);
                                    }}
                                    className="px-5 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded-xl text-xs font-bold shadow-md shadow-purple-500/20 active:scale-95 transition-all"
                                >
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
            {/* Modal: Tareas adicionales */}
            {additionalModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-purple-600 dark:text-purple-400">playlist_add</span>
                                <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider font-extrabold">
                                    {additionalModalData.index !== null ? 'Editar Tarea Adicional' : 'Nueva Tarea Adicional'}
                                </h3>
                            </div>
                            <button onClick={() => setAdditionalModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        
                        <div className="p-5 space-y-4">
                            {/* LEVEL SELECT */}
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Nivel</label>
                                <select
                                    value={additionalModalData.levelId}
                                    onChange={(e) => setAdditionalModalData(prev => ({
                                        ...prev,
                                        levelId: e.target.value,
                                        unitId: '' // Reset unit on level change
                                    }))}
                                    className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                                >
                                    <option value="">Seleccionar Nivel...</option>
                                    {levels.map(l => (
                                        <option key={l.id} value={l.id}>{l.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* UNIT SELECT (SITIO) */}
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Sitio (Unidad Física)</label>
                                <select
                                    value={additionalModalData.unitId}
                                    onChange={(e) => setAdditionalModalData(prev => ({
                                        ...prev,
                                        unitId: e.target.value
                                    }))}
                                    disabled={!additionalModalData.levelId}
                                    className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <option value="">Seleccionar Sitio...</option>
                                    {units
                                        .filter(u => u.levelId === additionalModalData.levelId)
                                        .map(u => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))
                                    }
                                </select>
                            </div>

                            {/* TASK NAME */}
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Nombre de la Tarea</label>
                                <input
                                    type="text"
                                    value={additionalModalData.additionalTaskName}
                                    onChange={(e) => setAdditionalModalData(prev => ({
                                        ...prev,
                                        additionalTaskName: e.target.value
                                    }))}
                                    placeholder="Escriba el nombre de la tarea..."
                                    className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                                />
                            </div>

                            {/* RESPONSIBLE */}
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Responsable</label>
                                <select
                                    value={additionalModalData.responsibleId}
                                    onChange={(e) => setAdditionalModalData(prev => ({
                                        ...prev,
                                        responsibleId: e.target.value
                                    }))}
                                    className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                                >
                                    <option value="">Seleccionar Responsable...</option>
                                    {responsibles.map(r => (
                                        <option key={r.id} value={r.id}>
                                            {r.name} ({r.role})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* DAYS OF EXECUTION */}
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Días de Ejecución</label>
                                <div className="flex justify-between bg-slate-50 dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
                                    {['L', 'M', 'X', 'J', 'V', 'S'].map(day => (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => {
                                                setAdditionalModalData(prev => ({
                                                    ...prev,
                                                    days: { ...prev.days, [day]: !prev.days[day] }
                                                }));
                                            }}
                                            className={`w-8 h-8 rounded-xl text-xs font-black transition-all flex items-center justify-center ${
                                                additionalModalData.days[day]
                                                    ? 'bg-purple-600 text-white shadow-md'
                                                    : 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600 hover:border-purple-500'
                                            }`}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-4 bg-slate-50 dark:bg-slate-850/50 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
                            <button
                                onClick={() => setAdditionalModalOpen(false)}
                                className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveAdditionalTask}
                                className="px-5 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded-xl text-xs font-bold shadow-md shadow-purple-500/20 active:scale-95 transition-all"
                            >
                                {additionalModalData.index !== null ? 'Guardar Cambios' : 'Crear Tarea'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Exit Warning Modal */}
            {showExitWarningModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-all duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden transform scale-100 animate-in fade-in zoom-in duration-200">
                        <div className="p-6 text-center space-y-4">
                            <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-amber-50 dark:bg-amber-950/30 text-amber-500 dark:text-amber-400">
                                <span className="material-symbols-outlined text-[32px]">warning</span>
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">¿Desea salir de la Planificación sin guardar?</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                    Tiene cambios sin guardar en esta planificación.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 pt-2">
                                <button
                                    onClick={handleExitWithoutSaving}
                                    className="w-full bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400 font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 transition-all"
                                >
                                    <span className="material-symbols-outlined text-[16px]">delete_forever</span>
                                    Salir sin Guardar
                                </button>
                                <button
                                    onClick={handleExitWithSaving}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all"
                                >
                                    <span className="material-symbols-outlined text-[16px]">save</span>
                                    Guardar Planificación
                                </button>
                                <button
                                    onClick={() => {
                                        setShowExitWarningModal(false);
                                        setExitWarningTarget(null);
                                    }}
                                    className="w-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all"
                                >
                                    <span className="material-symbols-outlined text-[16px]">edit</span>
                                    Seguir Editando
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default PlanningScreen;